import { useCoreT } from '../context/CoreLangContext';
import { LANGUAGES, ALL_LANGUAGE_CODES, UI_SECTIONS, UI_DEFAULTS, ADMIN_UI_SECTIONS, ADMIN_UI_DEFAULTS } from '../context/LanguageContext';
import { useLanguage } from '../context/LanguageContext';
import { UI_FA } from '../lang/fa';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ImageUploader from '../components/ImageUploader';
import QrCodeCard from '../components/QrCodeCard';
import { BASE_URL } from '../api/baseURL';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi, translateApi } from '../api/apiClient';
import BlogManager from '../components/BlogManager';
import VisitorsPanel from '../components/VisitorsPanel';
import { MENU_THEMES, MENU_LAYOUTS, resolveMenuTheme, menuCssVars, buildMenuCss } from '../theme/menuTheme';
import AnalyticsPanel from '../components/AnalyticsPanel';
import { API as API_BASE } from '../api/baseURL';

// ── tiny helpers ─────────────────────────────────────────────────────────────
const slugify = s => s.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
const STATUSES = ['Pending','Confirmed','Preparing','Ready','Delivered','Cancelled'];
const TAG_OPTIONS = ['Bestseller','Popular','Vegetarian','Vegan','Spicy','Gluten-Free',"Chef's Choice",'Premium','Fresh','Authentic'];

// ─────────────────────────────────────────────────────────────────────────────
// CURRENCIES — comprehensive world currency list
// code: ISO 4217, symbol: display symbol, decimals: decimal places (0 for JPY etc)
// ─────────────────────────────────────────────────────────────────────────────
const CURRENCIES = {
  USD: { code:'USD', symbol:'$',       name:'US Dollar',            flag:'🇺🇸', decimals:2, region:'Americas' },
  EUR: { code:'EUR', symbol:'€',       name:'Euro',                 flag:'🇪🇺', decimals:2, region:'Europe' },
  GBP: { code:'GBP', symbol:'£',       name:'British Pound',        flag:'🇬🇧', decimals:2, region:'Europe' },
  JPY: { code:'JPY', symbol:'¥',       name:'Japanese Yen',         flag:'🇯🇵', decimals:0, region:'Asia' },
  CNY: { code:'CNY', symbol:'¥',       name:'Chinese Yuan',         flag:'🇨🇳', decimals:2, region:'Asia' },
  AUD: { code:'AUD', symbol:'A$',      name:'Australian Dollar',    flag:'🇦🇺', decimals:2, region:'Oceania' },
  CAD: { code:'CAD', symbol:'C$',      name:'Canadian Dollar',      flag:'🇨🇦', decimals:2, region:'Americas' },
  CHF: { code:'CHF', symbol:'CHF',     name:'Swiss Franc',          flag:'🇨🇭', decimals:2, region:'Europe' },
  HKD: { code:'HKD', symbol:'HK$',    name:'Hong Kong Dollar',     flag:'🇭🇰', decimals:2, region:'Asia' },
  SGD: { code:'SGD', symbol:'S$',      name:'Singapore Dollar',     flag:'🇸🇬', decimals:2, region:'Asia' },
  INR: { code:'INR', symbol:'₹',       name:'Indian Rupee',         flag:'🇮🇳', decimals:2, region:'Asia' },
  KRW: { code:'KRW', symbol:'₩',       name:'South Korean Won',     flag:'🇰🇷', decimals:0, region:'Asia' },
  BRL: { code:'BRL', symbol:'R$',      name:'Brazilian Real',       flag:'🇧🇷', decimals:2, region:'Americas' },
  MXN: { code:'MXN', symbol:'MX$',    name:'Mexican Peso',         flag:'🇲🇽', decimals:2, region:'Americas' },
  RUB: { code:'RUB', symbol:'₽',       name:'Russian Ruble',        flag:'🇷🇺', decimals:2, region:'Europe' },
  ZAR: { code:'ZAR', symbol:'R',       name:'South African Rand',   flag:'🇿🇦', decimals:2, region:'Africa' },
  TRY: { code:'TRY', symbol:'₺',       name:'Turkish Lira',         flag:'🇹🇷', decimals:2, region:'Europe' },
  SAR: { code:'SAR', symbol:'﷼',       name:'Saudi Riyal',          flag:'🇸🇦', decimals:2, region:'Middle East' },
  AED: { code:'AED', symbol:'د.إ',     name:'UAE Dirham',           flag:'🇦🇪', decimals:2, region:'Middle East' },
  IRR: { code:'IRR', symbol:'﷼',       name:'Iranian Rial',         flag:'🇮🇷', decimals:0, region:'Middle East' },
  ILS: { code:'ILS', symbol:'₪',       name:'Israeli Shekel',       flag:'🇮🇱', decimals:2, region:'Middle East' },
  EGP: { code:'EGP', symbol:'£',       name:'Egyptian Pound',       flag:'🇪🇬', decimals:2, region:'Africa' },
  MAD: { code:'MAD', symbol:'MAD',     name:'Moroccan Dirham',      flag:'🇲🇦', decimals:2, region:'Africa' },
  NGN: { code:'NGN', symbol:'₦',       name:'Nigerian Naira',       flag:'🇳🇬', decimals:2, region:'Africa' },
  KES: { code:'KES', symbol:'KSh',     name:'Kenyan Shilling',      flag:'🇰🇪', decimals:2, region:'Africa' },
  GHS: { code:'GHS', symbol:'GH₵',    name:'Ghanaian Cedi',        flag:'🇬🇭', decimals:2, region:'Africa' },
  ETB: { code:'ETB', symbol:'Br',      name:'Ethiopian Birr',       flag:'🇪🇹', decimals:2, region:'Africa' },
  TZS: { code:'TZS', symbol:'TSh',     name:'Tanzanian Shilling',   flag:'🇹🇿', decimals:2, region:'Africa' },
  SEK: { code:'SEK', symbol:'kr',      name:'Swedish Krona',        flag:'🇸🇪', decimals:2, region:'Europe' },
  NOK: { code:'NOK', symbol:'kr',      name:'Norwegian Krone',      flag:'🇳🇴', decimals:2, region:'Europe' },
  DKK: { code:'DKK', symbol:'kr',      name:'Danish Krone',         flag:'🇩🇰', decimals:2, region:'Europe' },
  PLN: { code:'PLN', symbol:'zł',      name:'Polish Zloty',         flag:'🇵🇱', decimals:2, region:'Europe' },
  CZK: { code:'CZK', symbol:'Kč',     name:'Czech Koruna',         flag:'🇨🇿', decimals:2, region:'Europe' },
  HUF: { code:'HUF', symbol:'Ft',      name:'Hungarian Forint',     flag:'🇭🇺', decimals:0, region:'Europe' },
  RON: { code:'RON', symbol:'lei',     name:'Romanian Leu',         flag:'🇷🇴', decimals:2, region:'Europe' },
  BGN: { code:'BGN', symbol:'лв',      name:'Bulgarian Lev',        flag:'🇧🇬', decimals:2, region:'Europe' },
  HRK: { code:'HRK', symbol:'kn',      name:'Croatian Kuna',        flag:'🇭🇷', decimals:2, region:'Europe' },
  UAH: { code:'UAH', symbol:'₴',       name:'Ukrainian Hryvnia',    flag:'🇺🇦', decimals:2, region:'Europe' },
  GEL: { code:'GEL', symbol:'₾',       name:'Georgian Lari',        flag:'🇬🇪', decimals:2, region:'Europe' },
  IDR: { code:'IDR', symbol:'Rp',      name:'Indonesian Rupiah',    flag:'🇮🇩', decimals:0, region:'Asia' },
  MYR: { code:'MYR', symbol:'RM',      name:'Malaysian Ringgit',    flag:'🇲🇾', decimals:2, region:'Asia' },
  THB: { code:'THB', symbol:'฿',       name:'Thai Baht',            flag:'🇹🇭', decimals:2, region:'Asia' },
  VND: { code:'VND', symbol:'₫',       name:'Vietnamese Dong',      flag:'🇻🇳', decimals:0, region:'Asia' },
  PHP: { code:'PHP', symbol:'₱',       name:'Philippine Peso',      flag:'🇵🇭', decimals:2, region:'Asia' },
  PKR: { code:'PKR', symbol:'₨',       name:'Pakistani Rupee',      flag:'🇵🇰', decimals:2, region:'Asia' },
  BDT: { code:'BDT', symbol:'৳',       name:'Bangladeshi Taka',     flag:'🇧🇩', decimals:2, region:'Asia' },
  NPR: { code:'NPR', symbol:'₨',       name:'Nepalese Rupee',       flag:'🇳🇵', decimals:2, region:'Asia' },
  LKR: { code:'LKR', symbol:'₨',       name:'Sri Lankan Rupee',     flag:'🇱🇰', decimals:2, region:'Asia' },
  MMK: { code:'MMK', symbol:'K',       name:'Myanmar Kyat',         flag:'🇲🇲', decimals:0, region:'Asia' },
  KHR: { code:'KHR', symbol:'₭',       name:'Cambodian Riel',       flag:'🇰🇭', decimals:0, region:'Asia' },
  TWD: { code:'TWD', symbol:'NT$',     name:'Taiwan Dollar',        flag:'🇹🇼', decimals:0, region:'Asia' },
  NZD: { code:'NZD', symbol:'NZ$',    name:'New Zealand Dollar',   flag:'🇳🇿', decimals:2, region:'Oceania' },
  FJD: { code:'FJD', symbol:'FJ$',    name:'Fijian Dollar',        flag:'🇫🇯', decimals:2, region:'Oceania' },
  PGK: { code:'PGK', symbol:'K',       name:'Papua New Guinea Kina',flag:'🇵🇬', decimals:2, region:'Oceania' },
  ARS: { code:'ARS', symbol:'$',       name:'Argentine Peso',       flag:'🇦🇷', decimals:2, region:'Americas' },
  CLP: { code:'CLP', symbol:'$',       name:'Chilean Peso',         flag:'🇨🇱', decimals:0, region:'Americas' },
  COP: { code:'COP', symbol:'$',       name:'Colombian Peso',       flag:'🇨🇴', decimals:0, region:'Americas' },
  PEN: { code:'PEN', symbol:'S/',      name:'Peruvian Sol',         flag:'🇵🇪', decimals:2, region:'Americas' },
  UYU: { code:'UYU', symbol:'$U',      name:'Uruguayan Peso',       flag:'🇺🇾', decimals:2, region:'Americas' },
  BOB: { code:'BOB', symbol:'Bs.',     name:'Bolivian Boliviano',   flag:'🇧🇴', decimals:2, region:'Americas' },
  VES: { code:'VES', symbol:'Bs.S',    name:'Venezuelan Bolívar',   flag:'🇻🇪', decimals:2, region:'Americas' },
  GTQ: { code:'GTQ', symbol:'Q',       name:'Guatemalan Quetzal',   flag:'🇬🇹', decimals:2, region:'Americas' },
  CRC: { code:'CRC', symbol:'₡',       name:'Costa Rican Colón',    flag:'🇨🇷', decimals:0, region:'Americas' },
  DOP: { code:'DOP', symbol:'RD$',    name:'Dominican Peso',       flag:'🇩🇴', decimals:2, region:'Americas' },
  JMD: { code:'JMD', symbol:'J$',      name:'Jamaican Dollar',      flag:'🇯🇲', decimals:2, region:'Americas' },
  TTD: { code:'TTD', symbol:'TT$',    name:'T&T Dollar',           flag:'🇹🇹', decimals:2, region:'Americas' },
  QAR: { code:'QAR', symbol:'﷼',       name:'Qatari Riyal',         flag:'🇶🇦', decimals:2, region:'Middle East' },
  KWD: { code:'KWD', symbol:'KD',      name:'Kuwaiti Dinar',        flag:'🇰🇼', decimals:3, region:'Middle East' },
  BHD: { code:'BHD', symbol:'BD',      name:'Bahraini Dinar',       flag:'🇧🇭', decimals:3, region:'Middle East' },
  OMR: { code:'OMR', symbol:'﷼',       name:'Omani Rial',           flag:'🇴🇲', decimals:3, region:'Middle East' },
  JOD: { code:'JOD', symbol:'JD',      name:'Jordanian Dinar',      flag:'🇯🇴', decimals:3, region:'Middle East' },
  LBP: { code:'LBP', symbol:'£',       name:'Lebanese Pound',       flag:'🇱🇧', decimals:0, region:'Middle East' },
  IQD: { code:'IQD', symbol:'ع.د',    name:'Iraqi Dinar',          flag:'🇮🇶', decimals:0, region:'Middle East' },
  YER: { code:'YER', symbol:'﷼',       name:'Yemeni Rial',          flag:'🇾🇪', decimals:0, region:'Middle East' },
  DZD: { code:'DZD', symbol:'دج',      name:'Algerian Dinar',       flag:'🇩🇿', decimals:2, region:'Africa' },
  TND: { code:'TND', symbol:'DT',      name:'Tunisian Dinar',       flag:'🇹🇳', decimals:3, region:'Africa' },
  LYD: { code:'LYD', symbol:'LD',      name:'Libyan Dinar',         flag:'🇱🇾', decimals:3, region:'Africa' },
  UGX: { code:'UGX', symbol:'USh',    name:'Ugandan Shilling',     flag:'🇺🇬', decimals:0, region:'Africa' },
  RWF: { code:'RWF', symbol:'Fr',      name:'Rwandan Franc',        flag:'🇷🇼', decimals:0, region:'Africa' },
  MUR: { code:'MUR', symbol:'₨',       name:'Mauritian Rupee',      flag:'🇲🇺', decimals:2, region:'Africa' },
  XOF: { code:'XOF', symbol:'CFA',    name:'West African CFA',     flag:'🌍', decimals:0, region:'Africa' },
  XAF: { code:'XAF', symbol:'FCFA',   name:'Central African CFA',  flag:'🌍', decimals:0, region:'Africa' },
  CZK: { code:'CZK', symbol:'Kč',     name:'Czech Koruna',         flag:'🇨🇿', decimals:2, region:'Europe' },
  ISK: { code:'ISK', symbol:'kr',      name:'Icelandic Króna',      flag:'🇮🇸', decimals:0, region:'Europe' },
  MKD: { code:'MKD', symbol:'ден',    name:'Macedonian Denar',     flag:'🇲🇰', decimals:2, region:'Europe' },
  ALL: { code:'ALL', symbol:'L',       name:'Albanian Lek',         flag:'🇦🇱', decimals:2, region:'Europe' },
  RSD: { code:'RSD', symbol:'din',    name:'Serbian Dinar',        flag:'🇷🇸', decimals:2, region:'Europe' },
  BAM: { code:'BAM', symbol:'KM',     name:'Bosnian Mark',         flag:'🇧🇦', decimals:2, region:'Europe' },
  MDL: { code:'MDL', symbol:'L',       name:'Moldovan Leu',         flag:'🇲🇩', decimals:2, region:'Europe' },
  AMD: { code:'AMD', symbol:'֏',       name:'Armenian Dram',        flag:'🇦🇲', decimals:0, region:'Europe' },
  AZN: { code:'AZN', symbol:'₼',       name:'Azerbaijani Manat',    flag:'🇦🇿', decimals:2, region:'Europe' },
  BYN: { code:'BYN', symbol:'Br',      name:'Belarusian Ruble',     flag:'🇧🇾', decimals:2, region:'Europe' },
  KZT: { code:'KZT', symbol:'₸',       name:'Kazakhstani Tenge',    flag:'🇰🇿', decimals:2, region:'Asia' },
  UZS: { code:'UZS', symbol:'soʼm',  name:'Uzbekistani Som',      flag:'🇺🇿', decimals:0, region:'Asia' },
  TJS: { code:'TJS', symbol:'SM',      name:'Tajikistani Somoni',   flag:'🇹🇯', decimals:2, region:'Asia' },
  TMT: { code:'TMT', symbol:'T',       name:'Turkmenistani Manat',  flag:'🇹🇲', decimals:2, region:'Asia' },
  KGS: { code:'KGS', symbol:'с',       name:'Kyrgyzstani Som',      flag:'🇰🇬', decimals:2, region:'Asia' },
  MNT: { code:'MNT', symbol:'₮',       name:'Mongolian Tögrög',     flag:'🇲🇳', decimals:0, region:'Asia' },
};

/**
 * Format a price number using a currency code.
 * Uses Intl.NumberFormat for locale-aware formatting.
 */
function formatPrice(amount, currencyCode = 'USD') {
  const cur = CURRENCIES[currencyCode] || CURRENCIES.USD;
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: cur.decimals,
      maximumFractionDigits: cur.decimals,
    }).format(amount);
  } catch {
    const sym = cur.symbol || currencyCode;
    return sym + Number(amount).toFixed(cur.decimals);
  }
}

export default function AdminDashboard({ restaurant: initRestaurant, setRestaurant: setParentRestaurant }) {
  const navigate = useNavigate();
  const T = useCoreT();
  const { ta } = useLanguage();
  const { adminUser, logoutAdmin } = useAuth();

  const [restaurant, setRestaurant] = useState(initRestaurant);
  const [activeTab, setActiveTab]   = useState('overview');
  const [navOpen,   setNavOpen]     = useState(false);   // mobile sidebar drawer

  // ── Branch context — string = branch _id, null = base (only when NO branches) ─
  // A restaurant with branches has no separate "main" location the customer can
  // visit — the branches ARE the locations — so we default to the first branch
  // and never show a "Main Restaurant" tab. The base restaurant.products is only
  // edited directly when the venue has no branches at all.
  const _firstBranch = (restaurant.branches || []).find(b => b.isActive) || (restaurant.branches || [])[0];
  const [activeBranchId, setActiveBranchId] = useState(_firstBranch ? String(_firstBranch._id) : null);
  const activeBranch = activeBranchId
    ? (restaurant.branches || []).find(b => String(b._id) === activeBranchId)
    : null;
  // The "menu source" is whichever object has the products/categories we edit
  const menuSource = activeBranch || restaurant;

  // ── "Make all branches identical" sync tool ───────────────────────────────
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  // Do the branches currently differ? Compare a signature of each menu (sorted
  // product slugs/names) against the main restaurant's.
  const menuSig = (src) => (src?.products || [])
    .map(p => String(p.slug || p.name || '').toLowerCase()).sort().join('|');
  const branchesDiffer = (() => {
    const list = (restaurant.branches || []);
    if (list.length < 2) return false;
    const first = menuSig(list[0]);
    return list.some(s => menuSig(s) !== first);
  })();
  const runBranchSync = async (payload) => {
    setSyncBusy(true);
    try {
      const r = await adminApi.syncBranches(slug, payload);
      syncUp(r.data);
      setSyncOpen(false);
      notify(ta('adm_sync_done'));
    } catch (e) { notify(e.message || 'Failed', 'error'); }
    finally { setSyncBusy(false); }
  };

  const [products,  setProducts]    = useState(menuSource.products   || []);
  const [categories,setCategories]  = useState(menuSource.categories || []);
  const [orders,       setOrders]       = useState([]);
  const [orderStats,   setOrderStats]   = useState(null);
  const [orderFilter,  setOrderFilter]  = useState('all');   // all | Pending | Preparing | Ready | Delivered | Cancelled
  const [orderSearch,  setOrderSearch]  = useState('');
  const [orderDetail,  setOrderDetail]  = useState(null);    // order being viewed in modal
  const [orderSort,    setOrderSort]    = useState('newest');
  const [autoRefresh,  setAutoRefresh]  = useState(false);
  const [customers, setCustomers]   = useState([]);
  const [subAdmins, setSubAdmins]   = useState([]);
  const [stats,     setStats]       = useState(null);
  const [notif,     setNotif]       = useState(null);

  // modal states
  const [productModal,  setProductModal]  = useState(null); // null | { mode:'add'|'edit', data }
  const [categoryModal, setCategoryModal] = useState(null); // null | { mode:'add'|'edit', data }
  const [settingsForm,  setSettingsForm]  = useState({
    name: initRestaurant.name, tagline: initRestaurant.tagline,
    description: initRestaurant.description, cuisine: initRestaurant.cuisine,
    address: initRestaurant.address, phone: initRestaurant.phone,
    email: initRestaurant.email, hours: initRestaurant.hours,
    primaryColor: initRestaurant.primaryColor, accentColor: initRestaurant.accentColor,
    coverImage: initRestaurant.coverImage || '', logo: initRestaurant.logo || '', favicon: initRestaurant.favicon || '',
    currencyCode: initRestaurant.currencyCode || 'USD',
    // Menu appearance
    menuTheme:  initRestaurant.menuTheme  || 'classic',
    menuLayout: initRestaurant.menuLayout || 'horizontal',
    menuColors: initRestaurant.menuColors || {},
    aiBlogEnabled: !!initRestaurant.aiBlogEnabled,
  });

  // Helper to update one key of the menu colour palette
  const setMenuColor = (key, val) => setSettingsForm(f => ({
    ...f, menuColors: { ...(f.menuColors || {}), [key]: val },
  }));
  // Resolved palette defaults for the colour pickers (mirrors menuTheme.js)
  const mcMode    = (settingsForm.menuColors?.mode === 'light') ? 'light' : 'dark';
  const mcDefault = (k) => {
    const dark = mcMode === 'dark';
    if (k === 'accent')  return settingsForm.primaryColor || '#c8a96e';
    if (k === 'bg')      return dark ? '#0f0f0f' : '#f7f4ef';
    if (k === 'surface') return dark ? '#191a1d' : '#ffffff';
    if (k === 'text')    return dark ? '#f5f0e8' : '#1f1c19';
    return '#000000';
  };
  const mcVal = (k) => settingsForm.menuColors?.[k] || mcDefault(k);

  // Bulk price adjustment state
  const [bulkPct,     setBulkPct]     = useState('');
  const [bulkDir,     setBulkDir]     = useState('increase'); // 'increase' | 'decrease'
  const [bulkApplied, setBulkApplied] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');

  // Menu import state
  const [importOpen,      setImportOpen]      = useState(false);
  const [importFiles,     setImportFiles]     = useState([]);
  const [importLoading,   setImportLoading]   = useState(false);
  const [importProgress,  setImportProgress]  = useState('');
  const [importedItems,   setImportedItems]   = useState(null);  // extracted items
  const [importSelected,  setImportSelected]  = useState({});    // { idx: true/false }
  const [importSaving,    setImportSaving]    = useState(false);
  const [importSaveMsg,   setImportSaveMsg]   = useState('');
  const [importMode,      setImportMode]      = useState('files'); // 'files' | 'url'
  const [importUrl,       setImportUrl]       = useState('');
  const importFileRef = React.useRef(null);

  // Events & Promotions state
  const [events,      setEvents]      = useState([]);
  const [selEventKey, setSelEventKey] = useState(null);   // currently open event key
  const [eventForm,   setEventForm]   = useState({});     // form state for open event
  const [eventSaving, setEventSaving] = useState(false);
  const [eventBannerLoading, setEventBannerLoading] = useState(false);
  const [evtGenPrompt,setEvtGenPrompt]= useState('');

  const slug = restaurant.slug;

  const notify = (msg, type = 'success') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3500);
  };

  // Sync products/categories when branch changes — fetch from API for branch data
  React.useEffect(() => {
    if (!activeBranchId) {
      setProducts(restaurant.products || []);
      setCategories(restaurant.categories || []);
      return;
    }
    // Fetch fresh branch data from API (ensures we get saved state)
    const loadBranch = async () => {
      try {
        const [cats, prods] = await Promise.all([
          adminApi.getBranchCategories(slug, activeBranchId),
          adminApi.getBranchProducts(slug, activeBranchId),
        ]);
        setCategories(cats.data || []);
        setProducts(prods.data || []);
      } catch {
        // Fallback to in-memory
        const src = (restaurant.branches || []).find(b => String(b._id) === activeBranchId);
        setProducts((src || restaurant).products || []);
        setCategories((src || restaurant).categories || []);
      }
    };
    loadBranch();
  }, [activeBranchId, restaurant, slug]);

  const syncUp = updated => {
    setRestaurant(updated);
    setParentRestaurant?.(updated);
  };

  // ── fetch ────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try { const r = await adminApi.getStats(slug); setStats(r.data); } catch {}
  }, [slug]);

  // Orders can be filtered to a single branch ('all' = every branch the admin
  // is allowed to see; the server also enforces branch-limited admins).
  const [ordersBranch, setOrdersBranch] = useState('all');
  const fetchOrders = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (ordersBranch && ordersBranch !== 'all') params.branch = ordersBranch;
      const [ordersRes, statsRes] = await Promise.all([
        adminApi.getOrders(slug, params),
        adminApi.getOrderStats(slug),
      ]);
      setOrders(ordersRes.data || []);
      setOrderStats(statsRes.data || null);
    } catch {}
  }, [slug, ordersBranch]);

  // Auto-refresh interval
  React.useEffect(() => {
    if (!autoRefresh || activeTab !== 'orders') return;
    const id = setInterval(fetchOrders, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, activeTab, fetchOrders]);

  const fetchCustomers = useCallback(async () => {
    try { const r = await adminApi.getCustomers(slug); setCustomers(r.data); } catch {}
  }, [slug]);

  const fetchSubAdmins = useCallback(async () => {
    try { const r = await adminApi.getSubAdmins(slug); setSubAdmins(r.data); } catch {}
  }, [slug]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === 'orders')    fetchOrders();    }, [activeTab, fetchOrders]);
  useEffect(() => { if (activeTab === 'customers') fetchCustomers(); }, [activeTab, fetchCustomers]);
  useEffect(() => { if (activeTab === 'admins')    fetchSubAdmins(); }, [activeTab, fetchSubAdmins]);

  // ── product CRUD ─────────────────────────────────────────────────────────
  // ── bulk price adjustment ────────────────────────────────────────────────────
  const handleBulkPrice = async () => {
    const pct = parseFloat(bulkPct);
    if (!pct || pct <= 0 || pct > 1000) return notify(ta('adm_t_valid_pct'), 'error');
    const multiplier = bulkDir === 'increase' ? 1 + pct / 100 : 1 - pct / 100;
    if (!window.confirm(ta('adm_c_bulk_price'))) return;

    const sourceProducts = products; // already filtered to active branch or main
    const errors = [];
    for (const p of sourceProducts) {
      const newPrice = Math.round(p.price * multiplier * 100) / 100;
      try {
        bId
          ? await adminApi.updateBranchProduct(slug, bId, p._id, { price: newPrice })
          : await adminApi.updateProduct(slug, p._id, { price: newPrice });
      } catch { errors.push(p.name); }
    }
    if (errors.length) {
      notify(ta('adm_t_done_errors')+' '+errors.slice(0,3).join(', '), 'error');
    } else {
      notify(ta('adm_t_prices_adjusted')+` (${pct}%)`);
      setBulkApplied(true);
    }
    // Refresh products list
    if (bId) {
      const r = await adminApi.getBranchProducts(slug, bId);
      setProducts(r.data || []);
    } else {
      const r = await adminApi.getProducts(slug);
      setProducts(r.data || []);
    }
    setBulkPct('');
    setTimeout(() => setBulkApplied(false), 3000);
  };

  // ── Menu Import ──────────────────────────────────────────────────────────────
  const handleImportFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImportFiles(files);
    setImportedItems(null);
    setImportSaveMsg('');
    e.target.value = '';
  };

  const runExtraction = async () => {
    if (!importFiles.length) return;
    setImportLoading(true);
    setImportProgress('Sending to AI for analysis…');
    setImportedItems(null);
    setImportSaveMsg('');
    try {
      const form = new FormData();
      importFiles.forEach(f => form.append('files', f));
      setImportProgress(`Analysing ${importFiles.length > 1 ? importFiles.length + ' pages' : '1 page'} — this may take 20–40 seconds…`);
      const resp = await fetch(`${BASE_URL}/api/menu-extract`, { method: 'POST', body: form });
      const data = await resp.json();
      if (!data.success) throw new Error(data.message || 'Extraction failed');
      // Pre-select all items
      const sel = {};
      data.items.forEach((_, i) => { sel[i] = true; });
      setImportedItems(data.items);
      setImportSelected(sel);
      setImportProgress('');
    } catch (err) {
      setImportProgress('');
      notify(err.message || 'Extraction failed', 'error');
    }
    setImportLoading(false);
  };

  const runUrlExtraction = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setImportLoading(true);
    setImportProgress(ta('adm_scanning_url'));
    setImportedItems(null);
    setImportSaveMsg('');
    try {
      const resp = await fetch(`${BASE_URL}/api/menu-extract/url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.message || 'Extraction failed');
      const sel = {};
      data.items.forEach((_, i) => { sel[i] = true; });
      setImportedItems(data.items);
      setImportSelected(sel);
      setImportProgress('');
    } catch (err) {
      setImportProgress('');
      notify(err.message || 'Extraction failed', 'error');
    }
    setImportLoading(false);
  };

  const updateImportItem = (idx, field, value) => {
    setImportedItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const slugifyLocal = (str) =>
    String(str).toLowerCase().trim().replace(/[^\w\s-]/g,'').replace(/[\s_]+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'').slice(0,40) || 'general';

  const saveImportedItems = async () => {
    const toSave = importedItems.filter((_, i) => importSelected[i]);
    if (!toSave.length) { notify(ta('adm_t_select_item'), 'error'); return; }
    setImportSaving(true);

    let created = 0, skipped = 0;
    const catCache = {}; // categoryName → slug

    // Build current category map
    const existingCats = {};
    categories.forEach(c => { existingCats[c.name.toLowerCase()] = c.slug; });

    for (const item of toSave) {
      try {
        const catName = (item.categoryName || 'Main').trim();
        const catKey  = catName.toLowerCase();

        // Ensure category exists
        if (!catCache[catKey]) {
          if (existingCats[catKey]) {
            catCache[catKey] = existingCats[catKey];
          } else {
            // Create category
            const newSlug = slugifyLocal(catName) || 'imported-' + Date.now();
            try {
              const catResp = await adminApi.addCategory(slug, { name: catName, slug: newSlug, icon: '🍽️' });
              catCache[catKey] = newSlug;
              existingCats[catKey] = newSlug;
              // Refresh categories list
              const fresh = await adminApi.getCategories(slug);
              setCategories(fresh.data || fresh);
            } catch {
              catCache[catKey] = 'general';
            }
          }
        }

        const productPayload = {
          name:         item.name,
          description:  item.description || '',
          price:        parseFloat(item.price) || 0,
          categorySlug: catCache[catKey] || 'general',
          tags:         Array.isArray(item.tags) ? item.tags : [],
          options:      Array.isArray(item.options)
            ? item.options.map(o => (o && typeof o === 'object')
                ? { label: String(o.label || '').trim(), icon: String(o.icon || '').trim() }
                : { label: String(o || '').trim(), icon: '' }).filter(o => o.label)
            : [],
          image:        '',   // admin adds images later
          available:    true,
        };

        await adminApi.addProduct(slug, productPayload);
        created++;
      } catch {
        skipped++;
      }
    }

    // Refresh products
    const fresh = await adminApi.getProducts(slug);
    setProducts(fresh.data || fresh);

    setImportSaving(false);
    setImportSaveMsg(`✓ Imported ${created} products${skipped ? ` (${skipped} failed)` : ''}`);
    notify(created+' '+ta('adm_t_imported'));
    if (created > 0 && skipped === 0) {
      setTimeout(() => { setImportOpen(false); setImportedItems(null); setImportFiles([]); }, 1800);
    }
  };

  // ── Events ───────────────────────────────────────────────────────────────────
  const fetchEvents = async () => {
    try { const r = await adminApi.getEvents(slug); setEvents(r.data || []); } catch {}
  };

  const openEvent = (eventKey) => {
    const existing = events.find(e => e.eventKey === eventKey) || {};
    setSelEventKey(eventKey);
    setEvtGenPrompt('');
    setEventForm({
      eventKey,
      title:            existing.title           || '',
      message:          existing.message         || '',
      active:           existing.active          || false,
      startDate:        existing.startDate       ? existing.startDate.slice(0,10) : '',
      endDate:          existing.endDate         ? existing.endDate.slice(0,10)   : '',
      bannerImage:      existing.bannerImage     || '',
      featuredProducts: existing.featuredProducts|| [],
      discountType:     existing.discountType    || 'none',
      discountValue:    existing.discountValue   || 0,
      discountCode:     existing.discountCode    || '',
      popupStyle:       existing.popupStyle      || 'elegant',
      _id:              existing._id,
    });
  };

  const saveEventForm = async () => {
    setEventSaving(true);
    try {
      const r = await adminApi.saveEvent(slug, eventForm);
      setEvents(r.data || []);
      notify(ta('adm_t_event_saved'));
    } catch (e) { notify(e.message, 'error'); }
    setEventSaving(false);
  };

  const deleteCurrentEvent = async () => {
    const ev = events.find(e => e.eventKey === selEventKey);
    if (!ev?._id) { setSelEventKey(null); return; }
    if (!window.confirm(ta('adm_c_remove_event'))) return;
    await adminApi.deleteEvent(slug, ev._id);
    setEvents(events.filter(e => e._id !== ev._id));
    setSelEventKey(null);
    notify(ta('adm_t_event_removed'));
  };

  const generateEventBanner = async () => {
    setEventBannerLoading(true);
    try {
      const resp = await fetch(`${BASE_URL}/api/ai-generate`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          prompt: evtGenPrompt || '',
          imageType: 'event',
          eventKey: selEventKey,
          style: eventForm.popupStyle || 'elegant',
          restaurantName: restaurant.name,
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.message);
      // Compress and upload
      const img = await new Promise((res,rej) => { const i = new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=data.image; });
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 1400/Math.max(img.naturalWidth,img.naturalHeight));
      canvas.width=Math.round(img.naturalWidth*scale); canvas.height=Math.round(img.naturalHeight*scale);
      canvas.getContext('2d').drawImage(img,0,0,canvas.width,canvas.height);
      const blob = await new Promise(res => canvas.toBlob(res,'image/jpeg',0.82));
      const form = new FormData(); form.append('image',blob,'event-banner.jpg');
      const BASE = (window.location.hostname==='localhost') ? 'http://localhost:5000' : '';
      const upResp = await fetch(`${BASE_URL}/api/upload?slug=${slug}&type=cover&name=${selEventKey}-banner`, {method:'POST',body:form});
      const upData = await upResp.json();
      if (!upData.success) throw new Error(upData.message);
      setEventForm(f => ({ ...f, bannerImage: upData.url }));
      notify(ta('adm_t_banner'));
    } catch (e) { notify(e.message || 'Generation failed', 'error'); }
    setEventBannerLoading(false);
  };

  useEffect(() => { if (activeTab === 'events') fetchEvents(); }, [activeTab]);

  // ── branch-aware CRUD helpers ───────────────────────────────────────────────
  // When activeBranchId is set, all product/category operations go to the branch endpoints.
  const bId = activeBranchId; // shorthand

  const saveProduct = async (data) => {
    try {
      if (productModal.mode === 'edit') {
        const r = bId
          ? await adminApi.updateBranchProduct(slug, bId, productModal.data._id, data)
          : await adminApi.updateProduct(slug, productModal.data._id, data);
        setProducts(prev => prev.map(p => p._id === productModal.data._id ? r.data : p));
        notify(ta('adm_t_prod_updated'));
      } else {
        const r = bId
          ? await adminApi.addBranchProduct(slug, bId, data)
          : await adminApi.addProduct(slug, data);
        setProducts(prev => [...prev, r.data]);
        notify(ta('adm_t_prod_added'));
      }
      setProductModal(null);
    } catch (e) { notify(e.message, 'error'); }
  };

  const toggleProduct = async (id) => {
    try {
      bId ? await adminApi.toggleBranchProduct(slug, bId, id)
          : await adminApi.toggleProduct(slug, id);
      setProducts(prev => prev.map(p => p._id === id ? { ...p, available: !p.available } : p));
      notify(ta('adm_t_availability'));
    } catch (e) { notify(e.message, 'error'); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm(ta('adm_c_delete_product'))) return;
    try {
      bId ? await adminApi.deleteBranchProduct(slug, bId, id)
          : await adminApi.deleteProduct(slug, id);
      setProducts(prev => prev.filter(p => p._id !== id));
      notify(ta('adm_t_prod_deleted'));
    } catch (e) { notify(e.message, 'error'); }
  };

  // ── category CRUD ────────────────────────────────────────────────────────
  const saveCategory = async (data) => {
    try {
      if (categoryModal.mode === 'edit') {
        const r = bId
          ? await adminApi.updateBranchCategory(slug, bId, categoryModal.data._id, data)
          : await adminApi.updateCategory(slug, categoryModal.data._id, data);
        setCategories(prev => prev.map(c => c._id === categoryModal.data._id ? r.data : c));
        notify(ta('adm_t_cat_updated'));
      } else {
        const catData = { ...data, slug: data.slug || slugify(data.name) };
        const r = bId
          ? await adminApi.addBranchCategory(slug, bId, catData)
          : await adminApi.addCategory(slug, catData);
        setCategories(prev => [...prev, r.data]);
        notify(ta('adm_t_cat_added'));
      }
      setCategoryModal(null);
    } catch (e) { notify(e.message, 'error'); }
  };

  const deleteCategory = async (catId, catSlug) => {
    const count = products.filter(p => p.categorySlug === catSlug).length;
    if (!window.confirm(ta('adm_c_delete_category'))) return;
    try {
      bId ? await adminApi.deleteBranchCategory(slug, bId, catId)
          : await adminApi.deleteCategory(slug, catId);
      setCategories(prev => prev.filter(c => c._id !== catId));
      setProducts(prev => prev.filter(p => p.categorySlug !== catSlug));
      notify(ta('adm_t_cat_deleted'));
    } catch (e) { notify(e.message, 'error'); }
  };

  // ── language settings ────────────────────────────────────────────────────
  const [langForm, setLangForm] = useState({
    languageMode:     restaurant.languageMode    || 'single',
    defaultLanguage:  restaurant.defaultLanguage || 'en',
    enabledLanguages: restaurant.enabledLanguages || ['en'],
  });
  const [translations,   setTranslations]   = useState(restaurant.translations || {});
  // restaurantInfoTrans[lang] = { name, tagline, description } — translatable landing page content
  const [infoTrans, setInfoTrans] = useState(() => {
    const stored = restaurant.translations || {};
    const out = {};
    Object.entries(stored).forEach(([lang, data]) => {
      if (data?.restaurantInfo) out[lang] = data.restaurantInfo;
    });
    return out;
  });
  // seoTrans[lang] = { title, description, keywords }
  const [seoTrans, setSeoTrans] = useState(() => {
    const out = {};
    for (const [lang, data] of Object.entries(restaurant.translations || {})) {
      if (data.seo) out[lang] = { ...data.seo };
    }
    return out;
  });

  // uiTranslations[lang][key] = string — for all UI strings (buttons, labels etc.)
  // Stored inside restaurant.translations[lang].ui on the server
  const [uiTranslations, setUiTranslations] = useState(() => {
    const stored = restaurant.translations || {};
    const ui = {};
    Object.entries(stored).forEach(([lang, data]) => {
      if (data?.ui) ui[lang] = data.ui;
    });
    return ui;
  });

  const [transLang, setTransLang] = useState(() => {
    // Pick first available language that isn't the restaurant's default
    const def = restaurant.languageMode === 'multi'
      ? (restaurant.enabledLanguages || ['en']).find(c => c !== (restaurant.defaultLanguage || 'en'))
      : undefined;
    return def || 'fa';
  });
  const [autoTranslating, setAutoTranslating] = useState({}); // { [entityId]: true } while translating
  const [savingLang,   setSavingLang]   = useState(false);
  const [serverTransAll, setServerTransAll] = useState(false); // server-side "translate all languages"

  // Pre-fill the customer-menu UI translation fields from what the menu actually
  // shows, so a language that was previously the default (its UI text comes from
  // the built-in Persian catalogue, not stored translations) never renders blank.
  useEffect(() => {
    const lang = transLang;
    const def = langForm.defaultLanguage || restaurant.defaultLanguage || 'en';
    if (!lang || lang === def) return;
    let cancelled = false;
    const faBase = lang === 'fa' ? UI_FA : {};
    fetch('/api/i18n/' + encodeURIComponent(lang))
      .then(r => (r.ok ? r.json() : { strings: {} }))
      .then(d => {
        if (cancelled) return;
        const store = d.strings || {};
        setUiTranslations(prev => {
          const cur = prev[lang] || {};
          const filled = {};
          Object.keys(UI_DEFAULTS).forEach(k => {
            if (!cur[k]) { const v = store[k] || faBase[k]; if (v) filled[k] = v; }
          });
          if (!Object.keys(filled).length) return prev;
          return { ...prev, [lang]: { ...filled, ...cur } };
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [transLang]);

  // ── Admin panel language (separate from customer-facing translations) ─────
  const [adminPanelLang,  setAdminPanelLang]  = useState(restaurant.adminPanelLang  || 'en');
  const [adminPanelTrans, setAdminPanelTrans] = useState(restaurant.adminPanelTranslations || {});
  const [adminLangAuto,   setAdminLangAuto]   = useState({});

  const toggleEnabledLang = (code) => {
    setLangForm(f => {
      const has = f.enabledLanguages.includes(code);
      if (has && f.enabledLanguages.length === 1) return f;
      const next = has ? f.enabledLanguages.filter(c => c !== code) : [...f.enabledLanguages, code];
      return { ...f, enabledLanguages: next };
    });
  };

  const setProductTrans = (pid, field, val) => setTranslations(prev => ({
    ...prev,
    [transLang]: { ...prev[transLang], [pid]: { ...(prev[transLang]?.[pid] || {}), [field]: val } },
  }));

  // ── Auto-translate via our server (multi-provider: MyMemory → Lingva →
  // LibreTranslate, with caching). No API key, robust against single-provider
  // downtime, and a whole menu translates in one batched request. ──
  const translateBatch = async (texts, targetLang, review = true) => {
    // Always auto-detect the source. The stored menu data is frequently NOT in
    // the declared default language (e.g. English content on a Persian-default
    // menu), so assuming "source = default" makes the model translate the wrong
    // way and the text comes back unchanged (looks like "English by default").
    // Auto-detect translates correctly INTO the target no matter the real source.
    const sourceLang = 'auto';
    const clean = texts.map(t => (t == null ? '' : String(t)));
    if (sourceLang === targetLang) return clean;
    // Chunk so a large menu can never exceed the server's per-request cap (which
    // would 413 and silently leave everything untranslated).
    const CHUNK = 120;
    const out = new Array(clean.length);
    for (let i = 0; i < clean.length; i += CHUNK) {
      const slice = clean.slice(i, i + CHUNK);
      try {
        const res = await translateApi.translate(slice, sourceLang, targetLang, review);
        const t = res?.translations;
        for (let j = 0; j < slice.length; j++) {
          out[i + j] = (Array.isArray(t) && t.length === slice.length && t[j]) ? t[j] : slice[j];
        }
      } catch (e) {
        console.warn('Translation request failed:', e?.message);
        for (let j = 0; j < slice.length; j++) out[i + j] = slice[j]; // keep originals
      }
    }
    return out;
  };

  // Single-string helper kept for per-field "translate" buttons.
  const autoTranslate = async (text, targetLang, onResult) => {
    if (!text?.trim()) return;
    const [out] = await translateBatch([text], targetLang);
    if (out && out.trim()) onResult(out);
  };

  // Translate everything for the current transLang in ONE batched request:
  // restaurant info + every category + every product (name + description) + all
  // customer UI strings. Each job records where its translated text belongs.
  const autoTranslateAll = async () => {
    setAutoTranslating({ _all: true });

    const jobs = [];   // { kind:'info'|'entity'|'ui', id?, field?, key? }
    const texts = [];
    const push = (text, job) => { if (text && String(text).trim()) { texts.push(text); jobs.push(job); } };

    // Restaurant info (name, tagline, description for landing page)
    push(restaurant.name,                                   { kind: 'info', field: 'name' });
    push(restaurant.tagline || restaurant.description || '',{ kind: 'info', field: 'tagline' });
    push(restaurant.description || '',                      { kind: 'info', field: 'description' });

    // Categories + products — for the MAIN restaurant AND every branch, so a
    // multi-branch venue translates its whole menu in one pass (entities are
    // keyed by their unique _id, which is how the branch menu reads them back).
    const dedupe = new Set();
    const pushMenu = (cats, prods) => {
      for (const cat of (cats || [])) {
        if (!cat || dedupe.has('c' + cat._id)) continue; dedupe.add('c' + cat._id);
        push(cat.name, { kind: 'entity', id: cat._id, field: 'name' });
      }
      for (const prod of (prods || [])) {
        if (!prod || dedupe.has('p' + prod._id)) continue; dedupe.add('p' + prod._id);
        push(prod.name,        { kind: 'entity', id: prod._id, field: 'name' });
        push(prod.description, { kind: 'entity', id: prod._id, field: 'description' });
        (Array.isArray(prod.tags) ? prod.tags : []).forEach((tg, ti) =>
          push(tg, { kind: 'entity', id: prod._id, field: 'tags', index: ti }));
        (Array.isArray(prod.options) ? prod.options : []).forEach((op, oi) =>
          push(op && op.label, { kind: 'entity', id: prod._id, field: 'options', index: oi }));
      }
    };
    pushMenu(restaurant.categories, restaurant.products);
    (restaurant.branches || []).forEach(b => pushMenu(b.categories, b.products));
    // Also include whatever is loaded in the current editing context (covers a
    // freshly-added branch not yet present on the restaurant prop).
    pushMenu(categories, products);

    // Customer-facing UI strings
    for (const section of UI_SECTIONS) {
      for (const { key, default: defaultText } of section.keys) {
        push(defaultText, { kind: 'ui', key });
      }
    }

    const results = await translateBatch(texts, transLang);

    // Distribute results back into the right buckets
    const entityUpdates = {};   // id → { name, description }
    const uiUpdates = {};
    const infoUpdates = {};
    results.forEach((val, i) => {
      const job = jobs[i];
      if (!val) return;
      if (job.kind === 'info')        infoUpdates[job.field] = val;
      else if (job.kind === 'ui')     uiUpdates[job.key] = val;
      else if (job.index != null) {   // array-valued field (tags / option labels)
        const bucket = entityUpdates[job.id] || (entityUpdates[job.id] = {});
        const arr = Array.isArray(bucket[job.field]) ? bucket[job.field] : (bucket[job.field] = []);
        arr[job.index] = val;
      }
      else                            entityUpdates[job.id] = { ...(entityUpdates[job.id] || {}), [job.field]: val };
    });

    setTranslations(prev => {
      const next = { ...prev, [transLang]: { ...(prev[transLang] || {}) } };
      for (const [id, fields] of Object.entries(entityUpdates)) {
        next[transLang][id] = { ...(next[transLang][id] || {}), ...fields };
      }
      if (Object.keys(uiUpdates).length > 0) {
        next[transLang].ui = { ...(next[transLang].ui || {}), ...uiUpdates };
      }
      return next;
    });

    if (Object.keys(infoUpdates).length > 0) {
      setInfoTrans(prev => ({ ...prev, [transLang]: { ...(prev[transLang] || {}), ...infoUpdates } }));
    }
    // Keep the dedicated UI-overrides state in sync too
    if (Object.keys(uiUpdates).length > 0) {
      setUiTranslations(prev => ({ ...prev, [transLang]: { ...(prev[transLang] || {}), ...uiUpdates } }));
    }

    // Derive localized SEO from the freshly translated restaurant info so the
    // SEO title/description/keywords for this language get filled too (mirrors
    // the server "Translate All" path). Only fills blanks — never clobbers text
    // the admin typed.
    setSeoTrans(prev => {
      const curInfo = { ...(infoTrans[transLang] || {}), ...infoUpdates };
      const sName = curInfo.name || restaurant.name || '';
      const sTag  = curInfo.tagline || '';
      const sDesc = curInfo.description || sTag || '';
      const seo = { ...(prev[transLang] || {}) };
      if (!seo.title && sName)       seo.title = sName + (sTag ? ' — ' + sTag : '');
      if (!seo.description && sDesc) seo.description = sDesc;
      if (!seo.keywords) {
        const kw = [sName, restaurant.cuisine].filter(Boolean).join(', ');
        if (kw) seo.keywords = kw;
      }
      return { ...prev, [transLang]: seo };
    });

    setAutoTranslating({});
    notify(ta('adm_t_translated_into')+' — '+(LANGUAGES[transLang]?.name||''));
  };

  // Translate the ENTIRE restaurant into every enabled language on the SERVER and
  // persist immediately. Unlike "Auto-Translate All" (which runs in this browser
  // for the selected language only), this keeps running even if you close the tab
  // and fills in every language at once — ideal for large menus.
  const serverTranslateAllLanguages = async () => {
    if (langForm.languageMode !== 'multi') {
      notify(ta('adm_t_switch_multi'), 'error');
      return;
    }
    const targets = langForm.enabledLanguages.filter(c => c !== langForm.defaultLanguage);
    if (targets.length === 0) {
      notify(ta('adm_t_enable_one_lang'), 'error');
      return;
    }
    const names = targets.map(c => LANGUAGES[c]?.name || c).join(', ');
    if (!window.confirm(`${ta('adm_c_server_translate')}\n\n${names}`)) return;

    setServerTransAll(true);
    try {
      const r = await adminApi.translateRestaurant(slug, { languages: targets, overwrite: false });
      const t = r.data?.translations || {};
      // Re-hydrate every local translation state from the saved server result
      setTranslations(t);
      const info = {}, seo = {}, ui = {};
      Object.entries(t).forEach(([lang, data]) => {
        if (data?.restaurantInfo) info[lang] = data.restaurantInfo;
        if (data?.seo)            seo[lang]  = { ...data.seo };
        if (data?.ui)             ui[lang]   = data.ui;
      });
      setInfoTrans(info); setSeoTrans(seo); setUiTranslations(ui);
      syncUp(r.data);
      const total = Object.values(r.summary || {}).reduce((a, b) => a + b, 0);
      notify(ta('adm_t_translated_saved')+` (${total})`);
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setServerTransAll(false);
    }
  };

  // Translate a single field
  // Translate all UI strings for current transLang
  const autoTranslateAllUI = async () => {
    setAutoTranslating(p => ({ ...p, _ui: true }));

    const keys = [];
    const texts = [];
    UI_SECTIONS.forEach(section => {
      section.keys.forEach(({ key, default: defaultText }) => {
        keys.push(key); texts.push(defaultText);
      });
    });

    const results = await translateBatch(texts, transLang);
    const updates = {};
    results.forEach((val, i) => { if (val && val.trim()) updates[keys[i]] = val; });

    setUiTranslations(prev => ({
      ...prev,
      [transLang]: { ...(prev[transLang] || {}), ...updates },
    }));
    setAutoTranslating(p => { const n = { ...p }; delete n._ui; return n; });
    notify(ta('adm_t_translated_ui')+' — '+(LANGUAGES[transLang]?.name||''));
  };

  const setUiTrans = (key, val) => setUiTranslations(prev => ({
    ...prev,
    [transLang]: { ...(prev[transLang] || {}), [key]: val },
  }));

  const setInfoField = (field, val) => setInfoTrans(prev => ({
    ...prev,
    [transLang]: { ...(prev[transLang] || {}), [field]: val },
  }));

  const autoTranslateInfoField = async (field, text) => {
    setAutoTranslating(p => ({ ...p, [`info-${field}`]: true }));
    await autoTranslate(text, transLang, (val) => setInfoField(field, val));
    setAutoTranslating(p => { const n = { ...p }; delete n[`info-${field}`]; return n; });
  };

  const autoTranslateSingleUI = async (key, defaultText) => {
    setAutoTranslating(p => ({ ...p, [`ui-${key}`]: true }));
    await autoTranslate(defaultText, transLang, (val) => setUiTrans(key, val));
    setAutoTranslating(p => { const n = { ...p }; delete n[`ui-${key}`]; return n; });
  };

  // ── Admin panel language helpers ──────────────────────────────────────────
  const setAdminKey = (key, val) => setAdminPanelTrans(prev => ({ ...prev, [key]: val }));

  // Admin panel strings are always translated FROM English (their defaults).
  // Chunked so the (large) admin catalogue can't exceed the server cap, and
  // review=true so the AI quality pass + fallback run when the free providers
  // fail (this is what makes the dashboard language actually translate).
  const translateFromEnglish = async (texts, targetLang) => {
    const clean = texts.map(t => (t == null ? '' : String(t)));
    const CHUNK = 120;
    const out = new Array(clean.length);
    for (let i = 0; i < clean.length; i += CHUNK) {
      const slice = clean.slice(i, i + CHUNK);
      try {
        const res = await translateApi.translate(slice, 'en', targetLang, true);
        const t = res?.translations;
        for (let j = 0; j < slice.length; j++) {
          out[i + j] = (Array.isArray(t) && t.length === slice.length && t[j]) ? t[j] : slice[j];
        }
      } catch { for (let j = 0; j < slice.length; j++) out[i + j] = slice[j]; }
    }
    return out;
  };

  const autoTranslateAdminKey = async (key, defaultText) => {
    if (adminPanelLang === 'en') { setAdminKey(key, ''); return; }
    setAdminLangAuto(p => ({ ...p, [key]: true }));
    const [t] = await translateFromEnglish([defaultText], adminPanelLang);
    if (t && t.trim() && t !== defaultText) setAdminKey(key, t);
    setAdminLangAuto(p => { const n = { ...p }; delete n[key]; return n; });
  };

  const autoTranslateAllAdmin = async () => {
    // Selecting English = reset: clear all overrides so built-in English defaults take over
    if (adminPanelLang === 'en') {
      setAdminPanelTrans({});
      notify(ta('adm_t_reset_defaults'));
      return;
    }
    setAdminLangAuto({ _all: true });
    const keys = [];
    const texts = [];
    ADMIN_UI_SECTIONS.forEach(sec => sec.keys.forEach(({ key, default: def }) => {
      keys.push(key); texts.push(def);
    }));
    const results = await translateFromEnglish(texts, adminPanelLang);
    results.forEach((t, i) => { if (t && t.trim() && t !== texts[i]) setAdminKey(keys[i], t); });
    setAdminLangAuto({});
    notify(ta('adm_t_admin_translated')+' — '+(LANGUAGES[adminPanelLang]?.name||''));
  };

  // One-click dashboard-language switch from the top bar: translate the whole
  // admin UI (API → AI fallback) AND persist it on the restaurant in one step.
  const [quickLangBusy, setQuickLangBusy] = useState(false);
  const quickSetAdminLang = async (langCode) => {
    setQuickLangBusy(true);
    try {
      let trans = {};
      // Persian ships with a complete built-in hand-authored catalogue (ta()
      // reads it automatically), so no machine translation is needed for fa.
      if (langCode !== 'en' && langCode !== 'fa') {
        const keys = [], texts = [];
        ADMIN_UI_SECTIONS.forEach(sec => sec.keys.forEach(({ key, default: def }) => { keys.push(key); texts.push(def); }));
        const results = await translateFromEnglish(texts, langCode);
        results.forEach((t, i) => { if (t && t.trim()) trans[keys[i]] = t; });
      }
      const r = await adminApi.updateRestaurant(slug, { adminPanelLang: langCode, adminPanelTranslations: trans });
      setAdminPanelLang(langCode);
      setAdminPanelTrans(trans);
      syncUp(r.data);
      notify(langCode === 'en' ? 'Dashboard set to English ✓' : `Dashboard translated to ${LANGUAGES[langCode]?.name} ✓`);
    } catch (e) { notify(e.message || 'Could not change language', 'error'); }
    finally { setQuickLangBusy(false); }
  };

  const autoTranslateSingle = async (entityId, field, text) => {
    setAutoTranslating(p => ({ ...p, [`${entityId}-${field}`]: true }));
    await autoTranslate(text, transLang, (val) => {
      if (field === 'name' && categories.find(c => c._id === entityId)) {
        setCategoryTrans(entityId, val);
      } else {
        setProductTrans(entityId, field, val);
      }
    });
    setAutoTranslating(p => { const n = { ...p }; delete n[`${entityId}-${field}`]; return n; });
  };

  const setCategoryTrans = (cid, val) => setTranslations(prev => ({
    ...prev,
    [transLang]: { ...prev[transLang], [cid]: { ...(prev[transLang]?.[cid] || {}), name: val } },
  }));

  const setSeoField = (lang, field, val) => setSeoTrans(prev => ({
    ...prev,
    [lang]: { ...(prev[lang] || {}), [field]: val },
  }));

  // Compute translation completeness % for a language
  const getCompleteness = (lang) => {
    const total = categories.length + products.length * 2 + 7 + 6; // cats + prods(name+desc) + info(7) + ui(6 landing)
    let filled = 0;
    const trans = translations[lang] || {};
    const info  = trans.restaurantInfo || {};
    const ui    = uiTranslations[lang] || {};
    ['name','tagline','description','address','phone','hours','email'].forEach(f => { if (info[f]) filled++; });
    ['landing_enter_menu','landing_view_menu','landing_maps','landing_location','landing_phone','landing_hours'].forEach(k => { if (ui[k]) filled++; });
    categories.forEach(c => { if (trans[c._id]?.name) filled++; });
    products.forEach(p => {
      if (trans[p._id]?.name) filled++;
      if (trans[p._id]?.description || !p.description) filled++;
    });
    return total > 0 ? Math.round((filled / total) * 100) : 0;
  };

  const saveLangSettings = async () => {
    setSavingLang(true);
    try {
      // Merge uiTranslations + infoTrans into the translations object
      const mergedTranslations = { ...translations };
      Object.entries(uiTranslations).forEach(([lang, keys]) => {
        mergedTranslations[lang] = { ...(mergedTranslations[lang] || {}), ui: keys };
      });
      Object.entries(infoTrans).forEach(([lang, info]) => {
        mergedTranslations[lang] = { ...(mergedTranslations[lang] || {}), restaurantInfo: info };
      });
      Object.entries(seoTrans).forEach(([lang, seo]) => {
        const cleanSeo = {};
        if (seo.title) cleanSeo.title = seo.title;
        if (seo.description) cleanSeo.description = seo.description;
        if (seo.keywords) cleanSeo.keywords = seo.keywords;
        if (Object.keys(cleanSeo).length > 0) {
          mergedTranslations[lang] = { ...(mergedTranslations[lang] || {}), seo: cleanSeo };
        }
      });
      const r = await adminApi.updateRestaurant(slug, {
        ...langForm,
        translations: mergedTranslations,
        adminPanelLang,
        adminPanelTranslations: adminPanelTrans,
      });
      syncUp(r.data);
      notify(ta('adm_t_lang_saved'));
    } catch (e) { notify(e.message, 'error'); } finally { setSavingLang(false); }
  };

  // ── settings ─────────────────────────────────────────────────────────────
  const saveSettings = async () => {
    try {
      const r = await adminApi.updateRestaurant(slug, settingsForm);
      syncUp(r.data);
      notify(ta('adm_t_settings_saved'));
    } catch (e) { notify(e.message, 'error'); }
  };

  // ── order status ─────────────────────────────────────────────────────────
  const updateOrderPayment = async (orderId, paymentMethod, paymentStatus) => {
    try {
      await adminApi.updateOrderPayment(slug, orderId, { paymentMethod, paymentStatus });
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, paymentMethod, paymentStatus } : o));
      if (orderDetail?._id === orderId) setOrderDetail(d => ({ ...d, paymentMethod, paymentStatus }));
    } catch (e) { notify(e.message, 'error'); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await adminApi.updateOrderStatus(slug, orderId, status);
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status } : o));
      notify(ta('adm_t_order_updated'));
    } catch (e) { notify(e.message, 'error'); }
  };

  const handleLogout = () => { logoutAdmin(); navigate('/admin/login'); };

  const isRoot = adminUser?.role === 'root' || !adminUser?.role;

  const canSeeAnalytics = ['root','full','cashier'].includes(adminUser?.role);
  const tabs = [
    { id: 'overview',   label: '📊', full: ta('adm_overview')   || T.overview       },
    { id: 'products',   label: '🍽️', full: ta('adm_products')   || T.products       },
    { id: 'categories', label: '📂', full: ta('adm_categories') || T.categories     },
    { id: 'orders',     label: '📋', full: ta('adm_orders')     || T.orders         },
    { id: 'customers',  label: '👥', full: ta('adm_customers')  || T.customers      },
    { id: 'blog',       label: '📝', full: ta('adm_blog')       || 'Blog'           },
    { id: 'visitors',   label: '👣', full: ta('adm_visitors')   || 'Visitors'       },
    ...(canSeeAnalytics ? [{ id: 'analytics', label: '📈', full: T.analyticsAI }] : []),
    ...(isRoot ? [
      { id: 'admins',     label: '🔐', full: ta('adm_admins')    || T.subAdmins         },
      { id: 'events',     label: '🎉', full: T.events                         },
      { id: 'settings',   label: '⚙️', full: ta('adm_settings')  || T.settings       },
      { id: 'language',   label: '🌐', full: ta('adm_language')  || T.language       },
      { id: 'admin-lang', label: '🖥️', full: ta('adm_admin_lang')|| T.adminLanguage },
    ] : [
      { id: 'events',     label: '🎉', full: T.events                         },
      { id: 'settings',   label: '⚙️', full: ta('adm_settings')  || T.settings       },
    ]),
  ];

  return (
    <div className="adm-root" dir={['fa','ar','he'].includes(adminPanelLang) ? 'rtl' : 'ltr'}
      style={{ '--pri': restaurant.primaryColor || '#c8a96e' }}>
      {/* ── NOTIFICATION ── */}
      {notif && <div className={`adm-notif ${notif.type}`}>{notif.msg}</div>}

      {/* ── MODALS ── */}
      {productModal  && <ProductModal  modal={productModal}  categories={categories} currencyCode={restaurant.currencyCode} slug={slug} branchId={bId} onSave={saveProduct}  onClose={() => setProductModal(null)} />}
      {categoryModal && <CategoryModal modal={categoryModal} onSave={saveCategory} onClose={() => setCategoryModal(null)} />}

      {/* ── MOBILE HAMBURGER — opens the sidebar drawer on small screens (CSS-gated) ── */}
      <button className="adm-hamburger" onClick={() => setNavOpen(o => !o)} aria-label={ta("adm_admin_panel")}>
        <span>☰</span>
        <span className="adm-hamburger-name">{restaurant.name}</span>
      </button>
      {navOpen && <div className="adm-nav-backdrop" onClick={() => setNavOpen(false)} />}

      {/* ── SIDEBAR ── */}
      <aside className={`adm-sidebar ${navOpen ? 'open' : ''}`}>
        <div className="adm-brand">
          <div className="adm-brand-logo">🍽️</div>
          <div>
            <div className="adm-brand-name">{restaurant.name}</div>
            <div className="adm-brand-role">{ta("adm_admin_panel")}</div>
          </div>
        </div>
        <nav className="adm-nav">
          {tabs.map(t => (
            <button key={t.id} className={`adm-nav-btn ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(t.id); setNavOpen(false); }}>
              <span>{t.label}</span><span>{t.full}</span>
            </button>
          ))}
        </nav>
        <div className="adm-sidebar-footer">
          <button className="adm-sidebar-link" onClick={() => navigate('/menu')}>👁️ {ta('adm_view_menu')}</button>
          <button className="adm-sidebar-link danger" onClick={handleLogout}>🚪 {ta('adm_logout')}</button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <main className="adm-main">

        {/* ── Branch Selector Bar — shown when restaurant has branches ── */}
        {(restaurant.branches || []).length > 0 && (
          <div className="adm-branch-bar">
            <span className="adm-branch-bar-label">📍 {ta('adm_branch_editing')}</span>
            <div className="adm-branch-tabs">
              {(restaurant.branches || []).filter(b => b.isActive).map(b => (
                <button key={String(b._id)}
                  className={`adm-branch-tab ${activeBranchId === String(b._id) ? 'active' : ''}`}
                  onClick={() => setActiveBranchId(String(b._id))}>
                  {b.name}
                </button>
              ))}
            </div>
            <button className="adm-branch-sync-btn" onClick={() => setSyncOpen(true)}>
              🔗 {ta('adm_branch_sync')}{branchesDiffer ? ' •' : ''}
            </button>
            <label className="adm-branch-agg-toggle" title={ta('adm_agg_hint')}>
              <input type="checkbox" checked={restaurant.aggregateMenu !== false}
                onChange={async e => {
                  const v = e.target.checked;
                  try { const r = await adminApi.updateRestaurant(slug, { aggregateMenu: v }); syncUp(r.data); notify(ta('adm_agg_saved')); }
                  catch (err) { notify(err.message || 'Failed', 'error'); }
                }} />
              {ta('adm_agg_toggle')}
            </label>
            {activeBranch && (
              <span className="adm-branch-bar-note">
                {ta('adm_branch_note')} <strong>{activeBranch.name}</strong>
              </span>
            )}
          </div>
        )}
        {syncOpen && (
          <BranchSyncModal
            restaurant={restaurant}
            differ={branchesDiffer}
            busy={syncBusy}
            ta={ta}
            onApply={runBranchSync}
            onClose={() => setSyncOpen(false)}
          />
        )}
        <div className="adm-topbar">
          <h1 className="adm-topbar-title">{tabs.find(t => t.id === activeTab)?.full}</h1>
          <div className="adm-topbar-right">
            {/* Stage 5: preview the customer menu in a chosen language */}
            {restaurant.languageMode === 'multi' && (restaurant.enabledLanguages || []).length > 1 && (
              <div className="adm-tb-group" title="Preview your customer menu in a language">
                <span className="adm-tb-ico">👁️</span>
                <select className="adm-tb-select"
                  defaultValue=""
                  onChange={e => { if (e.target.value) { window.open(`/menu?lang=${e.target.value}`, '_blank'); e.target.value=''; } }}>
                  <option value="" disabled>Preview menu…</option>
                  {(restaurant.enabledLanguages || []).map(code => (
                    <option key={code} value={code}>{LANGUAGES[code]?.flag} {LANGUAGES[code]?.name}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Stage 4: per-account dashboard language switcher (translates + saves) */}
            <div className="adm-tb-group" title="Dashboard language">
              <span className="adm-tb-ico">🌐</span>
              <select className="adm-tb-select" value={adminPanelLang} disabled={quickLangBusy}
                onChange={e => quickSetAdminLang(e.target.value)}>
                {Object.values(LANGUAGES).map(l => (
                  <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
                ))}
              </select>
              {quickLangBusy && <span className="adm-tb-spin">⏳</span>}
            </div>
            <span className="adm-topbar-user">👤 {adminUser?.email}</span>
          </div>
        </div>

        {/* ─────────── OVERVIEW ─────────── */}
        {activeTab === 'overview' && (
          <div className="adm-content">
            <div className="adm-stats-grid">
              {[
                { icon: '🍽️', label: T.products,    val: stats?.totalProducts    ?? products.length },
                { icon: '✅', label: T.available,          val: stats?.availableProducts ?? products.filter(p=>p.available).length },
                { icon: '📂', label: T.categories,         val: stats?.totalCategories  ?? categories.length },
                { icon: '📋', label: T.orders,       val: stats?.totalOrders      ?? '—' },
                { icon: '📅', label: T.todayOrders,     val: stats?.todayOrders      ?? '—' },
                { icon: '💰', label: T.revenue,      val: stats?.totalRevenue != null ? formatPrice(stats.totalRevenue, restaurant.currencyCode) : '—' },
              ].map(s => (
                <div key={s.label} className="adm-stat-card">
                  <div className="adm-stat-icon">{s.icon}</div>
                  <div className="adm-stat-val">{s.val}</div>
                  <div className="adm-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
            {(() => {
              /* Onboarding checklist — activates brand-new restaurants. Hidden once complete. */
              let qrDone = false;
              try { qrDone = localStorage.getItem(`nooshin_qr_done_${restaurant.slug}`) === '1'; } catch {}
              const steps = [
                { key: 'logo',  label: ta('adm_onb_logo'),       done: !!restaurant.logo,                                  go: () => setActiveTab('settings') },
                { key: 'cats',  label: ta('adm_onb_categories'), done: (categories || []).length >= 2,                      go: () => setActiveTab('categories') },
                { key: 'prods', label: ta('adm_onb_products'),   done: (products || []).length >= 5,                        go: () => setActiveTab('products') },
                { key: 'info',  label: ta('adm_onb_info'),       done: !!(restaurant.address && restaurant.hours),          go: () => setActiveTab('settings') },
                { key: 'qr',    label: ta('adm_onb_qr'),         done: qrDone,                                              go: () => window.scrollTo({ top: 600, behavior: 'smooth' }) },
              ];
              const doneCount = steps.filter(st => st.done).length;
              if (doneCount === steps.length) return null;
              return (
                <div className="adm-onb-card">
                  <div className="adm-onb-head">
                    <span className="adm-onb-title">🚀 {ta('adm_onb_title')}</span>
                    <span className="adm-onb-count">{doneCount}/{steps.length}</span>
                  </div>
                  <p className="adm-onb-sub">{ta('adm_onb_sub')}</p>
                  <div className="adm-onb-bar"><div className="adm-onb-fill" style={{ width: `${(doneCount/steps.length)*100}%` }} /></div>
                  <div className="adm-onb-steps">
                    {steps.map(st => (
                      <button key={st.key} className={`adm-onb-step ${st.done ? 'done' : ''}`} onClick={st.go}>
                        <span className="adm-onb-check">{st.done ? '✅' : '⬜'}</span>
                        <span>{st.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            <QrCodeCard restaurant={restaurant} />
            <div className="adm-ov-grid">
              <div className="adm-card">
                <h3>{ta("adm_restaurant_info")}</h3>
                {[['Name', restaurant.name],['Address', restaurant.address],['Phone', restaurant.phone],['Hours', restaurant.hours],['Cuisine', restaurant.cuisine]].map(([k,v]) => (
                  <div key={k} className="adm-info-row"><span>{k}</span><span>{v || '—'}</span></div>
                ))}
              </div>
              <div className="adm-card">
                <h3>{ta("adm_quick_actions")}</h3>
                <button className="adm-quick-btn" onClick={() => { setActiveTab('products'); setProductModal({ mode: 'add', data: null }); }}>+ Add New Product</button>
                <button className="adm-quick-btn" onClick={() => { setActiveTab('categories'); setCategoryModal({ mode: 'add', data: null }); }}>+ Add New Category</button>
                <button className="adm-quick-btn" onClick={() => setActiveTab('orders')}>📋 View Orders</button>
                <button className="adm-quick-btn" onClick={() => setActiveTab('settings')}>⚙️ Edit Settings</button>
              </div>
            </div>
          </div>
        )}

        {/* ─────────── PRODUCTS ─────────── */}
        {activeTab === 'products' && (
          <div className="adm-content">
            <div className="adm-tab-bar">
              <span className="adm-count">{products.length} products</span>
              <button className="adm-import-btn" onClick={() => { setImportOpen(v => !v); setImportedItems(null); setImportFiles([]); setImportSaveMsg(''); }}>
                📄 Import Menu
              </button>
              <button className="adm-add-btn" onClick={() => setProductModal({ mode: 'add', data: null })}>{ta('adm_prod_add') || '+ Add Product'}</button>
            </div>

            {/* ── Menu Import Panel ── */}
            {importOpen && (
              <div className="mi-panel">
                <div className="mi-header">
                  <span className="mi-title">📄 Import from Paper Menu or PDF</span>
                  <p className="mi-subtitle">Upload a photo of your printed menu or a PDF — AI will extract all items automatically.</p>
                </div>

                {!importedItems ? (
                  /* ── Upload stage ── */
                  <div className="mi-upload-area">
                    <div className="mi-mode-tabs">
                      <button className={`mi-mode-tab ${importMode === 'files' ? 'active' : ''}`}
                        onClick={() => setImportMode('files')}>🖼️ {ta('adm_import_from_files')}</button>
                      <button className={`mi-mode-tab ${importMode === 'url' ? 'active' : ''}`}
                        onClick={() => setImportMode('url')}>🔗 {ta('adm_import_from_url')}</button>
                    </div>

                    {importMode === 'files' ? (
                      <>
                        <input ref={importFileRef} type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                          multiple style={{display:'none'}} onChange={handleImportFiles} />

                        <div className="mi-drop-zone" onClick={() => importFileRef.current?.click()}>
                          {importFiles.length > 0 ? (
                            <div className="mi-files-list">
                              {importFiles.map((f,i) => (
                                <div key={i} className="mi-file-chip">
                                  <span>{f.type.includes('pdf') ? '📄' : '🖼️'}</span>
                                  <span>{f.name}</span>
                                  <span className="mi-file-size">({(f.size/1024).toFixed(0)} KB)</span>
                                </div>
                              ))}
                              <div className="mi-drop-hint" style={{fontSize:'.72rem',marginTop:'.4rem'}}>{ta("adm_click_change_files")}</div>
                            </div>
                          ) : (
                            <>
                              <div className="mi-drop-icon">📸</div>
                              <div className="mi-drop-text">{ta("adm_click_upload_menu")}</div>
                              <div className="mi-drop-hint">JPEG, PNG, WebP or PDF · Up to 10 pages/photos</div>
                            </>
                          )}
                        </div>

                        {importFiles.length > 0 && !importLoading && (
                          <button className="mi-extract-btn" onClick={runExtraction}>
                            ✦ Extract Menu with AI
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="mi-url-block">
                          <div className="mi-url-icon">🌐</div>
                          <div className="mi-url-title">{ta('adm_import_url_title')}</div>
                          <div className="mi-url-sub">{ta('adm_import_url_sub')}</div>
                          <input className="mi-url-input" type="url" value={importUrl}
                            onChange={e => setImportUrl(e.target.value)}
                            placeholder={ta('adm_import_url_ph')}
                            onKeyDown={e => { if (e.key === 'Enter' && importUrl.trim() && !importLoading) runUrlExtraction(); }} />
                        </div>

                        {importUrl.trim() && !importLoading && (
                          <button className="mi-extract-btn" onClick={runUrlExtraction}>
                            ✦ {ta('adm_scan_menu_ai')}
                          </button>
                        )}
                      </>
                    )}

                    {importLoading && (
                      <div className="mi-loading">
                        <div className="mi-spinner"/>
                        <span>{importProgress || 'Analysing…'}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ── Review stage ── */
                  <div className="mi-review">
                    <div className="mi-review-header">
                      <div>
                        <span className="mi-review-count">✦ {importedItems.length} items extracted</span>
                        <span className="mi-review-hint"> — review and edit before importing</span>
                      </div>
                      <div className="mi-review-actions">
                        <button className="mi-sel-btn" onClick={() => { const s={}; importedItems.forEach((_,i)=>{s[i]=true;}); setImportSelected(s); }}>{ta("adm_select_all")}</button>
                        <button className="mi-sel-btn" onClick={() => setImportSelected({})}>{ta("adm_none")}</button>
                        <button className="mi-back-btn" onClick={() => { setImportedItems(null); setImportFiles([]); setImportUrl(''); }}>↺ Re-upload</button>
                      </div>
                    </div>

                    <div className="mi-items-grid">
                      {importedItems.map((item, idx) => (
                        <div key={idx} className={`mi-item ${importSelected[idx] ? 'selected' : ''}`}>
                          <div className="mi-item-check-col">
                            <input type="checkbox" checked={!!importSelected[idx]}
                              onChange={e => setImportSelected(s => ({...s,[idx]:e.target.checked}))} />
                          </div>
                          <div className="mi-item-fields">
                            <div className="mi-item-row">
                              <div className="mi-field-wrap mi-field-name">
                                <label className="mi-field-label">{ta("adm_item_name")}</label>
                                <input className="mi-item-input mi-item-name" value={item.name}
                                  onChange={e => updateImportItem(idx,'name',e.target.value)}
                                  placeholder={ta("adm_ph_item_name")} />
                              </div>
                              <div className="mi-field-wrap mi-field-price">
                                <label className="mi-field-label">{ta("adm_price")}</label>
                                <input className="mi-item-input mi-item-price" type="number" min="0" step="0.01"
                                  value={item.price}
                                  onChange={e => updateImportItem(idx,'price',parseFloat(e.target.value)||0)}
                                  placeholder="0" />
                              </div>
                              <div className="mi-field-wrap mi-field-cat">
                                <label className="mi-field-label">{ta("adm_category")}</label>
                                <input className="mi-item-input mi-item-cat" value={item.categoryName}
                                  onChange={e => updateImportItem(idx,'categoryName',e.target.value)}
                                  placeholder={ta("adm_category")} />
                              </div>
                            </div>
                            <div className="mi-field-wrap" style={{width:'100%'}}>
                              <label className="mi-field-label">{ta("adm_description")}</label>
                              <input className="mi-item-input mi-item-desc" value={item.description}
                                onChange={e => updateImportItem(idx,'description',e.target.value)}
                                placeholder={ta("adm_description_opt")} />
                            </div>
                            {((item.tags && item.tags.length > 0) || (item.options && item.options.length > 0)) && (
                              <div className="mi-item-tags">
                                {(item.tags || []).map((t,ti) => <span key={'t'+ti} className="mi-tag">{t}</span>)}
                                {(item.options || []).map((o,oi) => {
                                  const lbl = (o && typeof o === 'object') ? o.label : o;
                                  const ic  = (o && typeof o === 'object') ? o.icon : '';
                                  return <span key={'o'+oi} className="mi-tag mi-tag-opt">{ic ? ic + ' ' : ''}{lbl}</span>;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mi-import-footer">
                      {importSaveMsg && <span className="mi-save-msg">{importSaveMsg}</span>}
                      <span className="mi-sel-count">{Object.values(importSelected).filter(Boolean).length} of {importedItems.length} selected</span>
                      <button className="mi-save-btn" onClick={saveImportedItems} disabled={importSaving || !Object.values(importSelected).some(Boolean)}>
                        {importSaving ? 'Importing…' : `✓ Add ${Object.values(importSelected).filter(Boolean).length} Items to Menu`}
                      </button>
                    </div>
                  </div>
                )}

                <style>{`
                  .mi-panel{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.09);border-radius:16px;padding:1.25rem;margin-bottom:1.25rem}
                  .mi-header{margin-bottom:1rem}
                  .mi-title{font-size:.9rem;font-weight:700;color:#fff}
                  .mi-subtitle{font-size:.75rem;color:rgba(255,255,255,.4);margin:.3rem 0 0;line-height:1.5}
                  .mi-mode-tabs{display:flex;gap:.4rem;margin-bottom:1rem;background:rgba(255,255,255,.04);padding:.3rem;border-radius:10px}
                  .mi-mode-tab{flex:1;padding:.55rem .75rem;background:transparent;border:none;border-radius:8px;color:rgba(255,255,255,.55);font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:600;cursor:pointer;transition:all .18s}
                  .mi-mode-tab:hover{color:rgba(255,255,255,.8)}
                  .mi-mode-tab.active{background:linear-gradient(135deg,#7c3aed,#a78bfa);color:#fff;box-shadow:0 2px 10px rgba(139,92,246,.3)}
                  .mi-url-block{border:2px dashed rgba(255,255,255,.12);border-radius:12px;padding:1.75rem 1.5rem;display:flex;flex-direction:column;align-items:center;gap:.5rem;text-align:center}
                  .mi-url-icon{font-size:2.2rem}
                  .mi-url-title{font-size:.9rem;font-weight:600;color:rgba(255,255,255,.75)}
                  .mi-url-sub{font-size:.74rem;color:rgba(255,255,255,.35);line-height:1.5;max-width:34rem}
                  .mi-url-input{margin-top:.6rem;width:100%;max-width:34rem;padding:.7rem .9rem;background:rgba(0,0,0,.25);border:1px solid rgba(167,139,250,.25);border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.86rem;text-align:center;direction:ltr}
                  .mi-url-input:focus{outline:none;border-color:rgba(167,139,250,.6);background:rgba(0,0,0,.35)}
                  .mi-drop-zone{border:2px dashed rgba(255,255,255,.12);border-radius:12px;padding:2rem 1.5rem;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.5rem;cursor:pointer;transition:all .2s;text-align:center}
                  .mi-drop-zone:hover{border-color:rgba(167,139,250,.4);background:rgba(167,139,250,.05)}
                  .mi-drop-icon{font-size:2.5rem}
                  .mi-drop-text{font-size:.9rem;font-weight:600;color:rgba(255,255,255,.7)}
                  .mi-drop-hint{font-size:.72rem;color:rgba(255,255,255,.3)}
                  .mi-files-list{display:flex;flex-direction:column;gap:.3rem;width:100%;align-items:flex-start}
                  .mi-file-chip{display:flex;align-items:center;gap:.4rem;padding:.3rem .6rem;background:rgba(167,139,250,.1);border:1px solid rgba(167,139,250,.2);border-radius:8px;font-size:.78rem;color:#c4b5fd}
                  .mi-file-size{color:rgba(167,139,250,.5);font-size:.68rem}
                  .mi-extract-btn{margin-top:.75rem;padding:.65rem 1.5rem;background:linear-gradient(135deg,#7c3aed,#a78bfa);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.88rem;font-weight:700;cursor:pointer;box-shadow:0 4px 16px rgba(139,92,246,.3);transition:opacity .2s;width:100%}
                  .mi-extract-btn:hover{opacity:.88}
                  .mi-loading{display:flex;align-items:center;gap:.75rem;padding:.75rem;color:rgba(167,139,250,.8);font-size:.82rem;margin-top:.5rem}
                  .mi-spinner{width:20px;height:20px;border:2px solid rgba(167,139,250,.2);border-top-color:#a78bfa;border-radius:50%;animation:miSpin .8s linear infinite;flex-shrink:0}
                  @keyframes miSpin{to{transform:rotate(360deg)}}
                  .mi-review-header{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.5rem;margin-bottom:.75rem}
                  .mi-review-count{font-size:.88rem;font-weight:700;color:#c4b5fd}
                  .mi-review-hint{font-size:.78rem;color:rgba(255,255,255,.35)}
                  .mi-review-actions{display:flex;gap:.4rem;flex-wrap:wrap}
                  .mi-sel-btn{padding:.3rem .65rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:7px;color:rgba(255,255,255,.55);font-family:'DM Sans',sans-serif;font-size:.72rem;cursor:pointer}
                  .mi-back-btn{padding:.3rem .65rem;background:rgba(167,139,250,.08);border:1px solid rgba(167,139,250,.18);border-radius:7px;color:#c4b5fd;font-family:'DM Sans',sans-serif;font-size:.72rem;cursor:pointer}
                  .mi-items-grid{display:flex;flex-direction:column;gap:.45rem;max-height:480px;overflow-y:auto;padding-right:.25rem}
                  .mi-item{display:flex;gap:.75rem;padding:.65rem .75rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.07);border-radius:10px;transition:all .2s;align-items:flex-start}
                  .mi-item.selected{border-color:rgba(167,139,250,.3);background:rgba(167,139,250,.06)}
                  .mi-item-check-col{padding-top:.35rem;flex-shrink:0}
                  .mi-item-check-col input{width:16px;height:16px;accent-color:#a78bfa;cursor:pointer}
                  .mi-item-fields{flex:1;display:flex;flex-direction:column;gap:.4rem}
                  .mi-item-row{display:flex;gap:.5rem;flex-wrap:wrap;align-items:flex-end}
                  .mi-field-wrap{display:flex;flex-direction:column;gap:.18rem}
                  .mi-field-name{flex:2;min-width:130px}
                  .mi-field-price{width:88px}
                  .mi-field-cat{flex:1;min-width:110px}
                  .mi-field-label{font-size:.62rem;font-weight:700;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.05em;white-space:nowrap}
                  .mi-item-input{padding:.38rem .6rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:7px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.78rem;box-sizing:border-box;width:100%}
                  .mi-item-input:focus{outline:none;border-color:rgba(167,139,250,.4)}
                  .mi-item-name{flex:2;min-width:120px}
                  .mi-item-price{width:80px}
                  .mi-item-cat{flex:1;min-width:100px}
                  .mi-item-desc{width:100%}
                  .mi-item-tags{display:flex;flex-wrap:wrap;gap:.25rem}
                  .mi-tag{padding:.1rem .45rem;background:rgba(200,169,110,.1);border:1px solid rgba(200,169,110,.2);border-radius:6px;font-size:.65rem;color:#c8a96e}
                  .mi-tag-opt{background:rgba(167,139,250,.12);border-color:rgba(167,139,250,.28);color:#c4b5fd}
                  .mi-import-footer{display:flex;align-items:center;justify-content:flex-end;gap:.75rem;margin-top:.75rem;padding-top:.75rem;border-top:1px solid rgba(255,255,255,.07);flex-wrap:wrap}
                  .mi-save-msg{font-size:.8rem;color:#6ee7b7;font-weight:600}
                  .mi-sel-count{font-size:.75rem;color:rgba(255,255,255,.35);margin-right:auto}
                  .mi-save-btn{padding:.65rem 1.4rem;background:#c8a96e;border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.85rem;font-weight:700;cursor:pointer;transition:opacity .2s}
                  .mi-save-btn:disabled{opacity:.4;cursor:not-allowed}
                `}</style>
              </div>
            )}

            {/* ── Bulk price adjustment ── */}
            {products.length > 0 && (
              <div className="bulk-price-panel">
                <div className="bulk-price-label">
                  <span>💰</span>
                  <strong>{ta("adm_bulk_price")}</strong>
                  <span className="bulk-price-sub">Adjust all {products.length} prices at once</span>
                </div>
                <div className="bulk-price-controls">
                  <div className="bulk-dir-toggle">
                    {[{v:'increase',icon:'↑',label:'Increase'},{v:'decrease',icon:'↓',label:'Decrease'}].map(o => (
                      <button key={o.v}
                        className={`bulk-dir-btn ${bulkDir === o.v ? 'active ' + o.v : ''}`}
                        onClick={() => setBulkDir(o.v)}>
                        {o.icon} {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="bulk-pct-wrap">
                    <input className="adm-input bulk-pct-input"
                      type="number" min="0.1" max="1000" step="0.5"
                      placeholder="e.g. 10"
                      value={bulkPct}
                      onChange={e => setBulkPct(e.target.value)} />
                    <span className="bulk-pct-sign">%</span>
                  </div>
                  {Number(bulkPct) > 0 && (
                    <div className="bulk-preview">
                      {products.slice(0, 3).map(p => {
                        const mult = bulkDir === 'increase' ? 1 + parseFloat(bulkPct)/100 : 1 - parseFloat(bulkPct)/100;
                        const newP = Math.round(p.price * mult * 100) / 100;
                        return (
                          <div key={p._id} className="bulk-preview-row">
                            <span className="bulk-preview-name">{p.name}</span>
                            <span className="bulk-preview-old">{formatPrice(p.price, restaurant.currencyCode)}</span>
                            <span className="bulk-preview-arrow">→</span>
                            <span className={`bulk-preview-new ${newP > 0 ? bulkDir : 'invalid'}`}>
                              {newP > 0 ? formatPrice(newP, restaurant.currencyCode) : '⚠'}
                            </span>
                          </div>
                        );
                      })}
                      {products.length > 3 && <div className="bulk-preview-more">+{products.length - 3} more</div>}
                    </div>
                  )}
                  <button className="adm-add-btn bulk-apply-btn"
                    onClick={handleBulkPrice}
                    disabled={!bulkPct || parseFloat(bulkPct) <= 0}
                    style={{background: bulkApplied ? '#10b981' : ''}}>
                    {bulkApplied ? '✓ Applied!' : `Apply ${bulkDir === 'increase' ? '↑' : '↓'} ${bulkPct||'?'}% to All`}
                  </button>
                </div>
              </div>
            )}
            {products.length === 0 ? (
              <div className="adm-empty">No products yet. <button onClick={() => setProductModal({ mode:'add', data:null })}>Add your first product →</button></div>
            ) : (
              <div className="adm-list">
                {products.map(p => (
                  <div key={p._id} className="adm-product-row">
                    <div className="adm-product-img-wrap">
                      {p.image ? <img src={p.image} alt={p.name} className="adm-product-img" onError={e => e.target.style.display='none'} /> : <div className="adm-product-img-fallback">🍽️</div>}
                    </div>
                    <div className="adm-product-info">
                      <div className="adm-product-name">{p.name}</div>
                      <div className="adm-product-meta">
                        {categories.find(c => c.slug === p.categorySlug)?.name || p.categorySlug}
                        {p.subCategory && ` · ${p.subCategory}`}
                      </div>
                      {p.tags?.length > 0 && <div className="adm-product-tags">{p.tags.map(t => <span key={t} className="adm-tag">{t}</span>)}</div>}
                    </div>
                    <div className="adm-product-price">{formatPrice(p.price, restaurant.currencyCode)}</div>
                    <button
                      className={`adm-avail-badge ${p.available ? 'on' : 'off'}`}
                      onClick={() => toggleProduct(p._id)}
                      title="Click to toggle"
                    >
                      {p.available ? '✓ Available' : '✗ Hidden'}
                    </button>
                    <div className="adm-row-actions">
                      <button className="adm-icon-btn" title="Edit" onClick={() => setProductModal({ mode: 'edit', data: p })}>✏️</button>
                      <button className="adm-icon-btn danger" title="Delete" onClick={() => deleteProduct(p._id)}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─────────── CATEGORIES ─────────── */}
        {activeTab === 'categories' && (
          <div className="adm-content">
            <div className="adm-tab-bar">
              <span className="adm-count">{categories.length} categories</span>
              <button className="adm-add-btn" onClick={() => setCategoryModal({ mode: 'add', data: null })}>+ Add Category</button>
            </div>
            {categories.length === 0 ? (
              <div className="adm-empty">No categories yet. <button onClick={() => setCategoryModal({ mode:'add', data:null })}>Add your first →</button></div>
            ) : (
              <div className="adm-cat-grid">
                {categories.map(cat => (
                  <div key={cat._id} className="adm-cat-card">
                    <div className="adm-cat-top">
                      <span className="adm-cat-icon">{cat.icon || '📂'}</span>
                      <div className="adm-cat-row-actions">
                        <button className="adm-icon-btn" title="Edit" onClick={() => setCategoryModal({ mode: 'edit', data: cat })}>✏️</button>
                        <button className="adm-icon-btn danger" title="Delete" onClick={() => deleteCategory(cat._id, cat.slug)}>🗑️</button>
                      </div>
                    </div>
                    <div className="adm-cat-name">{cat.name}</div>
                    <div className="adm-cat-slug">/{cat.slug}</div>
                    {cat.description && <div className="adm-cat-desc">{cat.description}</div>}
                    <div className="adm-cat-count">{products.filter(p => p.categorySlug === cat.slug).length} items</div>
                    {cat.subCategories?.length > 0 && (
                      <div className="adm-cat-subs">{cat.subCategories.map(s => <span key={s} className="adm-sub-badge">{s}</span>)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─────────── ORDERS ─────────── */}
        {activeTab === 'orders' && (() => {
          // Computed filtered/sorted list
          const STATUS_CONFIG = {
            Pending:   { color:'#fbbf24', bg:'rgba(251,191,36,.12)',   icon:'🕐', label:T.pending   },
            Confirmed: { color:'#60a5fa', bg:'rgba(96,165,250,.12)',   icon:'✅', label:T.confirmed },
            Preparing: { color:'#f97316', bg:'rgba(249,115,22,.12)',   icon:'👨‍🍳', label:T.preparing },
            Ready:     { color:'#34d399', bg:'rgba(52,211,153,.12)',   icon:'🔔', label:T.ready     },
            Delivered: { color:'#a3e635', bg:'rgba(163,230,53,.1)',    icon:'✓',  label:T.delivered },
            Cancelled: { color:'#f87171', bg:'rgba(248,113,113,.1)',   icon:'✕',  label:T.cancelled },
          };
          const PAYMENT_COLORS = { unpaid:'#f87171', pending:'#fbbf24', paid:'#34d399', refunded:'#a3e635' };

          const filtered = orders.filter(o => {
            if (orderFilter !== 'all' && o.status !== orderFilter) return false;
            if (orderSearch) {
              const q = orderSearch.toLowerCase();
              return (o.orderNumber||'').toLowerCase().includes(q)
                  || (o.customerName||'').toLowerCase().includes(q)
                  || (o.customerPhone||'').includes(q)
                  || (o.tableNumber||'').includes(q);
            }
            return true;
          }).sort((a,b) => {
            if (orderSort === 'newest')  return new Date(b.createdAt) - new Date(a.createdAt);
            if (orderSort === 'oldest')  return new Date(a.createdAt) - new Date(b.createdAt);
            if (orderSort === 'highest') return b.total - a.total;
            return 0;
          });

          return (
          <div className="adm-content">
            {/* ── Stats bar ── */}
            {orderStats && (
              <div className="ord-stats-bar">
                {[
                  {label:'Today',     val: orderStats.todayOrders,  color:'#a78bfa'},
                  {label:'Pending',   val: orderStats.pending,      color:'#fbbf24'},
                  {label:'Preparing', val: orderStats.preparing,    color:'#f97316'},
                  {label:'Ready',     val: orderStats.ready,        color:'#34d399'},
                  {label:'Revenue',   val: formatPrice(orderStats.totalRevenue||0, restaurant.currencyCode), color:'#c8a96e'},
                ].map(s => (
                  <div key={s.label} className="ord-stat" style={{borderColor:s.color+'33'}}>
                    <span className="ord-stat-val" style={{color:s.color}}>{s.val}</span>
                    <span className="ord-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* ── Per-branch order filter (only when the venue has branches) ── */}
            {(restaurant.branches || []).filter(b => b.isActive).length > 0 && (
              <div className="ord-branch-tabs">
                <span className="ord-branch-label">📍 {ta('adm_branch_orders')}</span>
                <button className={`ord-branch-tab ${ordersBranch==='all'?'active':''}`} onClick={()=>setOrdersBranch('all')}>
                  🏢 {ta('adm_branch_all_orders')}
                </button>
                {(restaurant.branches || []).filter(b => b.isActive).map(b => (
                  <button key={b.slug} className={`ord-branch-tab ${ordersBranch===b.slug?'active':''}`} onClick={()=>setOrdersBranch(b.slug)}>
                    {b.name}
                  </button>
                ))}
              </div>
            )}

            {/* ── Toolbar ── */}
            <div className="ord-toolbar">
              <input className="ord-search" placeholder="🔍 Order#, name, table…"
                value={orderSearch} onChange={e=>setOrderSearch(e.target.value)} />

              <div className="ord-filter-pills">
                {['all','Pending','Confirmed','Preparing','Ready','Delivered','Cancelled'].map(f => (
                  <button key={f} className={`ord-pill ${orderFilter===f?'active':''}`}
                    style={orderFilter===f && STATUS_CONFIG[f] ? {background:STATUS_CONFIG[f].bg, color:STATUS_CONFIG[f].color, borderColor:STATUS_CONFIG[f].color+'55'} : {}}
                    onClick={()=>setOrderFilter(f)}>
                    {f === 'all' ? 'All' : (STATUS_CONFIG[f]?.icon+' '+f)}
                  </button>
                ))}
              </div>

              <div className="ord-toolbar-right">
                <select className="ord-sort-sel" value={orderSort} onChange={e=>setOrderSort(e.target.value)}>
                  <option value="newest">{ta("adm_newest_first")}</option>
                  <option value="oldest">{ta("adm_oldest_first")}</option>
                  <option value="highest">{ta("adm_highest_value")}</option>
                </select>
                <label className="ord-auto-wrap">
                  <input type="checkbox" checked={autoRefresh} onChange={e=>setAutoRefresh(e.target.checked)}/>
                  <span>{ta("adm_auto_refresh")}</span>
                </label>
                <button className="adm-refresh-btn" onClick={fetchOrders}>↻ Refresh</button>
              </div>
            </div>

            <div className="ord-count-row">
              <span>{filtered.length} order{filtered.length!==1?'s':''}{orderFilter!=='all'?` · ${orderFilter}`:''}</span>
            </div>

            {filtered.length === 0 ? (
              <div className="adm-empty">{ta("adm_no_orders")}</div>
            ) : (
              <div className="ord-cards">
                {filtered.map(order => {
                  const sc   = STATUS_CONFIG[order.status] || STATUS_CONFIG.Pending;
                  const mins = Math.floor((Date.now() - new Date(order.createdAt)) / 60000);
                  const age  = mins < 60 ? `${mins}m ago` : `${Math.floor(mins/60)}h ago`;
                  const isOld = order.status === 'Pending' && mins >= 15;
                  return (
                    <div key={order._id} className={`ord-card ${isOld?'ord-card-urgent':''}`}
                      style={{borderLeftColor: sc.color}} onClick={()=>setOrderDetail(order)}>

                      {/* Header row */}
                      <div className="ord-card-head">
                        <div className="ord-card-id-col">
                          <span className="ord-card-num">{order.orderNumber}</span>
                          {order.tableNumber && <span className="ord-card-table">🪑 {order.tableNumber}</span>}
                          {order.orderType && order.orderType !== 'dine-in' && (
                            <span className="ord-card-type">{order.orderType==='takeaway'?'🥡':'🛵'} {order.orderType}</span>
                          )}
                        </div>
                        <div className="ord-card-badges">
                          <span className="ord-status-badge" style={{background:sc.bg,color:sc.color}}>{sc.icon} {order.status}</span>
                          <span className={`ord-payment-badge ${order.paymentStatus}`}
                            style={{color: PAYMENT_COLORS[order.paymentStatus||'unpaid']}}>
                            {order.paymentStatus==='paid'?'💳 Paid': order.paymentStatus==='pending'?'🕐 Unpaid':'💸 '+order.paymentStatus}
                          </span>
                        </div>
                        <span className={`ord-card-age ${isOld?'urgent':''}`}>{age}</span>
                      </div>

                      {/* Items summary */}
                      <div className="ord-card-items">
                        {order.items.slice(0,3).map((it,i)=>(
                          <span key={i} className="ord-item-pill">{it.productName} ×{it.quantity}</span>
                        ))}
                        {order.items.length > 3 && <span className="ord-item-pill more">+{order.items.length-3} more</span>}
                      </div>

                      {/* Footer row */}
                      <div className="ord-card-foot">
                        <span className="ord-card-customer">{order.customerName}</span>
                        {order.customerPhone && <span className="ord-card-phone">📞 {order.customerPhone}</span>}
                        <span className="ord-card-total">{formatPrice(order.total, restaurant.currencyCode)}</span>
                      </div>

                      {/* Quick status buttons */}
                      <div className="ord-card-quick" onClick={e=>e.stopPropagation()}>
                        {Object.entries(STATUS_CONFIG).filter(([s])=>s!==order.status).map(([s,cfg])=>(
                          <button key={s} className="ord-quick-btn" style={{color:cfg.color,borderColor:cfg.color+'44'}}
                            onClick={()=>updateOrderStatus(order._id, s)}>
                            {cfg.icon} {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Order Detail Modal ── */}
            {orderDetail && (
              <OrderDetailModal
                order={orderDetail}
                restaurant={restaurant}
                onClose={()=>setOrderDetail(null)}
                onStatusChange={(id,s)=>{ updateOrderStatus(id,s); setOrderDetail(d=>({...d,status:s})); }}
                onPaymentChange={(id,pm,ps)=>{ updateOrderPayment(id,pm,ps); }}
                formatPrice={formatPrice}
              />
            )}

            <style>{`
              .ord-stats-bar{display:flex;gap:.5rem;flex-wrap:wrap;margin-bottom:1rem}
              .ord-stat{flex:1;min-width:90px;background:rgba(255,255,255,.03);border:1px solid;border-radius:12px;padding:.65rem .75rem;display:flex;flex-direction:column;gap:.15rem}
              .ord-stat-val{font-size:1.1rem;font-weight:800;font-family:'DM Sans',sans-serif}
              .ord-stat-label{font-size:.65rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.06em}
              .ord-branch-tabs{display:flex;align-items:center;gap:.4rem;flex-wrap:wrap;margin-bottom:.75rem;padding-bottom:.75rem;border-bottom:1px solid rgba(255,255,255,.06)}
              .ord-branch-label{font-size:.75rem;color:rgba(255,255,255,.45);margin-inline-end:.25rem}
              .ord-branch-tab{padding:.4rem .85rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:9px;color:rgba(255,255,255,.6);font-family:'DM Sans',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;transition:all .2s;white-space:nowrap}
              .ord-branch-tab:hover{background:rgba(255,255,255,.08);color:#fff}
              .ord-branch-tab.active{background:var(--pri);border-color:var(--pri);color:#fff;font-weight:700}
              .ord-toolbar{display:flex;flex-direction:column;gap:.6rem;margin-bottom:.6rem}
              .ord-search{padding:.55rem .85rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.85rem;width:100%;box-sizing:border-box}
              .ord-search:focus{outline:none;border-color:rgba(200,169,110,.4)}
              .ord-filter-pills{display:flex;gap:.3rem;flex-wrap:wrap}
              .ord-pill{padding:.3rem .7rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:20px;color:rgba(255,255,255,.45);font-family:'DM Sans',sans-serif;font-size:.72rem;font-weight:600;cursor:pointer;transition:all .2s;white-space:nowrap}
              .ord-pill.active{background:rgba(200,169,110,.1);border-color:rgba(200,169,110,.4);color:#c8a96e}
              .ord-toolbar-right{display:flex;align-items:center;gap:.6rem;flex-wrap:wrap}
              .ord-sort-sel{padding:.38rem .6rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:8px;color:rgba(255,255,255,.6);font-family:'DM Sans',sans-serif;font-size:.78rem;cursor:pointer}
              .ord-auto-wrap{display:flex;align-items:center;gap:.3rem;font-size:.72rem;color:rgba(255,255,255,.4);cursor:pointer;user-select:none}
              .ord-auto-wrap input{accent-color:#c8a96e;cursor:pointer}
              .ord-count-row{font-size:.75rem;color:rgba(255,255,255,.3);margin-bottom:.6rem}
              .ord-cards{display:flex;flex-direction:column;gap:.6rem}
              .ord-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-left:3px solid;border-radius:12px;padding:.85rem 1rem;cursor:pointer;transition:all .2s;display:flex;flex-direction:column;gap:.5rem}
              .ord-card:hover{background:rgba(255,255,255,.06);transform:translateX(2px)}
              .ord-card-urgent{animation:ordPulse 2s ease-in-out infinite}
              @keyframes ordPulse{0%,100%{box-shadow:0 0 0 0 rgba(251,191,36,0)}50%{box-shadow:0 0 0 4px rgba(251,191,36,.15)}}
              .ord-card-head{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap}
              .ord-card-id-col{display:flex;align-items:center;gap:.4rem;flex-wrap:wrap}
              .ord-card-num{font-size:.88rem;font-weight:800;color:#fff;font-family:'DM Sans',sans-serif}
              .ord-card-table{padding:.1rem .45rem;background:rgba(167,139,250,.1);border:1px solid rgba(167,139,250,.2);border-radius:6px;font-size:.65rem;color:#c4b5fd;font-weight:700}
              .ord-card-type{padding:.1rem .45rem;background:rgba(255,255,255,.06);border-radius:6px;font-size:.65rem;color:rgba(255,255,255,.5)}
              .ord-card-badges{display:flex;gap:.3rem;flex-wrap:wrap;flex:1}
              .ord-status-badge{padding:.2rem .55rem;border-radius:8px;font-size:.7rem;font-weight:700;white-space:nowrap}
              .ord-payment-badge{font-size:.68rem;font-weight:700;padding:.2rem .5rem;border-radius:8px;background:rgba(255,255,255,.04)}
              .ord-card-age{font-size:.68rem;color:rgba(255,255,255,.3);white-space:nowrap}
              .ord-card-age.urgent{color:#fbbf24;font-weight:700}
              .ord-card-items{display:flex;flex-wrap:wrap;gap:.3rem}
              .ord-item-pill{padding:.15rem .5rem;background:rgba(255,255,255,.06);border-radius:6px;font-size:.72rem;color:rgba(255,255,255,.6)}
              .ord-item-pill.more{color:rgba(255,255,255,.35);font-style:italic}
              .ord-card-foot{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap}
              .ord-card-customer{font-size:.78rem;font-weight:600;color:rgba(255,255,255,.7)}
              .ord-card-phone{font-size:.72rem;color:rgba(255,255,255,.35)}
              .ord-card-total{margin-left:auto;font-size:.88rem;font-weight:800;color:var(--pri)}
              .ord-card-quick{display:flex;gap:.3rem;flex-wrap:wrap;padding-top:.25rem;border-top:1px solid rgba(255,255,255,.05)}
              .ord-quick-btn{padding:.22rem .55rem;border-radius:7px;border:1px solid;background:none;font-family:'DM Sans',sans-serif;font-size:.65rem;font-weight:700;cursor:pointer;transition:all .15s}
              .ord-quick-btn:hover{opacity:.8;transform:scale(1.04)}
            `}</style>
          </div>
          );
        })()}

        {/* ─────────── BLOG ─────────── */}
        {activeTab === 'blog' && (
          <div className="adm-content">
            <div style={{ marginBottom: '1.25rem' }}>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.4rem', color: '#fff', marginBottom: '.25rem' }}>📝 {ta('adm_blog') || 'Blog'}</h1>
              <p style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.35)' }}>{ta('adm_blog_hint') || 'AI writes about your restaurant and its dishes. Editing re-translates into every enabled language.'}</p>
            </div>
            <BlogManager
              accent={settingsForm.primaryColor || '#c8a96e'}
              api={{
                list:     () => adminApi.listPosts(slug),
                generate: () => adminApi.generatePost(slug),
                create:   (fields) => adminApi.createPost(slug, fields),
                edit:     (ps, fields) => adminApi.editPost(slug, ps, fields),
                remove:   (ps) => adminApi.deletePost(slug, ps),
              }}
            />
          </div>
        )}

        {/* ─────────── VISITORS ─────────── */}
        {activeTab === 'visitors' && (
          <div className="adm-content">
            <div style={{ marginBottom: '1.25rem' }}>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.4rem', color: '#fff', marginBottom: '.25rem' }}>👣 {ta('adm_visitors') || 'Visitors'}</h1>
              <p style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.35)' }}>{ta('adm_visitors_hint') || 'Real visits to your menu, with bots separated from real people.'}</p>
            </div>
            <VisitorsPanel
              accent={settingsForm.primaryColor || '#c8a96e'}
              fetchStats={(d) => adminApi.getVisitors(slug, d)}
            />
          </div>
        )}

        {/* ─────────── ANALYTICS ─────────── */}
        {activeTab === 'analytics' && (
          <div className="adm-content">
            <div style={{ marginBottom: '1.25rem' }}>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.4rem', color: '#fff', marginBottom: '.25rem' }}>{ta("adm_analytics_ai")}</h1>
              <p style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.35)' }}>Real-time data · AI analyst · Full audit trail</p>
            </div>
            <AnalyticsPanel
              slug={slug}
              scope="restaurant"
              currencyCode={restaurant.currencyCode || 'USD'}
              fetchData={async (days) => adminApi.getAnalytics(slug, days)}
              fetchBehavior={async (days) => adminApi.getBehavior(slug, days)}
              fetchLogs={async (params) => adminApi.getAuditLogs(slug, params)}
              aiAnalyze={async (messages) => {
                const token = localStorage.getItem('nooshin_admin_token');
                const r = await fetch(`${API_BASE}/ai-analyze`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ messages, restaurantSlug: slug, scope: 'restaurant' }),
                });
                return r.json();
              }}
              exportAuditUrl={(params) => `${API_BASE}/admin/${slug}/audit/export.csv?${params}&token=${localStorage.getItem('nooshin_admin_token')}`}
            />
          </div>
        )}

        {/* ─────────── CUSTOMERS ─────────── */}
        {activeTab === 'customers' && (
          <div className="adm-content">
            <div className="adm-tab-bar">
              <span className="adm-count">{customers.length} customers</span>
              <button className="adm-refresh-btn" onClick={fetchCustomers}>{ta('adm_ord_refresh') || '↻ Refresh'}</button>
            </div>
            {customers.length === 0 ? (
              <div className="adm-empty">{ta("adm_no_customers")}</div>
            ) : (
              <div className="adm-orders-list">
                {customers.map(c => (
                  <CustomerRow key={c._id} customer={c} onSave={async (id, payload) => {
                    try {
                      await adminApi.updateCustomer(slug, id, payload);
                      setCustomers(prev => prev.map(x => x._id === id ? { ...x, ...payload } : x));
                      notify(ta('adm_t_cust_updated'));
                    } catch (e) { notify(e.message, 'error'); }
                  }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─────────── ADMINS ─────────── */}
        {activeTab === 'admins' && isRoot && (
          <div className="adm-content">
            <div className="adm-tab-bar">
              <span className="adm-count">{subAdmins.length} admins</span>
              <button className="adm-refresh-btn" onClick={fetchSubAdmins}>{ta('adm_ord_refresh') || '↻ Refresh'}</button>
            </div>
            <SubAdminPanel
              slug={slug}
              branches={(restaurant.branches || []).filter(b => b.isActive)}
              subAdmins={subAdmins}
              currentAdminId={adminUser?.id}
              onCreated={a => setSubAdmins(prev => [...prev, a])}
              onUpdated={(id, data) => setSubAdmins(prev => prev.map(x => x._id === id ? { ...x, ...data } : x))}
              onDeleted={id => setSubAdmins(prev => prev.filter(x => x._id !== id))}
              notify={notify}
            />
          </div>
        )}

        {/* ─────────── SETTINGS ─────────── */}
        {activeTab === 'settings' && (
          <div className="adm-content">
            <div className="adm-settings-form">
              <div className="adm-settings-grid">
                {[
                  ['name',        'Restaurant Name',  'text'],
                  ['tagline',     'Tagline',          'text'],
                  ['cuisine',     'Cuisine Type',     'text'],
                  ['phone',       'Phone',            'text'],
                  ['email',       'Contact Email',    'email'],
                  ['hours',       'Opening Hours',    'text'],
                ].map(([field, label, type]) => (
                  <div key={field} className="adm-field">
                    <label>{label}</label>
                    <input type={type} className="adm-input"
                      value={settingsForm[field] || ''}
                      onChange={e => setSettingsForm(f => ({ ...f, [field]: e.target.value }))} />
                  </div>
                ))}
                <div className="adm-field full">
                  <label>{ta("adm_address")}</label>
                  <input className="adm-input" value={settingsForm.address || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div className="adm-field full">
                  <label>{ta("adm_description")}</label>
                  <textarea className="adm-input" rows={4} value={settingsForm.description || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="adm-field full">
                  <label>{ta("adm_cover_image")}</label>
                  <ImageUploader
                    aspectRatio={16/9}
                    label="Upload Cover Photo"
                    currentUrl={settingsForm.coverImage}
                    uploadContext={{ slug, type: 'cover', name: 'cover' }}
                    aiEnhance={true}
                    imageType="banner"
                    onUploaded={url => setSettingsForm(f => ({ ...f, coverImage: url }))}
                  />
                  <div style={{marginTop:'.5rem',display:'flex',alignItems:'center',gap:'.5rem'}}>
                    <span style={{fontSize:'.7rem',color:'rgba(255,255,255,.3)',whiteSpace:'nowrap'}}>or URL:</span>
                    <input className="adm-input" value={settingsForm.coverImage || ''} style={{flex:1}}
                      onChange={e => setSettingsForm(f => ({ ...f, coverImage: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-field full">
                  <label>{ta("adm_logo")}</label>
                  <ImageUploader
                    aspectRatio={1}
                    label="Upload Logo"
                    currentUrl={settingsForm.logo}
                    uploadContext={{ slug, type: 'logo', name: 'logo' }}
                    aiEnhance={true}
                    imageType="logo"
                    onUploaded={url => setSettingsForm(f => ({ ...f, logo: url }))}
                  />
                  <div style={{marginTop:'.5rem',display:'flex',alignItems:'center',gap:'.5rem'}}>
                    <span style={{fontSize:'.7rem',color:'rgba(255,255,255,.3)',whiteSpace:'nowrap'}}>or URL:</span>
                    <input className="adm-input" value={settingsForm.logo || ''} style={{flex:1}}
                      onChange={e => setSettingsForm(f => ({ ...f, logo: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-field full">
                  <label>{ta("adm_favicon") || 'Favicon (browser tab icon)'}</label>
                  <div style={{maxWidth:120}}>
                    <ImageUploader
                      aspectRatio={1}
                      label="Upload Favicon"
                      currentUrl={settingsForm.favicon}
                      uploadContext={{ slug, type: 'favicon', name: 'favicon' }}
                      imageType="logo"
                      onUploaded={url => setSettingsForm(f => ({ ...f, favicon: url }))}
                    />
                  </div>
                </div>
                {/* ── Currency ── */}
                <div className="adm-field full">
                  <label>{ta("adm_currency")}</label>
                  <div className="cur-picker-wrap">
                    {/* Selected currency display */}
                    <div className="cur-selected">
                      {(() => {
                        const cur = CURRENCIES[settingsForm.currencyCode] || CURRENCIES.USD;
                        return (
                          <>
                            <span className="cur-sel-flag">{cur.flag}</span>
                            <span className="cur-sel-symbol">{cur.symbol}</span>
                            <span className="cur-sel-name">{cur.name}</span>
                            <span className="cur-sel-code">{cur.code}</span>
                          </>
                        );
                      })()}
                    </div>
                    {/* Search */}
                    <input className="adm-input cur-search"
                      placeholder="🔍 Search currencies… (USD, Euro, Yen…)"
                      value={currencySearch}
                      onChange={e => setCurrencySearch(e.target.value)} />
                    {/* Currency grid */}
                    <div className="cur-grid">
                      {(() => {
                        const q = currencySearch.toLowerCase();
                        const list = Object.values(CURRENCIES).filter(c =>
                          !q || c.code.toLowerCase().includes(q) ||
                          c.name.toLowerCase().includes(q) ||
                          c.symbol.toLowerCase().includes(q)
                        );
                        // Group by region
                        const regions = [...new Set(list.map(c => c.region))].sort();
                        return regions.map(region => (
                          <div key={region} className="cur-region-group">
                            <div className="cur-region-label">{region}</div>
                            <div className="cur-region-items">
                              {list.filter(c => c.region === region).map(c => (
                                <button key={c.code}
                                  className={`cur-btn ${settingsForm.currencyCode === c.code ? 'active' : ''}`}
                                  onClick={() => { setSettingsForm(f => ({ ...f, currencyCode: c.code })); setCurrencySearch(''); }}
                                  title={c.name}>
                                  <span className="cur-btn-flag">{c.flag}</span>
                                  <span className="cur-btn-code">{c.code}</span>
                                  <span className="cur-btn-symbol">{c.symbol}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>

                <div className="adm-field">
                  <label>{ta("adm_primary_color")}</label>
                  <div className="adm-color-row">
                    <input type="color" value={settingsForm.primaryColor || '#c8a96e'}
                      onChange={e => setSettingsForm(f => ({ ...f, primaryColor: e.target.value }))} />
                    <input className="adm-input" value={settingsForm.primaryColor || ''}
                      onChange={e => setSettingsForm(f => ({ ...f, primaryColor: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-field">
                  <label>{ta("adm_accent_color")}</label>
                  <div className="adm-color-row">
                    <input type="color" value={settingsForm.accentColor || '#1a1a2e'}
                      onChange={e => setSettingsForm(f => ({ ...f, accentColor: e.target.value }))} />
                    <input className="adm-input" value={settingsForm.accentColor || ''}
                      onChange={e => setSettingsForm(f => ({ ...f, accentColor: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* ── MENU APPEARANCE ──────────────────────────────────────── */}
              <div className="adm-appearance">
                <div className="adm-appearance-title">🎨 Menu Design</div>
                <p className="adm-appearance-sub">{ta("adm_appearance_sub")}</p>

                {/* Theme picker */}
                <label className="adm-appearance-label">{ta("adm_design_template")}</label>
                <div className="adm-theme-grid">
                  {MENU_THEMES.map(t => (
                    <button key={t.id} type="button"
                      className={`adm-theme-card ${settingsForm.menuTheme === t.id ? 'active' : ''}`}
                      onClick={() => setSettingsForm(f => ({ ...f, menuTheme: t.id }))}>
                      <span className={`adm-theme-thumb thumb-${t.id}`} aria-hidden>
                        <i></i><i></i><i></i>
                      </span>
                      <strong>{t.name}</strong>
                      <span className="adm-theme-desc">{t.desc}</span>
                    </button>
                  ))}
                </div>

                {/* Layout toggle */}
                <label className="adm-appearance-label">{ta("adm_category_bar")}</label>
                <div className="adm-seg">
                  {MENU_LAYOUTS.map(l => (
                    <button key={l.id} type="button"
                      className={`adm-seg-btn ${settingsForm.menuLayout === l.id ? 'active' : ''}`}
                      onClick={() => setSettingsForm(f => ({ ...f, menuLayout: l.id }))}
                      title={l.desc}>
                      {l.id === 'sidebar' ? '▥ ' : '▤ '}{l.name}
                    </button>
                  ))}
                </div>

                {/* Colour mode */}
                <label className="adm-appearance-label">{ta("adm_base_mode")}</label>
                <div className="adm-seg">
                  {['dark','light'].map(m => (
                    <button key={m} type="button"
                      className={`adm-seg-btn ${mcMode === m ? 'active' : ''}`}
                      onClick={() => setMenuColor('mode', m)}>
                      {m === 'dark' ? '🌙 Dark' : '☀️ Light'}
                    </button>
                  ))}
                </div>

                {/* Palette */}
                <label className="adm-appearance-label">{ta("adm_colors")}</label>
                <div className="adm-palette">
                  {[
                    ['accent','Accent'], ['bg','Background'], ['surface','Cards'], ['text','Text'],
                  ].map(([key,labl]) => (
                    <div key={key} className="adm-palette-item">
                      <input type="color" value={mcVal(key)} onChange={e => setMenuColor(key, e.target.value)} />
                      <div className="adm-palette-meta">
                        <span>{labl}</span>
                        <input className="adm-input adm-palette-hex" value={mcVal(key)}
                          onChange={e => setMenuColor(key, e.target.value)} />
                      </div>
                    </div>
                  ))}
                  <button type="button" className="adm-palette-reset"
                    onClick={() => setSettingsForm(f => ({ ...f, menuColors: { mode: mcMode } }))}
                    title="Reset colors to the theme defaults">↺ Reset colors</button>
                </div>

                {/* Live preview */}
                <label className="adm-appearance-label">{ta("adm_live_preview")}</label>
                {(() => {
                  const previewRestaurant = {
                    ...settingsForm,
                    menuColors: { ...(settingsForm.menuColors || {}), mode: mcMode },
                  };
                  const { theme: pt, layout: pl, colors: pc } = resolveMenuTheme(previewRestaurant);
                  return (
                    <div className="adm-menu-preview">
                      <div className={`menu-page theme-${pt} layout-${pl}`} style={menuCssVars(pc)}>
                        <div className="menu-nav">
                          <button className="back-btn">← {settingsForm.name || 'Restaurant'}</button>
                          <div className="nav-search"><span className="search-icon">🔍</span>
                            <input className="search-input" placeholder={ta("adm_ph_search_dishes")} readOnly /></div>
                          <div className="nav-actions"><button className="cart-btn">🛒</button></div>
                        </div>
                        <div className="menu-body">
                          <div className="category-bar">
                            {['Starters','Mains','Drinks','Desserts'].map((c,i) => (
                              <button key={c} className={`cat-pill ${i===1?'active':''}`}>
                                <span className="cat-icon">{['🥗','🍝','🥤','🍰'][i]}</span>
                                <span className="cat-name">{c}</span></button>
                            ))}
                          </div>
                          <div className="menu-content">
                            <div className="section-header">
                              <h2 className="section-title">Mains</h2>
                              <span className="item-count">3 items</span>
                            </div>
                            <div className="products-grid">
                              {[['Truffle Pasta','Hand-made tagliatelle, black truffle, parmesan',22],
                                ['Grilled Salmon','Citrus glaze, seasonal greens',26],
                                ['Margherita','San Marzano tomato, basil, mozzarella',15]].map(([n,d,pr],i)=>(
                                <div key={n} className={`product-card pc-${pt}`}>
                                  <div className="card-img-wrap"><div className="card-img-fallback">🍽️</div></div>
                                  <div className="card-body">
                                    <h3 className="card-name">{n}</h3>
                                    <p className="card-desc">{d}</p>
                                    <div className="card-footer">
                                      <span className="card-price">${pr}</span>
                                      <button className="add-btn">+ Add</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <style>{buildMenuCss()}</style>
                    </div>
                  );
                })()}
              </div>

              <div className="adm-field full" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'1rem',flexWrap:'wrap',padding:'1rem',border:'1px solid var(--adm-border,rgba(0,0,0,.1))',borderRadius:12,marginTop:'1rem'}}>
                <div style={{flex:'1 1 220px'}}>
                  <label style={{fontWeight:600,display:'block',marginBottom:'.25rem'}}>📝 {ta("adm_ai_blog")}</label>
                  <span style={{fontSize:'.82rem',opacity:.7}}>{ta("adm_ai_blog_hint")}</span>
                </div>
                <button type="button" role="switch" aria-checked={!!settingsForm.aiBlogEnabled}
                  onClick={() => setSettingsForm(f => ({ ...f, aiBlogEnabled: !f.aiBlogEnabled }))}
                  style={{padding:'.5rem 1rem',borderRadius:999,border:'none',cursor:'pointer',fontWeight:700,
                    background: settingsForm.aiBlogEnabled ? '#16a34a' : 'rgba(120,120,120,.25)',
                    color: settingsForm.aiBlogEnabled ? '#fff' : 'inherit'}}>
                  {settingsForm.aiBlogEnabled ? `✓ ${ta("adm_on") || 'On'}` : `✗ ${ta("adm_off") || 'Off'}`}
                </button>
              </div>

              <button className="adm-save-btn" onClick={saveSettings}>{ta("adm_save_settings")}</button>
            </div>
          </div>
        )}

        {/* ─────────── LANGUAGE ─────────── */}
        {activeTab === 'language' && (
          <div className="adm-content">
            <div className="adm-settings-form">

              {/* ── LANGUAGE MODE ─────────────────────────────────────── */}
              <div className="lang-section">
                <h3 className="lang-section-title">{ta("adm_lang_mode")}</h3>
                <p className="lang-hint">Single-language menus are simpler. Multi-language menus let customers switch languages — and unlock full SEO for every language.</p>
                <div className="lang-mode-cards">
                  {[
                    { val: 'single', icon: '🔤', title: 'Single Language', desc: 'One fixed language. Clean, simple. Good for local restaurants.' },
                    { val: 'multi',  icon: '🌐', title: 'Multi-Language + SEO',  desc: 'Customers switch languages. Google indexes your menu in each language separately.' },
                  ].map(opt => (
                    <button key={opt.val}
                      className={`lang-mode-card ${langForm.languageMode === opt.val ? 'active' : ''}`}
                      onClick={() => setLangForm(f => ({ ...f, languageMode: opt.val }))}>
                      <span className="lang-mode-icon">{opt.icon}</span>
                      <strong>{opt.title}</strong>
                      <span>{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── DEFAULT LANGUAGE ──────────────────────────────────── */}
              <div className="lang-section">
                <h3 className="lang-section-title">{ta("adm_default_lang")}</h3>
                <p className="lang-hint">This is the language your menu shows by default. Affects the HTML <code>lang</code> attribute and the x-default hreflang tag for SEO.</p>
                <div className="lang-flags-grid">
                  {Object.values(LANGUAGES).map(lang => (
                    <button key={lang.code}
                      className={`lang-flag-btn ${langForm.defaultLanguage === lang.code ? 'active' : ''}`}
                      onClick={() => setLangForm(f => ({ ...f, defaultLanguage: lang.code }))}>
                      <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── ENABLED LANGUAGES (multi mode) ────────────────────── */}
              {langForm.languageMode === 'multi' && (
                <div className="lang-section">
                  <h3 className="lang-section-title">{ta("adm_enabled_langs")}</h3>
                  <p className="lang-hint">Each enabled language gets its own SEO-indexed URL (<code>?lang=XX</code>), hreflang tag, Open Graph locale, and JSON-LD structured data entry.</p>
                  <div className="lang-flags-grid">
                    {Object.values(LANGUAGES).map(lang => (
                      <button key={lang.code}
                        className={`lang-flag-btn ${langForm.enabledLanguages.includes(lang.code) ? 'active' : ''}`}
                        onClick={() => toggleEnabledLang(lang.code)}>
                        <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                        <span>{lang.name}</span>
                        {langForm.enabledLanguages.includes(lang.code) && <span className="lang-check">✓</span>}
                      </button>
                    ))}
                  </div>
                  {/* SEO impact summary */}
                  {langForm.enabledLanguages.length > 1 && (
                    <div className="lang-seo-impact">
                      <div className="lang-seo-impact-title">📡 SEO Impact</div>
                      <div className="lang-seo-impact-grid">
                        {langForm.enabledLanguages.map(code => {
                          const meta = LANGUAGES[code];
                          const pct  = getCompleteness(code);
                          const isDef = code === langForm.defaultLanguage;
                          return (
                            <div key={code} className="lang-seo-impact-card">
                              <div className="lang-seo-impact-head">
                                <span>{meta?.flag} {meta?.name}</span>
                                {isDef && <span className="lang-default-badge">default</span>}
                              </div>
                              <div className="lang-completeness-bar-wrap">
                                <div className="lang-completeness-bar"
                                  style={{width: isDef ? '100%' : pct+'%',
                                    background: pct===100?'#10b981':pct>60?'#f59e0b':'#ef4444'}} />
                              </div>
                              <div className="lang-completeness-label">
                                {isDef ? '100% (source)' : pct + '% translated'}
                              </div>
                              <a href={`/?lang=${code}`} target="_blank" rel="noopener noreferrer"
                                className="lang-preview-link">
                                🔍 Preview SEO page →
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── TRANSLATIONS ──────────────────────────────────────── */}
              <div className="lang-section">
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'.75rem',marginBottom:'.5rem'}}>
                  <h3 className="lang-section-title" style={{margin:0}}>{ta("adm_translations")}</h3>
                  <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap'}}>
                    <button className="lang-io-btn" title="Export all translations as JSON"
                      onClick={() => {
                        const exp = { translations, uiTranslations, infoTrans, seoTrans };
                        const blob = new Blob([JSON.stringify(exp, null, 2)], { type: 'application/json' });
                        const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                        a.download = `${slug}-translations.json`; a.click();
                      }}>
                      ⬇ Export JSON
                    </button>
                    <label className="lang-io-btn" title="Import translations from a previously exported JSON">
                      ⬆ Import JSON
                      <input type="file" accept=".json" style={{display:'none'}}
                        onChange={async e => {
                          const file = e.target.files[0]; if (!file) return;
                          try {
                            const text = await file.text();
                            const imp = JSON.parse(text);
                            if (imp.translations) setTranslations(imp.translations);
                            if (imp.uiTranslations) setUiTranslations(imp.uiTranslations);
                            if (imp.infoTrans) setInfoTrans(imp.infoTrans);
                            if (imp.seoTrans) setSeoTrans(imp.seoTrans);
                            notify(ta('adm_t_trans_imported'));
                          } catch { notify(ta('adm_t_invalid_json'), 'error'); }
                          e.target.value = '';
                        }} />
                    </label>
                  </div>
                </div>
                <p className="lang-hint">Translate your content into each language. Auto-Translate uses free machine translation — always review the output.</p>

                {/* Language picker */}
                <div className="lang-trans-controls">
                  <span style={{fontSize:'.82rem',color:'rgba(255,255,255,.45)'}}>Translating into:</span>
                  <div className="lang-trans-tabs">
                    {(langForm.languageMode === 'multi'
                      ? langForm.enabledLanguages
                      : Object.keys(LANGUAGES)
                    ).map(code => {
                      const pct = getCompleteness(code);
                      const isDefault = code === langForm.defaultLanguage;
                      return (
                        <button key={code}
                          className={`lang-trans-tab ${transLang === code ? 'active' : ''}`}
                          onClick={() => setTransLang(code)}
                          title={isDefault ? (ta('adm_t_clean_into') || 'Normalize / clean all content into this language') : undefined}>
                          {LANGUAGES[code]?.flag} {LANGUAGES[code]?.name}{isDefault ? ' ⭐' : ''}
                          <span className="lang-tab-pct" style={{
                            background: pct===100?'rgba(16,185,129,.2)':pct>60?'rgba(245,158,11,.2)':'rgba(239,68,68,.15)',
                            color: pct===100?'#6ee7b7':pct>60?'#fde68a':'#fca5a5',
                          }}>{pct}%</span>
                        </button>
                      );
                    })}
                  </div>
                  <button className="lang-auto-btn" onClick={autoTranslateAll}
                    disabled={!!autoTranslating._all || serverTransAll}
                    title={ta('adm_t_auto_title')}>
                    {autoTranslating._all ? `⏳ ${ta('adm_t_translating')}` : `✨ ${ta('adm_t_auto_translate')} ${LANGUAGES[transLang]?.name || ''}`}
                  </button>
                  <button className="lang-auto-btn" onClick={serverTranslateAllLanguages}
                    disabled={serverTransAll || !!autoTranslating._all}
                    style={{ background:'rgba(16,185,129,.18)', color:'#6ee7b7', borderColor:'rgba(16,185,129,.4)' }}
                    title={ta('adm_t_all_title')}>
                    {serverTransAll ? `⏳ ${ta('adm_t_translating_all')}` : `🌍 ${ta('adm_t_translate_all_save')}`}
                  </button>
                </div>
                <div className="lang-method-note">
                  💡 {ta('adm_t_method_note')}
                </div>

                {/* ── SEO FIELDS PER LANGUAGE ───────────────────────── */}
                <div className="lang-trans-group lang-seo-group">
                  <div className="lang-trans-group-title">🔍 SEO — Search Engine Optimization for {LANGUAGES[transLang]?.name}</div>
                  <div className="lang-seo-info">
                    <span>ℹ️</span>
                    <p>Google shows these in search results for <strong>{LANGUAGES[transLang]?.nativeName || transLang}</strong>-speaking users. If left blank, your translated name + tagline is used automatically.</p>
                  </div>
                  <div className="lang-seo-field">
                    <div className="lang-seo-field-header">
                      <label>{ta("adm_page_title")}</label>
                      <span className={`lang-seo-counter ${(seoTrans[transLang]?.title||'').length > 60 ? 'over' : ''}`}>
                        {(seoTrans[transLang]?.title||'').length}/60
                      </span>
                    </div>
                    <input className="lang-trans-input"
                      placeholder={`e.g. ${restaurant.name} — Authentic ${restaurant.cuisine || 'Food'} | Book Online`}
                      value={seoTrans[transLang]?.title || ''}
                      onChange={e => setSeoField(transLang, 'title', e.target.value)} />
                    <span className="lang-seo-hint">Shown as the clickable headline in Google results. Keep under 60 characters.</span>
                  </div>
                  <div className="lang-seo-field">
                    <div className="lang-seo-field-header">
                      <label>{ta("adm_meta_desc")}</label>
                      <span className={`lang-seo-counter ${(seoTrans[transLang]?.description||'').length > 160 ? 'over' : ''}`}>
                        {(seoTrans[transLang]?.description||'').length}/160
                      </span>
                    </div>
                    <textarea className="lang-trans-input" rows={3}
                      placeholder={ta("adm_ph_describe_rest")}
                      value={seoTrans[transLang]?.description || ''}
                      onChange={e => setSeoField(transLang, 'description', e.target.value)} />
                    <span className="lang-seo-hint">The snippet shown below the title in search results. Max 160 characters for best results.</span>
                  </div>
                  <div className="lang-seo-field">
                    <div className="lang-seo-field-header">
                      <label>{ta("adm_keywords")}</label>
                    </div>
                    <input className="lang-trans-input"
                      placeholder="e.g. Japanese restaurant Tokyo, sushi delivery, ramen"
                      value={seoTrans[transLang]?.keywords || ''}
                      onChange={e => setSeoField(transLang, 'keywords', e.target.value)} />
                    <span className="lang-seo-hint">{ta("adm_keywords_hint")}</span>
                  </div>
                  {/* Live SERP preview */}
                  <div className="lang-serp-preview">
                    <div className="lang-serp-title">{seoTrans[transLang]?.title || infoTrans[transLang]?.name || restaurant.name + ' — Digital Menu'}</div>
                    <div className="lang-serp-url">https://{slug}.nowshin.ir{transLang !== langForm.defaultLanguage ? '/?lang='+transLang : '/'}</div>
                    <div className="lang-serp-desc">{seoTrans[transLang]?.description || infoTrans[transLang]?.description || infoTrans[transLang]?.tagline || restaurant.description || 'Add a meta description to show here.'}</div>
                  </div>
                </div>

                {/* ── RESTAURANT INFO TRANSLATIONS ──────────────────── */}
                <div className="lang-trans-group" style={{marginBottom:'1rem'}}>
                  <div className="lang-trans-group-title">🏠 Restaurant Info (Landing Page)</div>
                  {[
                    { field:'name',        label:'Restaurant Name',  src: restaurant.name        },
                    { field:'tagline',     label:'Tagline',          src: restaurant.tagline || restaurant.description || '' },
                    { field:'description', label:'Description',      src: restaurant.description || '' },
                    { field:'address',     label:'Address',          src: restaurant.address     || '' },
                    { field:'phone',       label:'Phone',            src: restaurant.phone       || '' },
                    { field:'hours',       label:'Hours',            src: restaurant.hours       || '' },
                    { field:'email',       label:'Email',            src: restaurant.email       || '' },
                  ].map(({ field, label, src }) => (
                    <div key={field} className="lang-trans-row prod">
                      <div className="lang-trans-original-wrap">
                        <span className="lang-trans-original">{label}</span>
                        <span className="lang-trans-sub">{src}</span>
                      </div>
                      <div style={{display:'flex',gap:'.4rem',flex:1}}>
                        <input className="lang-trans-input"
                          placeholder={`${label} in ${LANGUAGES[transLang]?.name || transLang}…`}
                          value={infoTrans[transLang]?.[field] || ''}
                          onChange={e => setInfoField(field, e.target.value)} />
                        {src && (
                          <button className="lang-single-auto"
                            onClick={() => autoTranslateInfoField(field, src)}
                            disabled={!!autoTranslating[`info-${field}`]}
                            title={`Auto-translate "${src}"`}>
                            {autoTranslating[`info-${field}`] ? '…' : '✨'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── CATEGORY TRANSLATIONS ─────────────────────────── */}
                {categories.length > 0 && (
                  <div className="lang-trans-group">
                    <div className="lang-trans-group-title">📂 Categories</div>
                    {categories.map(cat => (
                      <div key={cat._id} className="lang-trans-row">
                        <div className="lang-trans-original-wrap">
                          <span className="lang-trans-original">{cat.name}</span>
                        </div>
                        <span className="lang-trans-arrow">→</span>
                        <div style={{display:'flex',gap:'.4rem',flex:1}}>
                          <input className="lang-trans-input"
                            placeholder={`${LANGUAGES[transLang]?.name || transLang} name…`}
                            value={translations[transLang]?.[cat._id]?.name || ''}
                            onChange={e => setCategoryTrans(cat._id, e.target.value)} />
                          <button className="lang-single-auto"
                            onClick={() => autoTranslateSingle(cat._id, 'name', cat.name)}
                            disabled={!!autoTranslating[`${cat._id}-name`]}>
                            {autoTranslating[`${cat._id}-name`] ? '…' : '✨'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── PRODUCT TRANSLATIONS ──────────────────────────── */}
                {products.length > 0 && (
                  <div className="lang-trans-group">
                    <div className="lang-trans-group-title">🍽️ Products ({products.length})</div>
                    {products.map(prod => (
                      <div key={prod._id} className="lang-trans-row prod">
                        <div className="lang-trans-original-wrap">
                          <span className="lang-trans-original">{prod.name}</span>
                          {prod.description && <span className="lang-trans-sub">{prod.description.slice(0,80)}{prod.description.length>80?'…':''}</span>}
                        </div>
                        <div className="lang-trans-fields">
                          <div style={{display:'flex',gap:'.4rem'}}>
                            <input className="lang-trans-input"
                              placeholder={`Name in ${LANGUAGES[transLang]?.name || transLang}…`}
                              value={translations[transLang]?.[prod._id]?.name || ''}
                              onChange={e => setProductTrans(prod._id, 'name', e.target.value)} />
                            <button className="lang-single-auto"
                              onClick={() => autoTranslateSingle(prod._id, 'name', prod.name)}
                              disabled={!!autoTranslating[`${prod._id}-name`]}>
                              {autoTranslating[`${prod._id}-name`] ? '…' : '✨'}
                            </button>
                          </div>
                          <div style={{display:'flex',gap:'.4rem'}}>
                            <input className="lang-trans-input"
                              placeholder={ta("adm_description_opt")+"…"}
                              value={translations[transLang]?.[prod._id]?.description || ''}
                              onChange={e => setProductTrans(prod._id, 'description', e.target.value)} />
                            {prod.description && (
                              <button className="lang-single-auto"
                                onClick={() => autoTranslateSingle(prod._id, 'description', prod.description)}
                                disabled={!!autoTranslating[`${prod._id}-description`]}>
                                {autoTranslating[`${prod._id}-description`] ? '…' : '✨'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── UI STRINGS ────────────────────────────────────────── */}
              <div className="lang-section">
                <h3 className="lang-section-title">🖥️ UI Strings</h3>
                <p className="lang-hint">{ta("adm_ui_trans_desc")}</p>
                <div className="lang-trans-controls">
                  <span style={{fontSize:'.82rem',color:'rgba(255,255,255,.45)'}}>
                    Translating UI into: <strong style={{color:'#fff'}}>{LANGUAGES[transLang]?.flag} {LANGUAGES[transLang]?.name}</strong>
                  </span>
                  <button className="lang-auto-btn" onClick={autoTranslateAllUI}
                    disabled={!!autoTranslating._ui}>
                    {autoTranslating._ui ? '⏳ Translating…' : '✨ Auto-Translate All UI'}
                  </button>
                </div>
                {UI_SECTIONS.map(section => (
                  <div key={section.id} className="lang-trans-group" style={{marginBottom:'.875rem'}}>
                    <div className="lang-trans-group-title">{section.label}</div>
                    {section.keys.map(({ key, default: defaultText }) => (
                      <div key={key} className="lang-ui-row">
                        <div className="lang-ui-original">
                          <span className="lang-ui-key">{key.replace(/_/g,' ')}</span>
                          <span className="lang-ui-default">{defaultText}</span>
                        </div>
                        <div style={{display:'flex',gap:'.4rem',flex:1}}>
                          <input className="lang-trans-input"
                            placeholder={defaultText}
                            value={uiTranslations[transLang]?.[key] || ''}
                            onChange={e => setUiTrans(key, e.target.value)} />
                          <button className="lang-single-auto"
                            onClick={() => autoTranslateSingleUI(key, defaultText)}
                            disabled={!!autoTranslating[`ui-${key}`]}>
                            {autoTranslating[`ui-${key}`] ? '…' : '✨'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <button className="adm-save-btn" onClick={saveLangSettings} disabled={savingLang}>
                {savingLang ? '⏳ Saving…' : '💾 Save Language & SEO Settings'}
              </button>
            </div>
          </div>
        )}

        {/* ── EVENTS & PROMOTIONS TAB ── */}
        {activeTab === 'events' && (
          <div className="adm-content">
            {!selEventKey ? (
              /* ── Event picker grid ── */
              <div>
                <div className="evt-header">
                  <h2 className="evt-title">🎉 Events & Promotions</h2>
                  <p className="evt-subtitle">{ta("adm_event_select")}</p>
                </div>
                <div className="evt-grid">
                  {[
                    {key:'valentine',    emoji:'❤️',  label:"Valentine's Day",   date:'Feb 14'},
                    {key:'ramadan',      emoji:'🌙',  label:'Ramadan',            date:'Varies'},
                    {key:'eid',          emoji:'🌙',  label:'Eid Celebration',    date:'Varies'},
                    {key:'nowruz',       emoji:'🌱',  label:'Nowruz',             date:'Mar 20'},
                    {key:'easter',       emoji:'🐣',  label:'Easter',             date:'Varies'},
                    {key:'mothers_day',  emoji:'💐',  label:"Mother's Day",       date:'May'},
                    {key:'halloween',    emoji:'🎃',  label:'Halloween',          date:'Oct 31'},
                    {key:'diwali',       emoji:'🪔',  label:'Diwali',             date:'Varies'},
                    {key:'chinese_newyear',emoji:'🧧',label:'Chinese New Year',   date:'Varies'},
                    {key:'thanksgiving', emoji:'🦃',  label:'Thanksgiving',       date:'Nov'},
                    {key:'christmas',    emoji:'🎄',  label:'Christmas',          date:'Dec 25'},
                    {key:'newyear',      emoji:'🎆',  label:"New Year's Eve",     date:'Dec 31'},
                    {key:'summer',       emoji:'☀️',  label:'Summer Special',     date:'Seasonal'},
                    {key:'winter',       emoji:'❄️',  label:'Winter Warmers',     date:'Seasonal'},
                    {key:'friday',       emoji:'🎉',  label:'Friday Night Special',date:'Weekly'},
                    {key:'brunch',       emoji:'🥂',  label:'Weekend Brunch',     date:'Weekly'},
                    {key:'black_friday', emoji:'🛍️',  label:'Black Friday',       date:'Nov'},
                    {key:'birthday',     emoji:'🎂',  label:'Birthday Special',   date:'Ongoing'},
                  ].map(ev => {
                    const configured = events.find(e => e.eventKey === ev.key);
                    const isActive   = configured?.active;
                    return (
                      <button key={ev.key} className={`evt-card ${isActive ? 'active' : ''} ${configured ? 'configured' : ''}`}
                        onClick={() => openEvent(ev.key)}>
                        <span className="evt-card-emoji">{ev.emoji}</span>
                        <span className="evt-card-label">{ev.label}</span>
                        <span className="evt-card-date">{ev.date}</span>
                        {isActive  && <span className="evt-card-badge live">{ta("adm_live")}</span>}
                        {configured && !isActive && <span className="evt-card-badge off">{ta("adm_saved")}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* ── Event configurator ── */
              (() => {
                const evMeta = {
                  valentine:{emoji:'❤️',label:"Valentine's Day"},ramadan:{emoji:'🌙',label:'Ramadan'},
                  eid:{emoji:'🌙',label:'Eid'},nowruz:{emoji:'🌱',label:'Nowruz'},
                  easter:{emoji:'🐣',label:'Easter'},mothers_day:{emoji:'💐',label:"Mother's Day"},
                  halloween:{emoji:'🎃',label:'Halloween'},diwali:{emoji:'🪔',label:'Diwali'},
                  chinese_newyear:{emoji:'🧧',label:'Chinese New Year'},thanksgiving:{emoji:'🦃',label:'Thanksgiving'},
                  christmas:{emoji:'🎄',label:'Christmas'},newyear:{emoji:'🎆',label:"New Year's Eve"},
                  summer:{emoji:'☀️',label:'Summer Special'},winter:{emoji:'❄️',label:'Winter Warmers'},
                  friday:{emoji:'🎉',label:'Friday Night Special'},brunch:{emoji:'🥂',label:'Weekend Brunch'},
                  black_friday:{emoji:'🛍️',label:'Black Friday'},birthday:{emoji:'🎂',label:'Birthday Special'},
                }[selEventKey] || {emoji:'🎉',label:selEventKey};
                const setF = (k,v) => setEventForm(f=>({...f,[k]:v}));
                const toggleProduct = (pid) => setF('featuredProducts',
                  eventForm.featuredProducts?.includes(pid)
                    ? eventForm.featuredProducts.filter(p=>p!==pid)
                    : [...(eventForm.featuredProducts||[]),pid]
                );
                return (
                  <div className="evt-config">
                    {/* Back */}
                    <button className="evt-back-btn" onClick={() => setSelEventKey(null)}>← All Events</button>
                    <div className="evt-config-header">
                      <span className="evt-config-emoji">{evMeta.emoji}</span>
                      <div>
                        <h2 className="evt-config-title">{evMeta.label}</h2>
                        <p className="evt-config-sub">{ta("adm_event_config")}</p>
                      </div>
                      <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'.75rem'}}>
                        <label className="evt-toggle-wrap">
                          <input type="checkbox" checked={!!eventForm.active} onChange={e=>setF('active',e.target.checked)} />
                          <span className="evt-toggle-slider"/>
                          <span className="evt-toggle-label">{eventForm.active?'LIVE':'Off'}</span>
                        </label>
                      </div>
                    </div>

                    <div className="evt-config-grid">
                      {/* ── Left column ── */}
                      <div className="evt-col">

                        {/* Basic info */}
                        <div className="evt-section">
                          <div className="evt-section-title">📝 Popup Content</div>
                          <label className="evt-label">{ta("adm_title_in_popup")}</label>
                          <input className="adm-input" value={eventForm.title||''} onChange={e=>setF('title',e.target.value)}
                            placeholder={`e.g. Happy ${evMeta.label}! 🎉`} />
                          <label className="evt-label" style={{marginTop:'.6rem'}}>{ta("adm_message")}</label>
                          <textarea className="adm-input" rows={3} value={eventForm.message||''} onChange={e=>setF('message',e.target.value)}
                            placeholder="e.g. Celebrate with our special menu — made with love just for you." />
                        </div>

                        {/* Date range */}
                        <div className="evt-section">
                          <div className="evt-section-title">📅 Date Range</div>
                          <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap'}}>
                            <div style={{flex:1,minWidth:'130px'}}>
                              <label className="evt-label">{ta("adm_start")}</label>
                              <input type="date" className="adm-input" value={eventForm.startDate||''} onChange={e=>setF('startDate',e.target.value)} />
                            </div>
                            <div style={{flex:1,minWidth:'130px'}}>
                              <label className="evt-label">{ta("adm_end")}</label>
                              <input type="date" className="adm-input" value={eventForm.endDate||''} onChange={e=>setF('endDate',e.target.value)} />
                            </div>
                          </div>
                          <p className="evt-hint">{ta("adm_popup_daterange")}</p>
                        </div>

                        {/* Discount */}
                        <div className="evt-section">
                          <div className="evt-section-title">💰 Discount / Offer</div>
                          <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap',alignItems:'flex-end'}}>
                            <div>
                              <label className="evt-label">{ta("adm_type")}</label>
                              <select className="adm-input" value={eventForm.discountType||'none'} onChange={e=>setF('discountType',e.target.value)}
                                style={{minWidth:'110px'}}>
                                <option value="none">{ta("adm_no_discount")}</option>
                                <option value="percentage">Percentage %</option>
                                <option value="fixed">{ta("adm_fixed_amount")}</option>
                              </select>
                            </div>
                            {eventForm.discountType !== 'none' && (
                              <div>
                                <label className="evt-label">{ta("adm_value")}</label>
                                <input className="adm-input" type="number" min="0" step="1" style={{width:'80px'}}
                                  value={eventForm.discountValue||0} onChange={e=>setF('discountValue',parseFloat(e.target.value))} />
                              </div>
                            )}
                            {eventForm.discountType !== 'none' && (
                              <div style={{flex:1,minWidth:'120px'}}>
                                <label className="evt-label">{ta("adm_promo_code")}</label>
                                <input className="adm-input" placeholder="e.g. LOVE20"
                                  value={eventForm.discountCode||''} onChange={e=>setF('discountCode',e.target.value.toUpperCase())} />
                              </div>
                            )}
                          </div>
                          {eventForm.discountType !== 'none' && (
                            <div className="evt-discount-preview">
                              {eventForm.discountType==='percentage' ? `${eventForm.discountValue||0}% OFF` : `${CURRENCIES[restaurant.currencyCode]?.symbol||'$'}${eventForm.discountValue||0} OFF`}
                              {eventForm.discountCode ? ` · Code: ${eventForm.discountCode}` : ' (auto-applied)'}
                            </div>
                          )}
                        </div>

                        {/* Popup style */}
                        <div className="evt-section">
                          <div className="evt-section-title">🎨 Popup Style</div>
                          <div className="evt-style-grid">
                            {[
                              {k:'elegant',label:'Elegant'},
                              {k:'luxury',label:'Luxury'},
                              {k:'playful',label:'Playful'},
                              {k:'warm',label:'Warm'},
                              {k:'dark',label:'Dark'},
                              {k:'vibrant',label:'Vibrant'},
                            ].map(s=>(
                              <button key={s.k} type="button"
                                className={`evt-style-btn ${eventForm.popupStyle===s.k?'active':''}`}
                                onClick={()=>setF('popupStyle',s.k)}>{s.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Featured products */}
                        <div className="evt-section">
                          <div className="evt-section-title">🍽️ Featured Products</div>
                          <p className="evt-hint">{ta("adm_featured_badge")}</p>
                          <div className="evt-product-grid">
                            {products.map(p => (
                              <button key={p._id} type="button"
                                className={`evt-product-btn ${(eventForm.featuredProducts||[]).includes(p._id)?'active':''}`}
                                onClick={()=>toggleProduct(p._id)}>
                                {p.image && <img src={p.image} alt="" className="evt-product-img" onError={e=>e.target.style.display='none'} />}
                                <span className="evt-product-name">{p.name}</span>
                                <span className="evt-product-price">{formatPrice(p.price,restaurant.currencyCode)}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* ── Right column: Banner ── */}
                      <div className="evt-col">
                        <div className="evt-section">
                          <div className="evt-section-title">🖼️ Event Banner</div>

                          {/* Current banner preview */}
                          {eventForm.bannerImage ? (
                            <div className="evt-banner-preview-wrap">
                              <img src={eventForm.bannerImage} alt="banner" className="evt-banner-preview" />
                              <button className="evt-banner-clear" onClick={()=>setF('bannerImage','')} title="Remove">✕</button>
                            </div>
                          ) : (
                            <div className="evt-banner-placeholder">No banner yet — generate one with AI below</div>
                          )}

                          {/* AI Generate banner */}
                          <div className="evt-gen-box">
                            <div className="evt-gen-box-title">✦ Generate with AI</div>
                            <textarea className="adm-input" rows={2}
                              placeholder={`Optional: describe extras (e.g. "warm candlelight, roses, couple dining")`}
                              value={evtGenPrompt}
                              onChange={e=>setEvtGenPrompt(e.target.value)} />
                            <button className="evt-gen-btn" onClick={generateEventBanner} disabled={eventBannerLoading}>
                              {eventBannerLoading ? '✦ Generating…' : `✦ Generate ${evMeta.emoji} Banner`}
                            </button>
                            <p className="evt-gen-hint">AI creates a themed banner automatically. Generates in ~20 seconds.</p>
                          </div>

                          {/* Manual URL upload */}
                          <div style={{marginTop:'.75rem'}}>
                            <label className="evt-label">{ta("adm_paste_image_url")}</label>
                            <input className="adm-input" placeholder="https://..." value={eventForm.bannerImage||''}
                              onChange={e=>setF('bannerImage',e.target.value)} />
                          </div>
                        </div>

                        {/* Live popup preview */}
                        <div className="evt-section">
                          <div className="evt-section-title">👁️ Popup Preview</div>
                          <div className={`evt-popup-preview popup-style-${eventForm.popupStyle||'elegant'}`}>
                            {eventForm.bannerImage && <img src={eventForm.bannerImage} alt="" className="evt-popup-banner" />}
                            <div className="evt-popup-body">
                              <div className="evt-popup-emoji-row">{evMeta.emoji}</div>
                              <h3 className="evt-popup-title">{eventForm.title || evMeta.label + ' Special'}</h3>
                              <p className="evt-popup-msg">{eventForm.message || 'A special menu just for you.'}</p>
                              {eventForm.discountType !== 'none' && (
                                <div className="evt-popup-discount">
                                  {eventForm.discountType==='percentage' ? `${eventForm.discountValue||0}% OFF` : `${CURRENCIES[restaurant.currencyCode]?.symbol||'$'}${eventForm.discountValue||0} OFF`}
                                  {eventForm.discountCode && <span className="evt-popup-code">{eventForm.discountCode}</span>}
                                </div>
                              )}
                              <div className="evt-popup-cta">See Special Menu →</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Save / Delete */}
                    <div className="evt-config-footer">
                      <button className="evt-delete-btn" onClick={deleteCurrentEvent}>{ta("adm_remove_event")}</button>
                      <button className="adm-save-btn evt-save-btn" onClick={saveEventForm} disabled={eventSaving}>
                        {eventSaving ? 'Saving…' : '💾 Save Event'}
                      </button>
                    </div>
                  </div>
                );
              })()
            )}

            {/* Events CSS */}
            <style>{`
              .evt-header{margin-bottom:1.5rem}
              .evt-title{font-family:'Playfair Display',serif;font-size:1.5rem;color:#fff;margin:0 0 .35rem}
              .evt-subtitle{font-size:.85rem;color:rgba(255,255,255,.4);margin:0}
              .evt-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:.75rem}
              .evt-card{position:relative;display:flex;flex-direction:column;align-items:center;gap:.35rem;padding:1.1rem .75rem .9rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;cursor:pointer;transition:all .2s;text-align:center;font-family:'DM Sans',sans-serif}
              .evt-card:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.15);transform:translateY(-2px)}
              .evt-card.configured{border-color:rgba(200,169,110,.25);background:rgba(200,169,110,.05)}
              .evt-card.active{border-color:rgba(16,185,129,.4);background:rgba(16,185,129,.08);box-shadow:0 0 16px rgba(16,185,129,.12)}
              .evt-card-emoji{font-size:2rem;line-height:1}
              .evt-card-label{font-size:.78rem;font-weight:700;color:#fff}
              .evt-card-date{font-size:.65rem;color:rgba(255,255,255,.35)}
              .evt-card-badge{position:absolute;top:.45rem;right:.45rem;padding:.1rem .4rem;border-radius:5px;font-size:.58rem;font-weight:800;letter-spacing:.04em}
              .evt-card-badge.live{background:rgba(16,185,129,.25);color:#6ee7b7}
              .evt-card-badge.off{background:rgba(255,255,255,.08);color:rgba(255,255,255,.4)}
              .evt-back-btn{background:none;border:none;color:rgba(255,255,255,.5);font-family:'DM Sans',sans-serif;font-size:.85rem;cursor:pointer;padding:0;margin-bottom:1rem;display:flex;align-items:center;gap:.3rem}
              .evt-back-btn:hover{color:#fff}
              .evt-config-header{display:flex;align-items:center;gap:1rem;margin-bottom:1.5rem;flex-wrap:wrap}
              .evt-config-emoji{font-size:2.5rem;line-height:1}
              .evt-config-title{font-family:'Playfair Display',serif;font-size:1.4rem;color:#fff;margin:0}
              .evt-config-sub{font-size:.78rem;color:rgba(255,255,255,.4);margin:.2rem 0 0}
              .evt-config-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.5rem}
              @media(max-width:800px){.evt-config-grid{grid-template-columns:1fr}}
              .evt-col{display:flex;flex-direction:column;gap:1rem}
              .evt-section{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:14px;padding:1.1rem}
              .evt-section-title{font-size:.8rem;font-weight:700;color:rgba(255,255,255,.5);text-transform:uppercase;letter-spacing:.06em;margin-bottom:.75rem}
              .evt-label{font-size:.75rem;color:rgba(255,255,255,.45);display:block;margin-bottom:.25rem}
              .evt-hint{font-size:.72rem;color:rgba(255,255,255,.3);margin:.4rem 0 0;line-height:1.5}
              .evt-discount-preview{margin-top:.5rem;padding:.45rem .75rem;background:rgba(200,169,110,.1);border:1px solid rgba(200,169,110,.25);border-radius:8px;font-size:.82rem;font-weight:700;color:#c8a96e;display:flex;align-items:center;gap:.5rem}
              .evt-style-grid{display:flex;flex-wrap:wrap;gap:.35rem}
              .evt-style-btn{padding:.3rem .7rem;border-radius:8px;border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04);color:rgba(255,255,255,.5);font-family:'DM Sans',sans-serif;font-size:.72rem;cursor:pointer;transition:all .2s}
              .evt-style-btn.active{border-color:rgba(200,169,110,.5);background:rgba(200,169,110,.12);color:#c8a96e}
              .evt-product-grid{display:flex;flex-direction:column;gap:.35rem;max-height:300px;overflow-y:auto}
              .evt-product-btn{display:flex;align-items:center;gap:.6rem;padding:.5rem .65rem;border-radius:10px;border:1px solid rgba(255,255,255,.07);background:rgba(255,255,255,.03);cursor:pointer;transition:all .2s;font-family:'DM Sans',sans-serif;text-align:left}
              .evt-product-btn.active{border-color:rgba(200,169,110,.4);background:rgba(200,169,110,.1)}
              .evt-product-img{width:36px;height:36px;border-radius:6px;object-fit:cover;flex-shrink:0}
              .evt-product-name{flex:1;font-size:.8rem;color:#fff;font-weight:600}
              .evt-product-price{font-size:.75rem;color:var(--pri);font-weight:700;white-space:nowrap}
              .evt-toggle-wrap{display:flex;align-items:center;gap:.5rem;cursor:pointer;user-select:none}
              .evt-toggle-wrap input{display:none}
              .evt-toggle-slider{width:42px;height:24px;background:rgba(255,255,255,.12);border-radius:12px;position:relative;transition:background .2s}
              .evt-toggle-slider::after{content:'';position:absolute;width:18px;height:18px;background:#fff;border-radius:50%;top:3px;left:3px;transition:transform .2s}
              .evt-toggle-wrap input:checked+.evt-toggle-slider{background:#10b981}
              .evt-toggle-wrap input:checked+.evt-toggle-slider::after{transform:translateX(18px)}
              .evt-toggle-label{font-size:.75rem;font-weight:700;color:rgba(255,255,255,.5)}
              .evt-banner-preview-wrap{position:relative;margin-bottom:.75rem}
              .evt-banner-preview{width:100%;border-radius:10px;object-fit:cover;max-height:180px}
              .evt-banner-clear{position:absolute;top:.4rem;right:.4rem;background:rgba(0,0,0,.6);border:none;color:#fff;border-radius:50%;width:24px;height:24px;cursor:pointer;font-size:.8rem;display:flex;align-items:center;justify-content:center}
              .evt-banner-placeholder{padding:2rem;text-align:center;border:2px dashed rgba(255,255,255,.1);border-radius:10px;color:rgba(255,255,255,.25);font-size:.8rem;margin-bottom:.75rem}
              .evt-gen-box{background:rgba(167,139,250,.06);border:1px solid rgba(167,139,250,.15);border-radius:12px;padding:.85rem;display:flex;flex-direction:column;gap:.5rem}
              .evt-gen-box-title{font-size:.75rem;font-weight:700;color:#c4b5fd;text-transform:uppercase;letter-spacing:.05em}
              .evt-gen-btn{padding:.6rem 1rem;background:linear-gradient(135deg,#7c3aed,#a78bfa);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:700;cursor:pointer;transition:opacity .2s;box-shadow:0 4px 14px rgba(139,92,246,.3)}
              .evt-gen-btn:disabled{opacity:.5;cursor:wait}
              .evt-gen-hint{font-size:.68rem;color:rgba(167,139,250,.6);margin:0}
              /* Popup preview */
              .evt-popup-preview{border-radius:16px;overflow:hidden;border:1px solid rgba(255,255,255,.1)}
              .evt-popup-banner{width:100%;height:120px;object-fit:cover;display:block}
              .evt-popup-body{padding:1rem;display:flex;flex-direction:column;align-items:center;gap:.4rem;text-align:center}
              .popup-style-elegant .evt-popup-body{background:linear-gradient(135deg,#1a1a2e,#16213e)}
              .popup-style-luxury .evt-popup-body{background:linear-gradient(135deg,#1a1505,#2d2210)}
              .popup-style-playful .evt-popup-body{background:linear-gradient(135deg,#1a0530,#2d0b5a)}
              .popup-style-warm .evt-popup-body{background:linear-gradient(135deg,#1a0d05,#2d1a08)}
              .popup-style-dark .evt-popup-body{background:linear-gradient(135deg,#0a0a0a,#111)}
              .popup-style-vibrant .evt-popup-body{background:linear-gradient(135deg,#050a2d,#0a1a4d)}
              .evt-popup-emoji-row{font-size:1.8rem;line-height:1}
              .evt-popup-title{font-family:'Playfair Display',serif;color:#fff;font-size:1rem;margin:0;font-weight:900}
              .evt-popup-msg{font-size:.75rem;color:rgba(255,255,255,.55);margin:0}
              .evt-popup-discount{padding:.3rem .75rem;background:rgba(200,169,110,.15);border:1px solid rgba(200,169,110,.3);border-radius:8px;color:#c8a96e;font-size:.8rem;font-weight:800;display:flex;align-items:center;gap:.4rem}
              .evt-popup-code{background:rgba(200,169,110,.2);padding:.1rem .4rem;border-radius:5px;font-size:.7rem;letter-spacing:.05em}
              .evt-popup-cta{padding:.4rem 1rem;background:#c8a96e;border-radius:8px;color:#fff;font-size:.75rem;font-weight:700;cursor:pointer}
              .evt-config-footer{display:flex;justify-content:space-between;align-items:center;margin-top:1.5rem;padding-top:1rem;border-top:1px solid rgba(255,255,255,.07)}
              .evt-delete-btn{padding:.6rem 1.1rem;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);border-radius:10px;color:#f87171;font-family:'DM Sans',sans-serif;font-size:.82rem;cursor:pointer}
              .evt-save-btn{margin-left:auto}
            `}</style>
          </div>
        )}

                {/* ── ADMIN PANEL LANGUAGE TAB ── */}
        {activeTab === 'admin-lang' && (
          <div className="adm-content">
            <div className="adm-settings-form">

              {/* Info banner */}
              <div className="adm-lang-info-banner">
                <span className="adm-lang-info-icon">🖥️</span>
                <div>
                  <strong>{ta("adm_admin_panel_lang")}</strong>
                  <p>{ta("adm_admin_lang_desc")}</p>
                </div>
              </div>

              {/* Language picker */}
              <div className="lang-section">
                <h3 className="lang-section-title">{ta("adm_choose_lang")}</h3>
                <div className="lang-flags-grid" style={{marginBottom:'1.25rem'}}>
                  {Object.values(LANGUAGES).map(lang => (
                    <button key={lang.code}
                      className={`lang-flag-btn ${adminPanelLang === lang.code ? 'active' : ''}`}
                      onClick={() => setAdminPanelLang(lang.code)}>
                      <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                      <span>{lang.name}</span>
                      {adminPanelLang === lang.code && <span className="lang-check">✓</span>}
                    </button>
                  ))}
                </div>

                {/* Action buttons row */}
                <div className="adm-lang-actions">
                  {adminPanelLang === 'en' ? (
                    /* English selected — offer a clear/reset */
                    <button className="adm-lang-reset-btn"
                      onClick={() => { setAdminPanelTrans({}); notify(ta('adm_t_reset_en')); }}>
                      🔄 Reset All to English (clear overrides)
                    </button>
                  ) : (
                    <button className="lang-auto-btn"
                      onClick={autoTranslateAllAdmin}
                      disabled={!!adminLangAuto._all}>
                      {adminLangAuto._all
                        ? '⏳ Translating…'
                        : `✨ Auto-Translate All to ${LANGUAGES[adminPanelLang]?.name}`}
                    </button>
                  )}
                  {/* Always show clear button unless already empty */}
                  {Object.keys(adminPanelTrans).length > 0 && adminPanelLang !== 'en' && (
                    <button className="adm-lang-clear-btn"
                      onClick={() => { if(window.confirm(ta('adm_c_clear_admin_trans'))) { setAdminPanelTrans({}); notify(ta('adm_t_trans_cleared')); } }}>
                      🗑 Clear All
                    </button>
                  )}
                </div>

                {/* Live preview strip */}
                <div className="adm-lang-preview">
                  <span className="adm-lang-preview-label">Preview:</span>
                  {['adm_overview','adm_orders','adm_products','adm_customers','adm_settings','adm_language'].map(k => (
                    <span key={k} className="adm-lang-preview-chip">
                      {adminPanelTrans[k] || ADMIN_UI_DEFAULTS[k]}
                    </span>
                  ))}
                </div>
              </div>

              {/* All admin UI sections */}
              {ADMIN_UI_SECTIONS.map(section => (
                <div key={section.id} className="lang-trans-group" style={{marginBottom:'0.875rem'}}>
                  <div className="lang-trans-group-title">{section.label}</div>
                  {section.keys.map(({ key, default: defaultText }) => (
                    <div key={key} className="lang-ui-row">
                      <div className="lang-ui-original">
                        <span className="lang-ui-key">{key.replace(/_/g,' ')}</span>
                        <span className="lang-ui-default">{defaultText}</span>
                      </div>
                      <div style={{display:'flex',gap:'.4rem',flex:1}}>
                        <input
                          className="lang-trans-input"
                          placeholder={adminPanelLang === 'en' ? '(uses English default)' : defaultText}
                          value={adminPanelTrans[key] || ''}
                          onChange={e => setAdminKey(key, e.target.value)}
                          readOnly={adminPanelLang === 'en'}
                          style={adminPanelLang === 'en' ? {opacity:.45,cursor:'not-allowed'} : {}}
                        />
                        {adminPanelLang !== 'en' && (
                          <button className="lang-single-auto"
                            onClick={() => autoTranslateAdminKey(key, defaultText)}
                            disabled={!!adminLangAuto[key]}
                            title={`Auto-translate to ${LANGUAGES[adminPanelLang]?.name}`}>
                            {adminLangAuto[key] ? '…' : '✨'}
                          </button>
                        )}
                        {adminPanelTrans[key] && (
                          <button className="adm-lang-field-clear"
                            onClick={() => setAdminKey(key, '')}
                            title="Clear — revert to English default">
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <button className="adm-save-btn" onClick={saveLangSettings} disabled={savingLang}
                style={{marginTop:'1.5rem'}}>
                {savingLang ? 'Saving…' : `Save — ${LANGUAGES[adminPanelLang]?.flag} ${LANGUAGES[adminPanelLang]?.name || 'English'} Panel`}
              </button>
            </div>
          </div>
        )}


      </main>

      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        .adm-root{display:flex;min-height:100vh;background:#0a0a0a;color:#f5f0e8;font-family:'DM Sans',sans-serif}
        .adm-notif{position:fixed;top:1.5rem;right:1.5rem;z-index:9999;padding:.85rem 1.5rem;border-radius:12px;font-weight:600;font-size:.9rem;animation:slideInR .3s ease}
        .adm-notif.success{background:#064e3b;color:#6ee7b7;border:1px solid #10b981}
        .adm-notif.error{background:#7f1d1d;color:#fca5a5;border:1px solid #ef4444}
        .lang-section { margin-bottom: 2rem; }
        .lang-section-title { font-family: 'Playfair Display', serif; color: #fff; font-size: 1rem; margin-bottom: .5rem; }
        .lang-hint { font-size: .8rem; color: rgba(255,255,255,.4); margin-bottom: 1rem; }
        .lang-mode-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        .lang-mode-card { display: flex; flex-direction: column; gap: .4rem; padding: 1.25rem; background: rgba(255,255,255,.04); border: 2px solid rgba(255,255,255,.08); border-radius: 14px; cursor: pointer; text-align: left; color: rgba(255,255,255,.6); font-family: 'DM Sans', sans-serif; transition: all .2s; }
        .lang-mode-card:hover { border-color: rgba(255,255,255,.2); }
        .lang-mode-card.active { border-color: var(--pri); background: rgba(200,169,110,.08); color: #fff; }
        .lang-mode-icon { font-size: 2rem; }
        .lang-mode-card strong { color: #fff; font-size: .95rem; }
        .lang-mode-card span:last-child { font-size: .78rem; color: rgba(255,255,255,.45); line-height: 1.5; }
        .lang-flags-grid { display: flex; flex-wrap: wrap; gap: .5rem; }
        .lang-flag-btn { display: flex; flex-direction: column; align-items: center; gap: .3rem; padding: .65rem .9rem; background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.08); border-radius: 12px; cursor: pointer; color: rgba(255,255,255,.6); font-family: 'DM Sans', sans-serif; font-size: .72rem; min-width: 72px; transition: all .2s; position: relative; }
        .lang-flag-btn:hover { border-color: rgba(255,255,255,.2); color: #fff; }
        .lang-flag-btn.active { border-color: var(--pri); background: rgba(200,169,110,.12); color: #fff; }
        .lang-check { position: absolute; top: .25rem; right: .35rem; font-size: .6rem; color: var(--pri); }
        .lang-trans-controls { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.25rem; flex-wrap: wrap; }
        .lang-trans-tabs { display: flex; gap: .4rem; flex-wrap: wrap; }
        .lang-trans-tab { padding: .4rem .9rem; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: 20px; color: rgba(255,255,255,.5); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .82rem; transition: all .2s; }
        .lang-trans-tab.active { background: var(--pri); border-color: var(--pri); color: #fff; font-weight: 700; }
        .lang-trans-group { background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.07); border-radius: 14px; padding: 1rem; margin-bottom: 1rem; }
        .lang-trans-group-title { font-size: .78rem; font-weight: 700; color: rgba(255,255,255,.5); text-transform: uppercase; letter-spacing: .08em; margin-bottom: .875rem; }
        .lang-trans-row { display: grid; grid-template-columns: 1fr auto 1.2fr; align-items: center; gap: .75rem; padding: .6rem 0; border-bottom: 1px solid rgba(255,255,255,.05); }
        .lang-trans-row:last-child { border-bottom: none; }
        .lang-trans-row.prod { grid-template-columns: 1fr 1.4fr; }
        .lang-trans-original { font-size: .85rem; color: rgba(255,255,255,.7); font-weight: 600; }
        .lang-trans-original-wrap { display: flex; flex-direction: column; gap: .2rem; }
        .lang-trans-sub { font-size: .72rem; color: rgba(255,255,255,.35); }
        .lang-trans-arrow { color: rgba(255,255,255,.25); font-size: .8rem; }
        .lang-trans-fields { display: flex; flex-direction: column; gap: .4rem; }
        .lang-trans-input { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1); border-radius: 8px; padding: .55rem .75rem; color: #fff; font-family: 'DM Sans', sans-serif; font-size: .85rem; outline: none; transition: border-color .2s; width: 100%; }
        .lang-trans-input:focus { border-color: var(--pri); }
        .lang-auto-btn { padding: .45rem 1rem; background: linear-gradient(135deg,#7c3aed,#4f46e5); border: none; border-radius: 20px; color: #fff; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .8rem; font-weight: 700; white-space: nowrap; transition: opacity .2s; }
        .lang-auto-btn:hover:not(:disabled) { opacity: .85; }
        .lang-auto-btn:disabled { opacity: .5; cursor: not-allowed; }
        .lang-single-auto { flex-shrink: 0; width: 32px; height: 32px; border: 1px solid rgba(124,58,237,.4); border-radius: 8px; background: rgba(124,58,237,.12); color: #a78bfa; cursor: pointer; font-size: .9rem; display: flex; align-items: center; justify-content: center; transition: all .2s; }
        .lang-single-auto:hover:not(:disabled) { background: rgba(124,58,237,.25); border-color: rgba(124,58,237,.7); }
        .lang-single-auto:disabled { opacity: .5; cursor: not-allowed; }
        .lang-method-note { font-size: .78rem; color: rgba(255,255,255,.4); background: rgba(124,58,237,.08); border: 1px solid rgba(124,58,237,.2); border-radius: 10px; padding: .6rem 1rem; margin-bottom: 1.25rem; line-height: 1.5; }
        .lang-method-note strong { color: rgba(167,139,250,.9); }
        .lang-ui-row { display: grid; grid-template-columns: 200px 1fr; align-items: center; gap: .75rem; padding: .5rem 0; border-bottom: 1px solid rgba(255,255,255,.05); }
        .lang-ui-row:last-child { border-bottom: none; }
        .lang-ui-original { display: flex; flex-direction: column; gap: .15rem; }
        .lang-ui-key { font-size: .65rem; color: rgba(255,255,255,.3); text-transform: uppercase; letter-spacing: .06em; }
        .lang-ui-default { font-size: .82rem; color: rgba(255,255,255,.65); font-weight: 500; }
        @media (max-width: 640px) { .lang-ui-row { grid-template-columns: 1fr; } }

        /* ── Admin-lang tab specific ── */
        .adm-lang-info-banner { display:flex; align-items:flex-start; gap:1rem; background:rgba(99,102,241,.08); border:1px solid rgba(99,102,241,.2); border-radius:14px; padding:1.25rem 1.5rem; margin-bottom:1.75rem; }
        .adm-lang-info-icon { font-size:2rem; flex-shrink:0; }
        .adm-lang-info-banner strong { display:block; color:#fff; font-size:.95rem; margin-bottom:.25rem; }
        .adm-lang-info-banner p { font-size:.8rem; color:rgba(255,255,255,.4); line-height:1.6; margin:0; }
        .adm-lang-actions { display:flex; gap:.75rem; flex-wrap:wrap; margin-bottom:1.25rem; }
        .adm-lang-reset-btn { padding:.55rem 1.25rem; background:rgba(16,185,129,.12); border:1px solid rgba(16,185,129,.3); border-radius:20px; color:#6ee7b7; cursor:pointer; font-family:'DM Sans',sans-serif; font-size:.82rem; font-weight:700; transition:all .2s; }
        .adm-lang-reset-btn:hover { background:rgba(16,185,129,.22); border-color:rgba(16,185,129,.5); }
        .adm-lang-clear-btn { padding:.55rem 1.1rem; background:rgba(239,68,68,.08); border:1px solid rgba(239,68,68,.2); border-radius:20px; color:#fca5a5; cursor:pointer; font-family:'DM Sans',sans-serif; font-size:.82rem; font-weight:600; transition:all .2s; }
        .adm-lang-clear-btn:hover { background:rgba(239,68,68,.16); border-color:rgba(239,68,68,.4); }
        .adm-lang-preview { display:flex; align-items:center; gap:.5rem; flex-wrap:wrap; background:rgba(255,255,255,.03); border:1px solid rgba(255,255,255,.07); border-radius:12px; padding:.75rem 1rem; margin-bottom:.25rem; }
        .adm-lang-preview-label { font-size:.7rem; color:rgba(255,255,255,.3); text-transform:uppercase; letter-spacing:.08em; margin-right:.25rem; }
        .adm-lang-preview-chip { padding:.25rem .6rem; background:rgba(99,102,241,.15); border:1px solid rgba(99,102,241,.25); border-radius:8px; color:#a5b4fc; font-size:.78rem; font-weight:600; }
        .adm-lang-field-clear { flex-shrink:0; width:28px; height:32px; border:1px solid rgba(255,255,255,.1); border-radius:6px; background:rgba(255,255,255,.04); color:rgba(255,255,255,.35); cursor:pointer; font-size:1.1rem; display:flex; align-items:center; justify-content:center; transition:all .2s; line-height:1; }
        .adm-lang-field-clear:hover { background:rgba(239,68,68,.12); border-color:rgba(239,68,68,.3); color:#fca5a5; }
        @keyframes slideInR{from{transform:translateX(30px);opacity:0}to{transform:translateX(0);opacity:1}}

        /* sidebar */
        .adm-sidebar{width:240px;background:#111;border-right:1px solid rgba(255,255,255,.06);display:flex;flex-direction:column;position:fixed;top:0;left:0;height:100vh;z-index:40}
        /* mobile hamburger — hidden on desktop, revealed by the responsive rule below */
        .adm-hamburger{display:none;align-items:center;gap:.6rem;position:fixed;top:0;left:0;right:0;height:52px;padding:0 1rem;background:#111;border:none;border-bottom:1px solid rgba(255,255,255,.08);color:#fff;font-family:'DM Sans',sans-serif;font-size:1.05rem;font-weight:700;cursor:pointer;z-index:50}
        .adm-hamburger-name{font-size:.9rem;font-weight:600;color:rgba(255,255,255,.7)}
        .adm-nav-backdrop{display:none}
        .adm-brand{display:flex;align-items:center;gap:.75rem;padding:1.5rem;border-bottom:1px solid rgba(255,255,255,.06)}
        .adm-brand-logo{font-size:2rem}
        .adm-brand-name{font-weight:700;color:#fff;font-size:.95rem}
        .adm-brand-role{font-size:.65rem;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.1em}
        .adm-nav{flex:1;padding:1rem;display:flex;flex-direction:column;gap:.2rem}
        .adm-nav-btn{display:flex;align-items:center;gap:.75rem;padding:.75rem 1rem;border:none;background:none;color:rgba(255,255,255,.5);cursor:pointer;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:.9rem;text-align:left;transition:all .2s}
        .adm-nav-btn:hover{background:rgba(255,255,255,.06);color:#fff}
        .adm-nav-btn.active{background:var(--pri);color:#fff;font-weight:600}
        .adm-sidebar-footer{padding:1rem;border-top:1px solid rgba(255,255,255,.06);display:flex;flex-direction:column;gap:.5rem}
        .adm-sidebar-link{padding:.65rem 1rem;border-radius:10px;border:1px solid rgba(255,255,255,.1);background:none;color:rgba(255,255,255,.6);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.85rem;text-align:left;transition:all .2s}
        .adm-sidebar-link:hover{background:rgba(255,255,255,.06);color:#fff}
        .adm-sidebar-link.danger:hover{background:rgba(239,68,68,.1);border-color:rgba(239,68,68,.3);color:#f87171}

        /* main */
        .adm-main{margin-left:240px;flex:1;min-width:0;display:flex;flex-direction:column;min-height:100vh}
        .adm-topbar{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 2rem;border-bottom:1px solid rgba(255,255,255,.06);background:rgba(17,17,17,.9);backdrop-filter:blur(10px);position:sticky;top:0;z-index:30}
        .adm-topbar-title{font-family:'Playfair Display',serif;font-size:1.4rem;color:#fff}
        .adm-topbar-right{display:flex;align-items:center;gap:.85rem;flex-wrap:wrap}
        /* Branch switcher bar */
        .adm-branch-bar{display:flex;align-items:center;gap:.75rem;flex-wrap:wrap;padding:.75rem 2rem;border-bottom:1px solid rgba(255,255,255,.06);background:rgba(255,255,255,.02)}
        .adm-branch-bar-label{font-size:.8rem;color:rgba(255,255,255,.5);white-space:nowrap}
        .adm-branch-tabs{display:flex;gap:.4rem;flex-wrap:wrap}
        .adm-branch-tab{display:inline-flex;align-items:center;gap:.35rem;padding:.45rem .9rem;border-radius:9px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.05);color:rgba(255,255,255,.7);font-family:'DM Sans',sans-serif;font-size:.82rem;cursor:pointer;transition:all .2s;white-space:nowrap}
        .adm-branch-tab:hover{background:rgba(255,255,255,.1);color:#fff}
        .adm-branch-tab.active{background:var(--pri);border-color:var(--pri);color:#fff;font-weight:700}
        .adm-branch-sync-btn{display:inline-flex;align-items:center;gap:.35rem;padding:.45rem .9rem;border-radius:9px;border:1px solid rgba(200,169,110,.4);background:rgba(200,169,110,.12);color:var(--pri);font-family:'DM Sans',sans-serif;font-size:.8rem;font-weight:700;cursor:pointer;transition:all .2s;white-space:nowrap}
        .adm-branch-sync-btn:hover{background:rgba(200,169,110,.22)}
        .adm-branch-agg-toggle{display:inline-flex;align-items:center;gap:.4rem;font-size:.78rem;color:rgba(255,255,255,.7);cursor:pointer;white-space:nowrap}
        .adm-branch-agg-toggle input{accent-color:var(--pri);width:15px;height:15px;cursor:pointer}
        .adm-branch-bar-note{font-size:.72rem;color:rgba(255,255,255,.35);margin-inline-start:auto}
        .adm-tb-group{display:flex;align-items:center;gap:.3rem;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);border-radius:9px;padding:.2rem .45rem;position:relative}
        .adm-tb-ico{font-size:.85rem;opacity:.7}
        .adm-tb-select{background:none;border:none;color:#fff;font-family:'DM Sans',sans-serif;font-size:.8rem;cursor:pointer;outline:none;max-width:140px}
        .adm-tb-select option{background:#1a1a1a;color:#fff}
        .adm-tb-spin{font-size:.8rem}
        .adm-topbar-user{font-size:.82rem;color:rgba(255,255,255,.4)}
        .adm-content{padding:2rem;flex:1}
        .adm-onb-card{background:linear-gradient(135deg,rgba(200,169,110,.08),rgba(255,255,255,.03));border:1px solid rgba(200,169,110,.25);border-radius:16px;padding:1.4rem;margin-bottom:1.5rem}
        .adm-onb-head{display:flex;align-items:center;justify-content:space-between;gap:.75rem}
        .adm-onb-title{font-weight:800;color:#fff;font-size:.95rem}
        .adm-onb-count{font-size:.8rem;font-weight:700;color:var(--pri)}
        .adm-onb-sub{font-size:.78rem;color:rgba(255,255,255,.45);margin:.3rem 0 .8rem}
        .adm-onb-bar{height:6px;border-radius:3px;background:rgba(255,255,255,.08);overflow:hidden;margin-bottom:1rem}
        .adm-onb-fill{height:100%;background:var(--pri);border-radius:3px;transition:width .4s ease}
        .adm-onb-steps{display:flex;flex-direction:column;gap:.35rem}
        .adm-onb-step{display:flex;align-items:center;gap:.6rem;padding:.55rem .7rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;color:rgba(255,255,255,.75);font-family:'DM Sans',sans-serif;font-size:.85rem;cursor:pointer;text-align:start;transition:all .2s}
        .adm-onb-step:hover{background:rgba(255,255,255,.07)}
        .adm-onb-step.done{opacity:.55;text-decoration:line-through}

        /* tab bar */
        .adm-tab-bar{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.5rem}
        .adm-count{color:rgba(255,255,255,.45);font-size:.875rem}
        .adm-add-btn{padding:.65rem 1.4rem;background:var(--pri);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.875rem;font-weight:700;cursor:pointer;transition:opacity .2s}
        .adm-add-btn:hover{opacity:.85}
        /* Import button — must be styled on first paint, not only after the panel
           opens (its rules used to live inside the conditionally-rendered panel). */
        .adm-import-btn{padding:.5rem 1rem;background:rgba(167,139,250,.1);border:1px solid rgba(167,139,250,.25);border-radius:10px;color:#c4b5fd;font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:600;cursor:pointer;transition:all .2s}
        .adm-import-btn:hover{background:rgba(167,139,250,.18);border-color:rgba(167,139,250,.4)}
        .adm-refresh-btn{padding:.65rem 1.2rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:10px;color:rgba(255,255,255,.7);font-family:'DM Sans',sans-serif;font-size:.875rem;cursor:pointer;transition:all .2s}
        .adm-refresh-btn:hover{background:rgba(255,255,255,.1);color:#fff}
        .adm-empty{text-align:center;padding:4rem 2rem;color:rgba(255,255,255,.3);font-size:.95rem}
        .adm-empty button{background:none;border:none;color:var(--pri);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.95rem}

        /* stats */
        .adm-stats-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:1rem;margin-bottom:2rem}
        .adm-stat-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:1.25rem}
        .adm-stat-icon{font-size:1.4rem;margin-bottom:.6rem}
        .adm-stat-val{font-size:1.75rem;font-weight:700;color:#fff;margin-bottom:.2rem;overflow-wrap:anywhere;font-variant-numeric:tabular-nums}
        .adm-stat-label{font-size:.72rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.05em}
        .adm-ov-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.5rem}
        .adm-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:1.5rem}
        .adm-card h3{font-family:'Playfair Display',serif;color:#fff;margin-bottom:1rem;font-size:1.05rem}
        .adm-info-row{display:flex;justify-content:space-between;padding:.45rem 0;border-bottom:1px solid rgba(255,255,255,.05);font-size:.84rem}
        .adm-info-row span:first-child{color:rgba(255,255,255,.4)}
        .adm-info-row span:last-child{color:rgba(255,255,255,.8);text-align:right;max-width:60%}
        .adm-quick-btn{display:block;width:100%;padding:.7rem 1rem;margin-bottom:.5rem;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);border-radius:10px;color:rgba(255,255,255,.75);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.875rem;text-align:left;transition:all .2s}
        .adm-quick-btn:hover{background:var(--pri);color:#fff;border-color:var(--pri)}

        /* product rows */
        .adm-list{display:flex;flex-direction:column;gap:.75rem}
        .adm-product-row{display:flex;align-items:center;gap:1rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.07);border-radius:14px;padding:.875rem;transition:border-color .2s}
        .adm-product-row:hover{border-color:rgba(255,255,255,.14)}
        .adm-product-img-wrap{width:56px;height:56px;border-radius:10px;overflow:hidden;flex-shrink:0;background:rgba(255,255,255,.06)}
        .adm-product-img{width:100%;height:100%;object-fit:cover}
        .adm-product-img-fallback{width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:1.5rem}
        .adm-product-info{flex:1;min-width:0}
        .adm-product-name{font-weight:600;color:#fff;font-size:.9rem;margin-bottom:.2rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .adm-product-meta{font-size:.75rem;color:rgba(255,255,255,.4);margin-bottom:.25rem}
        .adm-product-tags{display:flex;flex-wrap:wrap;gap:.3rem}
        .adm-tag{padding:.15rem .5rem;background:rgba(255,255,255,.08);border-radius:8px;font-size:.62rem;color:rgba(255,255,255,.6);font-weight:600}
        /* Rial/Toman prices run to many digits — cap the column, use tabular
           figures, and let a very long amount wrap instead of overflowing. */
        .adm-product-price{font-weight:700;color:var(--pri);font-size:.95rem;min-width:60px;max-width:150px;text-align:right;font-variant-numeric:tabular-nums;overflow-wrap:anywhere;flex-shrink:0}
        .adm-avail-badge{padding:.3rem .8rem;border-radius:12px;border:none;cursor:pointer;font-size:.72rem;font-weight:700;white-space:nowrap;transition:all .2s}
        .adm-avail-badge.on{background:rgba(16,185,129,.15);color:#6ee7b7}
        .adm-avail-badge.off{background:rgba(239,68,68,.15);color:#fca5a5}
        .adm-row-actions{display:flex;gap:.4rem}
        .adm-icon-btn{width:34px;height:34px;border:1px solid rgba(255,255,255,.1);border-radius:8px;background:rgba(255,255,255,.05);color:rgba(255,255,255,.7);cursor:pointer;font-size:.85rem;display:flex;align-items:center;justify-content:center;transition:all .2s}
        .adm-icon-btn:hover{background:rgba(255,255,255,.12)}
        .adm-icon-btn.danger:hover{background:rgba(239,68,68,.15);border-color:rgba(239,68,68,.3);color:#f87171}

        /* category cards */
        .adm-cat-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:1rem}
        .adm-cat-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:1.25rem}
        .adm-cat-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:.75rem}
        .adm-cat-icon{font-size:2rem}
        .adm-cat-row-actions{display:flex;gap:.35rem}
        .adm-cat-name{font-weight:700;color:#fff;font-size:.95rem;margin-bottom:.2rem}
        .adm-cat-slug{font-size:.7rem;color:rgba(255,255,255,.3);font-family:monospace;margin-bottom:.5rem}
        .adm-cat-desc{font-size:.78rem;color:rgba(255,255,255,.45);margin-bottom:.6rem;line-height:1.5}
        .adm-cat-count{font-size:.78rem;color:var(--pri);font-weight:600;margin-bottom:.6rem}
        .adm-cat-subs{display:flex;flex-wrap:wrap;gap:.3rem}
        .adm-sub-badge{background:rgba(255,255,255,.07);border-radius:8px;padding:.15rem .5rem;font-size:.65rem;color:rgba(255,255,255,.5)}

        /* orders */
        .adm-orders-list{display:flex;flex-direction:column;gap:1rem}
        .adm-order-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;overflow:hidden}
        .adm-order-header{display:flex;align-items:center;gap:1rem;padding:.875rem 1.25rem;border-bottom:1px solid rgba(255,255,255,.06);flex-wrap:wrap}
        .adm-order-num{font-weight:700;color:var(--pri);font-size:.875rem}
        .adm-order-time{font-size:.75rem;color:rgba(255,255,255,.35);flex:1}
        .adm-status-select{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:8px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.8rem;padding:.4rem .75rem;cursor:pointer;outline:none}
        .adm-status-select.status-pending{background:rgba(234,179,8,.15);color:#fde047}
        .adm-status-select.status-confirmed{background:rgba(99,102,241,.15);color:#a5b4fc}
        .adm-status-select.status-preparing{background:rgba(249,115,22,.15);color:#fdba74}
        .adm-status-select.status-ready{background:rgba(16,185,129,.15);color:#6ee7b7}
        .adm-status-select.status-delivered{background:rgba(100,116,139,.15);color:#94a3b8}
        .adm-status-select.status-cancelled{background:rgba(239,68,68,.15);color:#fca5a5}
        .adm-order-body{display:grid;grid-template-columns:1fr auto;gap:1.5rem;padding:1.25rem}
        .adm-order-items{display:flex;flex-direction:column;gap:.4rem}
        .adm-order-item{display:flex;justify-content:space-between;font-size:.84rem;color:rgba(255,255,255,.7)}
        .adm-order-meta{font-size:.8rem;color:rgba(255,255,255,.5);display:flex;flex-direction:column;gap:.3rem;min-width:180px}
        .adm-cust-body{padding:.875rem 1.25rem 1.1rem;min-width:0}
        .adm-cust-body .adm-order-meta{min-width:0}
        .adm-cust-body .adm-order-meta div{overflow-wrap:anywhere}
        .adm-order-total{color:#fff;font-size:.9rem;margin-top:.5rem}

        /* settings */
        .adm-settings-form{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:16px;padding:2rem}
        .adm-settings-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1.5rem}
        .adm-field{display:flex;flex-direction:column;gap:.4rem}
        .adm-field.full{grid-column:1/-1}
        .adm-field label{font-size:.72rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.08em}
        .adm-input{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:.75rem 1rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;resize:vertical;transition:border-color .2s;width:100%}
        .adm-input:focus{border-color:var(--pri)}
        .adm-color-row{display:flex;gap:.5rem;align-items:center}
        .adm-color-row input[type=color]{width:44px;height:40px;border:none;border-radius:8px;cursor:pointer;background:none;padding:0;flex-shrink:0}
        .adm-img-preview{margin-top:.5rem;width:100%;max-height:140px;object-fit:cover;border-radius:10px;border:1px solid rgba(255,255,255,.1)}
        .adm-img-preview.small{max-height:60px;width:auto}
        .adm-save-btn{padding:.9rem 2rem;background:var(--pri);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:700;cursor:pointer;transition:opacity .2s}
        .adm-save-btn:hover{opacity:.85}
        /* ── Menu appearance controls ── */
        .adm-appearance{margin-top:1.75rem;padding-top:1.5rem;border-top:1px solid rgba(255,255,255,.08)}
        .adm-appearance-title{font-size:1.05rem;font-weight:800;color:#fff}
        .adm-appearance-sub{font-size:.82rem;color:rgba(255,255,255,.45);margin:.25rem 0 1rem}
        .adm-appearance-label{display:block;font-size:.78rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:rgba(255,255,255,.5);margin:1.25rem 0 .6rem}
        .adm-theme-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:.75rem}
        .adm-theme-card{display:flex;flex-direction:column;gap:.35rem;text-align:left;padding:.85rem;border-radius:14px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.03);cursor:pointer;transition:all .2s;color:#fff;font-family:'DM Sans',sans-serif}
        .adm-theme-card:hover{border-color:rgba(255,255,255,.3)}
        .adm-theme-card.active{border-color:var(--pri);background:rgba(255,255,255,.06);box-shadow:0 0 0 1px var(--pri)}
        .adm-theme-card strong{font-size:.9rem}
        .adm-theme-desc{font-size:.72rem;color:rgba(255,255,255,.4);line-height:1.4}
        .adm-theme-thumb{display:flex;gap:4px;height:46px;padding:7px;border-radius:9px;background:#111;border:1px solid rgba(255,255,255,.08);overflow:hidden}
        .adm-theme-thumb i{flex:1;border-radius:4px;background:linear-gradient(135deg,rgba(255,255,255,.18),rgba(255,255,255,.06))}
        .thumb-classic i{align-self:stretch}
        .thumb-modern{background:#f3efe9}
        .thumb-modern i{background:linear-gradient(135deg,#fff,#e4ddd2);box-shadow:0 2px 6px rgba(0,0,0,.12)}
        .thumb-elegant{flex-direction:column;gap:5px;justify-content:center}
        .thumb-elegant i{flex:none;height:3px;border-radius:2px;background:rgba(255,255,255,.35)}
        .thumb-elegant i:nth-child(2){width:70%}
        .adm-seg{display:inline-flex;gap:.4rem;background:rgba(255,255,255,.05);padding:.3rem;border-radius:12px;flex-wrap:wrap}
        .adm-seg-btn{padding:.5rem 1rem;border-radius:9px;border:1px solid transparent;background:none;color:rgba(255,255,255,.6);font-family:'DM Sans',sans-serif;font-size:.85rem;font-weight:600;cursor:pointer;transition:all .2s}
        .adm-seg-btn:hover{color:#fff}
        .adm-seg-btn.active{background:var(--pri);color:#fff}
        .adm-palette{display:flex;gap:1rem;flex-wrap:wrap;align-items:flex-end}
        .adm-palette-item{display:flex;gap:.5rem;align-items:center}
        .adm-palette-item input[type=color]{width:44px;height:44px;border:none;border-radius:10px;cursor:pointer;background:none;padding:0;flex-shrink:0}
        .adm-palette-meta{display:flex;flex-direction:column;gap:.2rem}
        .adm-palette-meta span{font-size:.72rem;color:rgba(255,255,255,.5)}
        .adm-palette-hex{width:96px;padding:.35rem .5rem;font-size:.78rem}
        .adm-palette-reset{align-self:center;background:none;border:1px solid rgba(255,255,255,.15);border-radius:9px;color:rgba(255,255,255,.6);padding:.5rem .85rem;font-size:.8rem;cursor:pointer;font-family:'DM Sans',sans-serif}
        .adm-palette-reset:hover{color:#fff;border-color:rgba(255,255,255,.35)}
        .adm-menu-preview{max-height:540px;overflow:auto;border-radius:16px;border:1px solid rgba(255,255,255,.12)}
        .adm-menu-preview .menu-page{min-height:0;padding-bottom:1.5rem}
        .adm-menu-preview .menu-nav{position:static}
        .adm-menu-preview .search-input{pointer-events:none}
        /* ── Language tab extras ── */
        .lang-tab-pct{margin-left:.35rem;padding:.1rem .4rem;border-radius:8px;font-size:.65rem;font-weight:700}
        .lang-seo-impact{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:12px;padding:1rem 1.2rem;margin-top:1rem}
        .lang-seo-impact-title{font-size:.78rem;font-weight:700;color:rgba(255,255,255,.45);text-transform:uppercase;letter-spacing:.05em;margin-bottom:.75rem}
        .lang-seo-impact-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:.75rem}
        .lang-seo-impact-card{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:.85rem;display:flex;flex-direction:column;gap:.4rem}
        .lang-seo-impact-head{display:flex;justify-content:space-between;align-items:center;font-size:.85rem;font-weight:600}
        .lang-default-badge{font-size:.6rem;background:rgba(200,169,110,.15);color:var(--pri);border:1px solid rgba(200,169,110,.2);border-radius:6px;padding:.1rem .35rem;font-weight:700}
        .lang-completeness-bar-wrap{height:5px;background:rgba(255,255,255,.07);border-radius:3px;overflow:hidden}
        .lang-completeness-bar{height:100%;border-radius:3px;transition:width .4s ease}
        .lang-completeness-label{font-size:.7rem;color:rgba(255,255,255,.4)}
        .lang-preview-link{font-size:.72rem;color:var(--pri);text-decoration:none;opacity:.8;margin-top:.15rem}
        .lang-preview-link:hover{opacity:1;text-decoration:underline}
        .lang-io-btn{padding:.45rem 1rem;border-radius:8px;border:1px solid var(--border);background:rgba(255,255,255,.04);color:rgba(255,255,255,.65);font-family:'DM Sans',sans-serif;font-size:.78rem;cursor:pointer;transition:all .2s;display:inline-flex;align-items:center;gap:.35rem}
        .lang-io-btn:hover{background:rgba(255,255,255,.09);color:#fff}
        .lang-seo-group{background:rgba(200,169,110,.04)!important;border-color:rgba(200,169,110,.15)!important}
        .lang-seo-info{display:flex;gap:.6rem;align-items:flex-start;background:rgba(255,255,255,.04);border-radius:8px;padding:.75rem;margin-bottom:.75rem;font-size:.82rem;color:rgba(255,255,255,.55);line-height:1.5}
        .lang-seo-field{display:flex;flex-direction:column;gap:.3rem;margin-bottom:.75rem}
        .lang-seo-field-header{display:flex;justify-content:space-between;align-items:baseline}
        .lang-seo-field label{font-size:.8rem;font-weight:600;color:rgba(255,255,255,.7)}
        .lang-seo-hint{font-size:.72rem;color:rgba(255,255,255,.35);margin-top:.1rem}
        .lang-seo-counter{font-size:.72rem;color:rgba(255,255,255,.35)}
        .lang-seo-counter.over{color:#f87171;font-weight:700}
        .lang-trans-input[rows]{resize:vertical;min-height:70px}
        .lang-serp-preview{background:#fff;border-radius:10px;padding:1rem 1.2rem;margin-top:.75rem;max-width:600px}
        .lang-serp-title{color:#1a0dab;font-size:1.05rem;font-weight:400;line-height:1.35;margin-bottom:.2rem;font-family:Arial,sans-serif;word-break:break-word}
        .lang-serp-url{color:#006621;font-size:.78rem;margin-bottom:.25rem;font-family:Arial,sans-serif}
        .lang-serp-desc{color:#545454;font-size:.83rem;line-height:1.5;font-family:Arial,sans-serif}

        /* ── Currency Picker ── */
        .cur-picker-wrap{display:flex;flex-direction:column;gap:.75rem}
        .cur-selected{display:flex;align-items:center;gap:.65rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:.75rem 1rem}
        .cur-sel-flag{font-size:1.4rem;line-height:1}
        .cur-sel-symbol{font-size:1.2rem;font-weight:800;color:var(--pri);min-width:28px}
        .cur-sel-name{font-size:.9rem;font-weight:600;flex:1}
        .cur-sel-code{font-size:.75rem;color:rgba(255,255,255,.35);font-weight:700;letter-spacing:.05em;background:rgba(255,255,255,.06);padding:.2rem .5rem;border-radius:6px}
        .cur-search{margin-bottom:0}
        .cur-grid{max-height:300px;overflow-y:auto;display:flex;flex-direction:column;gap:.5rem;padding-right:.25rem;scrollbar-width:thin}
        .cur-region-group{}
        .cur-region-label{font-size:.65rem;font-weight:700;color:rgba(255,255,255,.3);text-transform:uppercase;letter-spacing:.08em;padding:.25rem 0;border-bottom:1px solid rgba(255,255,255,.05);margin-bottom:.35rem}
        .cur-region-items{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:.35rem;margin-bottom:.5rem}
        .cur-btn{display:flex;flex-direction:column;align-items:center;gap:.15rem;padding:.5rem .35rem;border-radius:8px;border:1px solid rgba(255,255,255,.07);background:rgba(255,255,255,.03);cursor:pointer;transition:all .15s;text-align:center;font-family:'DM Sans',sans-serif}
        .cur-btn:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.15)}
        .cur-btn.active{background:rgba(200,169,110,.12);border-color:rgba(200,169,110,.35);box-shadow:0 0 0 1px rgba(200,169,110,.2)}
        .cur-btn-flag{font-size:1.1rem;line-height:1}
        .cur-btn-code{font-size:.68rem;font-weight:700;color:rgba(255,255,255,.7);letter-spacing:.03em}
        .cur-btn-symbol{font-size:.78rem;color:var(--pri);font-weight:700}

        /* ── Bulk Price Panel ── */
        .bulk-price-panel{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:12px;padding:1rem 1.2rem;margin-bottom:1.25rem;display:flex;flex-direction:column;gap:.85rem}
        .bulk-price-label{display:flex;align-items:center;gap:.5rem;font-size:.85rem;color:rgba(255,255,255,.65);flex-wrap:wrap}
        .bulk-price-label strong{color:#fff;font-size:.9rem}
        .bulk-price-sub{color:rgba(255,255,255,.35);font-size:.78rem;flex-basis:100%;padding-left:1.5rem;margin-top:-.35rem}
        .bulk-price-controls{display:flex;align-items:flex-start;gap:.75rem;flex-wrap:wrap}
        .bulk-dir-toggle{display:flex;border:1px solid rgba(255,255,255,.1);border-radius:8px;overflow:hidden}
        .bulk-dir-btn{padding:.5rem .9rem;background:none;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.8rem;font-weight:600;color:rgba(255,255,255,.45);transition:all .2s;white-space:nowrap}
        .bulk-dir-btn.active{color:#fff}
        .bulk-dir-btn.active.increase{background:rgba(16,185,129,.18);color:#6ee7b7}
        .bulk-dir-btn.active.decrease{background:rgba(239,68,68,.18);color:#fca5a5}
        .bulk-pct-wrap{display:flex;align-items:center;gap:.3rem}
        .bulk-pct-input{width:90px!important;text-align:center}
        .bulk-pct-sign{font-size:1rem;font-weight:700;color:rgba(255,255,255,.4)}
        .bulk-preview{background:rgba(0,0,0,.25);border-radius:8px;padding:.6rem .85rem;display:flex;flex-direction:column;gap:.3rem;min-width:220px}
        .bulk-preview-row{display:flex;align-items:center;gap:.4rem;font-size:.78rem}
        .bulk-preview-name{flex:1;color:rgba(255,255,255,.5);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:110px}
        .bulk-preview-old{color:rgba(255,255,255,.3);text-decoration:line-through;font-size:.72rem}
        .bulk-preview-arrow{color:rgba(255,255,255,.2);font-size:.7rem}
        .bulk-preview-new{font-weight:700}
        .bulk-preview-new.increase{color:#6ee7b7}
        .bulk-preview-new.decrease{color:#fca5a5}
        .bulk-preview-new.invalid{color:#f87171}
        .bulk-preview-more{font-size:.7rem;color:rgba(255,255,255,.3);margin-top:.1rem}
        .bulk-apply-btn{white-space:nowrap;transition:background .3s!important}

        @media(max-width:768px){
          .adm-hamburger{display:flex}
          .adm-sidebar{transform:translateX(-105%);transition:transform .25s ease;box-shadow:0 0 40px rgba(0,0,0,.5);z-index:60}
          .adm-sidebar.open{transform:translateX(0)}
          .adm-nav-backdrop{display:block;position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:55}
          .adm-main{margin-left:0;padding-top:52px}
          .adm-settings-grid{grid-template-columns:1fr}
          .adm-ov-grid{grid-template-columns:1fr}
        }
        /* ── RTL (Persian/Arabic/Hebrew admin panel) ─────────────────────────
           The fixed sidebar moves to the right and the drawer slides in from
           the right; text alignment follows dir automatically. Phone numbers
           and URLs stay LTR — see the input rule below. */
        .adm-root[dir="rtl"] .adm-sidebar{left:auto;right:0;border-right:none;border-left:1px solid rgba(255,255,255,.06)}
        .adm-root[dir="rtl"] .adm-main{margin-left:0;margin-right:240px}
        .adm-root[dir="rtl"] .adm-nav-btn{text-align:right}
        .adm-root[dir="rtl"] .adm-sidebar-link{text-align:right}
        .adm-root[dir="rtl"] .adm-product-price{text-align:left}
        /* Phone numbers, URLs and codes are always written LTR, even in RTL UIs */
        .adm-root input[type="tel"], .adm-root .ltr-field{direction:ltr;text-align:left}
        @media(max-width:768px){
          .adm-root[dir="rtl"] .adm-sidebar{transform:translateX(105%)}
          .adm-root[dir="rtl"] .adm-sidebar.open{transform:translateX(0)}
          .adm-root[dir="rtl"] .adm-main{margin-right:0}
        }
        @media(max-width:600px){
          /* tighter gutters so wide content stops overflowing the viewport */
          .adm-content{padding:1rem}
          .adm-branch-bar{padding:.75rem 1rem}
          .adm-branch-bar-note{margin-inline-start:0;flex-basis:100%}
          /* topbar: title + compact icon-only controls (iPhone-style globe), one row */
          .adm-topbar{padding:1rem;gap:.5rem}
          .adm-topbar-title{font-size:1.15rem;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
          .adm-topbar-right{gap:.5rem;flex-wrap:nowrap}
          .adm-topbar-user{display:none}                 /* admin email shown in the sidebar already */
          .adm-tb-group{width:38px;height:38px;justify-content:center;padding:0;border-radius:50%;flex-shrink:0}
          .adm-tb-ico{font-size:1.1rem;opacity:1}
          .adm-tb-select{position:absolute;inset:0;width:100%;height:100%;max-width:none;opacity:0;cursor:pointer}
          /* tab header: title on its own line, action buttons share the next row */
          .adm-tab-bar{flex-wrap:wrap;gap:.6rem}
          .adm-count{flex-basis:100%}
          .adm-import-btn,.adm-add-btn{flex:1 1 auto;text-align:center}
          /* product rows become a tidy 2-row card instead of one overflowing line */
          .adm-product-row{display:grid;grid-template-columns:auto 1fr auto;grid-template-areas:"img info price" "img badge actions";column-gap:.75rem;row-gap:.5rem;align-items:center}
          .adm-product-img-wrap{grid-area:img;align-self:start}
          .adm-product-info{grid-area:info;min-width:0}
          .adm-product-price{grid-area:price;min-width:0;text-align:right}
          .adm-avail-badge{grid-area:badge;justify-self:start;white-space:nowrap}
          .adm-row-actions{grid-area:actions;justify-self:end}
          .adm-cat-grid{grid-template-columns:1fr}
          .adm-stats-grid{grid-template-columns:1fr 1fr}
          .adm-stat-val{font-size:1.3rem;overflow-wrap:anywhere}   /* big revenue number wraps instead of overflowing */
          .adm-order-body{grid-template-columns:1fr}
        }
      `}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// PRODUCT MODAL
// ══════════════════════════════════════════════════════
// "Make all branches identical" — confirmation popup with merge / copy-from-master.
function BranchSyncModal({ restaurant, differ, busy, ta, onApply, onClose }) {
  const sources = (restaurant.branches || []).map(b => ({ id: String(b._id), name: b.name }));
  const [mode, setMode] = useState(differ ? 'merge' : 'overwrite');
  const [masterId, setMasterId] = useState(sources[0]?.id || 'main');
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <ModalStyles />
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>🔗 {ta('adm_branch_sync')}</h2>
          <button className="modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '.9rem', color: differ ? '#fcd34d' : 'rgba(255,255,255,.6)', marginBottom: '1rem' }}>
            {differ ? '⚠️ ' : ''}{differ ? ta('adm_sync_differ') : ta('adm_sync_same')}
          </p>
          {[['merge', ta('adm_sync_merge'), ta('adm_sync_merge_desc')],
            ['overwrite', ta('adm_sync_copy'), ta('adm_sync_copy_desc')]].map(([val, title, desc]) => (
            <label key={val}
              onClick={() => setMode(val)}
              style={{ display: 'flex', gap: '.7rem', alignItems: 'flex-start', padding: '.85rem', marginBottom: '.6rem', borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${mode === val ? 'var(--pri)' : 'rgba(255,255,255,.14)'}`,
                background: mode === val ? 'rgba(200,169,110,.12)' : 'rgba(255,255,255,.03)' }}>
              <input type="radio" checked={mode === val} readOnly style={{ marginTop: 3 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                <strong style={{ color: '#fff', fontSize: '.9rem' }}>{title}</strong>
                <span style={{ color: 'rgba(255,255,255,.5)', fontSize: '.78rem', lineHeight: 1.5 }}>{desc}</span>
              </div>
            </label>
          ))}
          {mode === 'overwrite' && (
            <div className="mf full" style={{ marginTop: '.5rem' }}>
              <label>{ta('adm_sync_source')}</label>
              <select className="mi" value={masterId} onChange={e => setMasterId(e.target.value)}>
                {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose} disabled={busy}>{ta('adm_cancel')}</button>
          <button className="modal-save" disabled={busy}
            onClick={() => onApply(mode === 'merge' ? { mode: 'merge' } : { mode: 'overwrite', masterId })}>
            {busy ? ta('adm_sync_working') : ta('adm_sync_apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductModal({ modal, categories, currencyCode, slug, branchId, onSave, onClose }) {
  const { ta } = useLanguage();
  const isEdit = modal.mode === 'edit';
  const p = modal.data;
  const [form, setForm] = useState({
    name:         p?.name         || '',
    price:        p?.price        || '',
    categorySlug: p?.categorySlug || categories[0]?.slug || '',
    subCategory:  p?.subCategory  || '',
    description:  p?.description  || '',
    image:        p?.image        || '',
    tags:         p?.tags         || [],
    options:      (p?.options || []).map(o => ({ label: o.label || '', icon: o.icon || '' })),
    available:    p?.available    ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const toggleTag = tag => setForm(f => ({
    ...f,
    tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag],
  }));

  const addOption    = ()             => setForm(f => ({ ...f, options: [...f.options, { label: '', icon: '' }] }));
  const setOption    = (i, k, v)      => setForm(f => ({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o) }));
  const removeOption = (i)            => setForm(f => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert(ta('adm_t_name_required'));
    if (!form.price || isNaN(Number(form.price))) return alert(ta('adm_t_valid_price'));
    if (!form.categorySlug) return alert(ta('adm_t_cat_required'));
    setSaving(true);
    const options = (form.options || [])
      .map(o => ({ label: (o.label || '').trim(), icon: (o.icon || '').trim() }))
      .filter(o => o.label);
    await onSave({ ...form, options, price: parseFloat(form.price) });
    setSaving(false);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-grid">
            <div className="mf full">
              <label>Product Name *</label>
              <input className="mi" value={form.name} onChange={set('name')} placeholder="e.g. Margherita Pizza" autoFocus />
            </div>
            <div className="mf">
              <label>Price ({CURRENCIES[currencyCode]?.code || currencyCode || 'USD'}) *</label>
              <input className="mi" type="number" step="0.5" min="0" value={form.price} onChange={set('price')} placeholder="12.50" />
            </div>
            <div className="mf">
              <label>Category *</label>
              <select className="mi" value={form.categorySlug} onChange={set('categorySlug')}>
                {categories.map(c => <option key={c._id} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="mf full">
              <label>{ta("adm_sub_category")}</label>
              <input className="mi" value={form.subCategory} onChange={set('subCategory')} placeholder="e.g. Burgers, Pizza, Wraps" />
            </div>
            <div className="mf full">
              <label>{ta("adm_description")}</label>
              <textarea className="mi" rows={3} value={form.description} onChange={set('description')} placeholder={ta('adm_ph_describe_dish')} />
            </div>
            <div className="mf full">
              <label>{ta("adm_product_image")}</label>
              <ImageUploader
                aspectRatio={1}
                label="Upload Photo"
                currentUrl={form.image}
                aiEnhance={true}
                uploadContext={{ slug, branch: branchId || '', type: 'product', name: form.name || 'product' }}
                onUploaded={url => setForm(f => ({ ...f, image: url }))}
              />
              <div style={{marginTop:'.5rem',display:'flex',alignItems:'center',gap:'.5rem'}}>
                <span style={{fontSize:'.7rem',color:'rgba(255,255,255,.3)',whiteSpace:'nowrap'}}>or paste URL:</span>
                <input className="mi" value={form.image} onChange={set('image')} placeholder="https://..." style={{flex:1}} />
              </div>
            </div>
            <div className="mf full">
              <label>{ta("adm_tags")}</label>
              <div className="modal-tags">
                {TAG_OPTIONS.map(t => (
                  <button key={t} type="button"
                    className={`modal-tag-btn ${form.tags.includes(t) ? 'active' : ''}`}
                    onClick={() => toggleTag(t)}>{t}</button>
                ))}
              </div>
            </div>
            <div className="mf full">
              <label>{ta("adm_options")}</label>
              <div className="modal-opt-hint">{ta("adm_options_hint")}</div>
              <div className="modal-opts">
                {form.options.map((o, i) => (
                  <div className="modal-opt-row" key={i}>
                    <input className="mi modal-opt-icon" value={o.icon}
                      onChange={e => setOption(i, 'icon', e.target.value)}
                      placeholder={ta("adm_option_icon_ph")} maxLength={4} />
                    <input className="mi modal-opt-label" value={o.label}
                      onChange={e => setOption(i, 'label', e.target.value)}
                      placeholder={ta("adm_option_label_ph")} />
                    <button type="button" className="modal-opt-del" onClick={() => removeOption(i)}>✕</button>
                  </div>
                ))}
                <button type="button" className="modal-opt-add" onClick={addOption}>{ta("adm_add_option")}</button>
              </div>
            </div>
            <div className="mf">
              <label>{ta("adm_availability")}</label>
              <div className="modal-toggle-row">
                <button type="button"
                  className={`modal-toggle ${form.available ? 'on' : ''}`}
                  onClick={() => setForm(f => ({ ...f, available: !f.available }))}>
                  {form.available ? '✓ Available' : '✗ Hidden'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>{ta("adm_cancel")}</button>
          <button className="modal-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Product'}
          </button>
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CATEGORY MODAL
// ══════════════════════════════════════════════════════
function CategoryModal({ modal, onSave, onClose }) {
  const { ta } = useLanguage();
  const isEdit = modal.mode === 'edit';
  const c = modal.data;
  const [form, setForm] = useState({
    slug:          c?.slug         || '',
    name:          c?.name         || '',
    icon:          c?.icon         || '🍽️',
    description:   c?.description  || '',
    subCategories: c?.subCategories?.join(', ') || '',
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // auto-fill slug from name when adding
  const handleNameChange = e => {
    const name = e.target.value;
    setForm(f => ({ ...f, name, ...(!isEdit && !f._slugManual ? { slug: slugify(name) } : {}) }));
  };
  const handleSlugChange = e => {
    setForm(f => ({ ...f, slug: slugify(e.target.value), _slugManual: true }));
  };

  const ICONS = ['🍔','🍕','🥗','🍣','🍜','🍛','🥩','🍗','🦐','☕','🧁','🍰','🥐','🍹','🍺','🥤','🧀','🌮','🥪','🍝','🥘','🍲'];

  const handleSave = async () => {
    if (!form.name.trim()) return alert(ta('adm_t_name_required'));
    setSaving(true);
    await onSave({
      slug: form.slug || slugify(form.name),
      name: form.name,
      icon: form.icon,
      description: form.description,
      subCategories: form.subCategories
        ? form.subCategories.split(',').map(s => s.trim()).filter(Boolean)
        : [],
    });
    setSaving(false);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Category' : 'Add New Category'}</h2>
          <button className="modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-grid">
            <div className="mf full">
              <label>Category Name *</label>
              <input className="mi" value={form.name} onChange={handleNameChange} placeholder="e.g. Burgers" autoFocus />
            </div>
            <div className="mf">
              <label>{ta("adm_slug_url")}</label>
              <input className="mi" value={form.slug} onChange={handleSlugChange} placeholder="burgers" />
            </div>
            <div className="mf">
              <label>{ta("adm_icon")}</label>
              <div style={{ display:'flex', gap:'.5rem', alignItems:'center' }}>
                <input className="mi" value={form.icon} onChange={set('icon')} style={{ width:70, textAlign:'center', fontSize:'1.4rem' }} />
                <div style={{ display:'flex', flexWrap:'wrap', gap:'.3rem', flex:1 }}>
                  {ICONS.map(ic => (
                    <button key={ic} type="button" onClick={() => setForm(f => ({ ...f, icon: ic }))}
                      style={{ background: form.icon===ic ? 'var(--pri)' : 'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)', borderRadius:8, padding:'.3rem .4rem', cursor:'pointer', fontSize:'1.1rem' }}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mf full">
              <label>{ta("adm_description")}</label>
              <input className="mi" value={form.description} onChange={set('description')} placeholder={ta('adm_ph_cat_desc')} />
            </div>
            <div className="mf full">
              <label>Sub-Categories <span style={{color:'rgba(255,255,255,.3)',fontWeight:400}}>(comma-separated)</span></label>
              <input className="mi" value={form.subCategories} onChange={set('subCategories')} placeholder="e.g. Vegetarian, Meat, Seafood" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>{ta("adm_cancel")}</button>
          <button className="modal-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Category'}
          </button>
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}

function ModalStyles() {
  return (
    <style>{`
      .modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.75);backdrop-filter:blur(8px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:1rem}
      .modal-box{background:#1a1a1a;border:1px solid rgba(255,255,255,.12);border-radius:20px;width:100%;max-width:600px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden}
      .modal-header{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 1.5rem;border-bottom:1px solid rgba(255,255,255,.08)}
      .modal-header h2{font-family:'Playfair Display',serif;color:#fff;font-size:1.2rem}
      .modal-x{background:none;border:none;color:rgba(255,255,255,.4);cursor:pointer;font-size:1rem;padding:.25rem .5rem;border-radius:6px;transition:color .2s}
      .modal-x:hover{color:#fff}
      .modal-body{padding:1.5rem;overflow-y:auto;flex:1}
      .modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
      .mf{display:flex;flex-direction:column;gap:.4rem}
      .mf.full{grid-column:1/-1}
      .mf label{font-size:.72rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.07em}
      .mi{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:.75rem 1rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;resize:vertical;transition:border-color .2s;width:100%}
      .mi:focus{border-color:var(--pri)}
      .modal-img-preview{margin-top:.5rem;width:100%;max-height:120px;object-fit:cover;border-radius:10px}
      .modal-tags{display:flex;flex-wrap:wrap;gap:.4rem}
      .modal-tag-btn{padding:.3rem .8rem;border-radius:20px;border:1px solid rgba(255,255,255,.12);background:none;color:rgba(255,255,255,.55);cursor:pointer;font-size:.75rem;font-family:'DM Sans',sans-serif;transition:all .2s}
      .modal-tag-btn.active{background:var(--pri);border-color:var(--pri);color:#fff;font-weight:700}
      .modal-opt-hint{font-size:.7rem;color:rgba(255,255,255,.35);margin:.1rem 0 .5rem;line-height:1.4}
      .modal-opts{display:flex;flex-direction:column;gap:.45rem}
      .modal-opt-row{display:flex;gap:.4rem;align-items:center}
      .modal-opt-icon{width:3.2rem;text-align:center;flex:0 0 auto}
      .modal-opt-label{flex:1}
      .modal-opt-del{flex:0 0 auto;width:2rem;height:2rem;border-radius:8px;border:1px solid rgba(239,68,68,.3);background:rgba(239,68,68,.1);color:#fca5a5;cursor:pointer;font-size:.8rem}
      .modal-opt-del:hover{background:rgba(239,68,68,.2)}
      .modal-opt-add{align-self:flex-start;padding:.35rem .9rem;border-radius:8px;border:1px dashed rgba(167,139,250,.4);background:rgba(167,139,250,.08);color:#c4b5fd;cursor:pointer;font-size:.78rem;font-family:'DM Sans',sans-serif;font-weight:600}
      .modal-opt-add:hover{background:rgba(167,139,250,.16)}
      .modal-toggle{padding:.5rem 1.2rem;border-radius:20px;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:600;background:rgba(239,68,68,.15);color:#fca5a5;transition:all .2s}
      .modal-toggle.on{background:rgba(16,185,129,.15);color:#6ee7b7}
      .modal-footer{display:flex;gap:.75rem;justify-content:flex-end;padding:1.25rem 1.5rem;border-top:1px solid rgba(255,255,255,.08)}
      .modal-cancel{padding:.75rem 1.5rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:10px;color:rgba(255,255,255,.7);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.9rem;transition:all .2s}
      .modal-cancel:hover{background:rgba(255,255,255,.1);color:#fff}
      .modal-save{padding:.75rem 1.75rem;background:var(--pri);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;font-weight:700;cursor:pointer;transition:opacity .2s}
      .modal-save:hover:not(:disabled){opacity:.85}
      .modal-save:disabled{opacity:.5;cursor:not-allowed}
    `}</style>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerRow — inline-editable customer card
// ─────────────────────────────────────────────────────────────────────────────
function CustomerRow({ customer, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({ name: customer.name, phone: customer.phone || '', address: customer.address || '' });
  const [saving,  setSaving]  = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    await onSave(customer._id, form);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="adm-order-card" style={{ marginBottom: '0.75rem' }}>
      <div className="adm-order-header">
        <span className="adm-order-num">{customer.name}</span>
        <span className="adm-order-time">{customer.email}</span>
        <span style={{
          padding: '0.2rem 0.65rem', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 700,
          background: customer.isActive ? 'rgba(16,185,129,.15)' : 'rgba(239,68,68,.15)',
          color: customer.isActive ? '#6ee7b7' : '#fca5a5',
        }}>{customer.isActive ? 'Active' : 'Inactive'}</span>
        <button onClick={() => setEditing(e => !e)} style={{
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '8px', color: '#fff', cursor: 'pointer', padding: '0.3rem 0.75rem',
          fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif',
        }}>{editing ? 'Cancel' : 'Edit'}</button>
      </div>

      {!editing ? (
        <div className="adm-cust-body">
          <div className="adm-order-meta">
            {customer.phone   && <div><strong>Phone:</strong> {customer.phone}</div>}
            {customer.address && <div><strong>Address:</strong> {customer.address}</div>}
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.25rem' }}>
              Member since {new Date(customer.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      ) : (
        <div className="adm-cust-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {[['Name', 'name', 'text'], ['Phone', 'phone', 'tel'], ['Address', 'address', 'text']].map(([label, key, type]) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
              <input type={type} value={form[key]} onChange={set(key)} style={{
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px', padding: '0.6rem 0.875rem', color: '#fff',
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', outline: 'none',
              }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
            <button onClick={handleSave} disabled={saving} style={{
              flex: 1, padding: '0.65rem', background: 'var(--pri)', border: 'none',
              borderRadius: '8px', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
              fontSize: '0.9rem', fontWeight: 700, opacity: saving ? 0.6 : 1,
            }}>{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => onSave(customer._id, { isActive: !customer.isActive })} style={{
              padding: '0.65rem 1rem', background: customer.isActive ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)',
              border: `1px solid ${customer.isActive ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`,
              borderRadius: '8px', color: customer.isActive ? '#f87171' : '#6ee7b7',
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem',
            }}>{customer.isActive ? 'Deactivate' : 'Activate'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── OrderDetailModal ────────────────────────────────────────────────────────
function OrderDetailModal({ order, restaurant, onClose, onStatusChange, onPaymentChange, formatPrice }) {
  const { ta } = useLanguage();
  const STATUS_CONFIG = {
    Pending:   { color:'#fbbf24', bg:'rgba(251,191,36,.12)',  icon:'🕐' },
    Confirmed: { color:'#60a5fa', bg:'rgba(96,165,250,.12)',  icon:'✅' },
    Preparing: { color:'#f97316', bg:'rgba(249,115,22,.12)',  icon:'👨‍🍳' },
    Ready:     { color:'#34d399', bg:'rgba(52,211,153,.12)',  icon:'🔔' },
    Delivered: { color:'#a3e635', bg:'rgba(163,230,53,.1)',   icon:'✓'  },
    Cancelled: { color:'#f87171', bg:'rgba(248,113,113,.1)',  icon:'✕'  },
  };
  const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.Pending;

  const printCashier = () => {
    const w = window.open('','_blank','width=320,height=600');
    const now = new Date(order.createdAt).toLocaleString();
    const items = order.items.map(it =>
      `<tr><td>${it.productName}</td><td class="center">x${it.quantity}</td><td class="right">${formatPrice(it.price*it.quantity, restaurant.currencyCode)}</td></tr>`
    ).join('');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Courier New',monospace;font-size:12px;width:80mm;padding:4mm}
      .center{text-align:center}.right{text-align:right}
      h1{font-size:16px;text-align:center;margin:4px 0}
      .subtitle{text-align:center;font-size:11px;color:#555;margin-bottom:6px}
      .divider{border-top:1px dashed #000;margin:6px 0}
      .thick{border-top:2px solid #000;margin:6px 0}
      table{width:100%;border-collapse:collapse}
      td{padding:2px 3px;font-size:11px;vertical-align:top}
      .total-row td{font-size:13px;font-weight:bold;padding-top:4px}
      .meta{font-size:11px;margin:3px 0;line-height:1.6}
      .status-badge{display:inline-block;border:1px solid #000;padding:2px 8px;font-weight:bold;font-size:11px}
      .payment-badge{display:inline-block;border:2px solid #000;padding:3px 10px;font-weight:bold;font-size:13px;margin:4px 0}
      .footer{text-align:center;font-size:10px;margin-top:8px;color:#555}
    </style></head><body>
    <h1>${restaurant.name || 'Restaurant'}</h1>
    ${restaurant.address ? `<div class="subtitle">${restaurant.address}</div>` : ''}
    ${restaurant.phone ? `<div class="subtitle">Tel: ${restaurant.phone}</div>` : ''}
    <div class="thick"></div>
    <div class="meta"><b>Order:</b> ${order.orderNumber}</div>
    ${order.tableNumber ? `<div class="meta"><b>Table:</b> ${order.tableNumber}</div>` : ''}
    ${order.orderType ? `<div class="meta"><b>Type:</b> ${order.orderType.toUpperCase()}</div>` : ''}
    <div class="meta"><b>Date:</b> ${now}</div>
    <div class="meta"><b>Customer:</b> ${order.customerName}</div>
    ${order.customerPhone ? `<div class="meta"><b>Phone:</b> ${order.customerPhone}</div>` : ''}
    ${order.customerAddress ? `<div class="meta"><b>Address:</b> ${order.customerAddress}</div>` : ''}
    <div class="divider"></div>
    <table>
      <tr><td><b>Item</b></td><td class="center"><b>Qty</b></td><td class="right"><b>{ta("adm_price")}</b></td></tr>
      <tr><td colspan="3"><div class="divider" style="margin:2px 0"></div></td></tr>
      ${items}
      <tr><td colspan="3"><div class="divider" style="margin:2px 0"></div></td></tr>
      ${order.subtotal !== order.total ? `<tr><td colspan="2">Subtotal</td><td class="right">${formatPrice(order.subtotal, restaurant.currencyCode)}</td></tr>` : ''}
      ${order.serviceFee ? `<tr><td colspan="2">Service fee</td><td class="right">${formatPrice(order.serviceFee, restaurant.currencyCode)}</td></tr>` : ''}
      <tr class="total-row"><td colspan="2">TOTAL</td><td class="right">${formatPrice(order.total, restaurant.currencyCode)}</td></tr>
    </table>
    <div class="thick"></div>
    <div class="center" style="margin:4px 0"><span class="payment-badge">${(order.paymentStatus||'UNPAID').toUpperCase()} · ${(order.paymentMethod||'—').toUpperCase()}</span></div>
    <div class="center"><span class="status-badge">${order.status}</span></div>
    ${order.deliveryNote ? `<div class="divider"></div><div class="meta"><b>Note:</b> ${order.deliveryNote}</div>` : ''}
    <div class="thick"></div>
    <div class="footer">{ta("adm_thank_you")}<br>Printed ${new Date().toLocaleTimeString()}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  const printKitchen = () => {
    const w = window.open('','_blank','width=320,height=500');
    const now = new Date(order.createdAt).toLocaleTimeString();
    const items = order.items.map(it =>
      `<tr><td class="item-name">${it.productName}</td><td class="qty">x${it.quantity}</td></tr>`
    ).join('');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Courier New',monospace;font-size:13px;width:80mm;padding:4mm}
      .center{text-align:center}
      h1{font-size:22px;text-align:center;font-weight:900;margin:4px 0;letter-spacing:2px}
      .order-num{text-align:center;font-size:28px;font-weight:900;border:3px solid #000;padding:4px 8px;margin:6px auto;display:inline-block}
      .order-num-wrap{text-align:center;margin:6px 0}
      .divider{border-top:1px dashed #000;margin:6px 0}
      .thick{border-top:3px solid #000;margin:6px 0}
      table{width:100%;border-collapse:collapse;margin:4px 0}
      .item-name{font-size:15px;font-weight:bold;padding:5px 3px;vertical-align:top}
      .qty{font-size:20px;font-weight:900;text-align:right;padding:5px 3px;white-space:nowrap}
      .meta{font-size:12px;margin:3px 0;line-height:1.6}
      .note{font-size:13px;font-weight:bold;border:2px solid #000;padding:4px 6px;margin:6px 0}
      .badge{display:inline-block;border:2px solid #000;padding:3px 10px;font-size:13px;font-weight:900;margin:4px 0}
      .time-stamp{text-align:center;font-size:11px;color:#555;margin-top:6px}
    </style></head><body>
    <h1>🍳 KITCHEN</h1>
    <div class="thick"></div>
    <div class="order-num-wrap"><span class="order-num">${order.orderNumber}</span></div>
    ${order.tableNumber ? `<div class="center"><span class="badge">TABLE ${order.tableNumber}</span></div>` : ''}
    ${order.orderType && order.orderType !== 'dine-in' ? `<div class="center"><span class="badge">${order.orderType.toUpperCase()}</span></div>` : ''}
    <div class="meta center"><b>${order.customerName}</b></div>
    <div class="thick"></div>
    <table>
      ${items}
    </table>
    <div class="thick"></div>
    ${order.kitchenNote || order.deliveryNote ? `<div class="note">📝 NOTE: ${order.kitchenNote || order.deliveryNote}</div>` : ''}
    <div class="time-stamp">Received: ${now} &nbsp;·&nbsp; ${new Date(order.createdAt).toLocaleDateString()}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  return (
    <div className="ord-modal-backdrop" onClick={onClose}>
      <div className="ord-modal" onClick={e=>e.stopPropagation()}>
        <div className="ord-modal-head">
          <div>
            <span className="ord-modal-num">{order.orderNumber}</span>
            <span className="ord-modal-status" style={{background:sc.bg,color:sc.color}}>{sc.icon} {order.status}</span>
          </div>
          <button className="ord-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Status strip */}
        <div className="ord-status-strip">
          {Object.entries(STATUS_CONFIG).map(([s,cfg])=>(
            <button key={s} className={`ord-status-step ${order.status===s?'active':''}`}
              style={order.status===s?{background:cfg.bg,color:cfg.color,borderColor:cfg.color}:{}}
              onClick={()=>onStatusChange(order._id,s)}>
              {cfg.icon} {s}
            </button>
          ))}
        </div>

        <div className="ord-modal-body">
          {/* Left: order info */}
          <div className="ord-modal-left">
            <div className="ord-section-title">📋 Order Details</div>
            <div className="ord-detail-grid">
              {order.tableNumber && <><span className="ord-dl">Table</span><span className="ord-dv">🪑 {order.tableNumber}</span></>}
              {order.orderType   && <><span className="ord-dl">{ta("adm_type")}</span><span className="ord-dv">{order.orderType}</span></>}
              <span className="ord-dl">{ta("adm_date")}</span><span className="ord-dv">{new Date(order.createdAt).toLocaleString()}</span>
              <span className="ord-dl">Customer</span><span className="ord-dv">{order.customerName}</span>
              {order.customerPhone && <><span className="ord-dl">Phone</span><span className="ord-dv">{order.customerPhone}</span></>}
              {order.customerEmail && <><span className="ord-dl">Email</span><span className="ord-dv">{order.customerEmail}</span></>}
              {(order.customerAddress||order.customer?.address) && <><span className="ord-dl">{ta("adm_address")}</span><span className="ord-dv">{order.customerAddress||order.customer?.address}</span></>}
              {(order.deliveryNote||order.kitchenNote) && <><span className="ord-dl">Note</span><span className="ord-dv">{order.kitchenNote||order.deliveryNote}</span></>}
            </div>

            {/* Payment */}
            <div className="ord-section-title" style={{marginTop:'1rem'}}>💳 Payment</div>
            <div className="ord-payment-row">
              <select className="ord-pay-sel"
                value={order.paymentMethod||'unpaid'}
                onChange={e=>onPaymentChange(order._id,e.target.value,order.paymentStatus)}>
                <option value="unpaid">{ta("adm_not_set")}</option>
                <option value="cash">💵 Cash</option>
                <option value="card">💳 Card</option>
                <option value="online">📱 Online</option>
              </select>
              <select className="ord-pay-sel"
                value={order.paymentStatus||'pending'}
                onChange={e=>onPaymentChange(order._id,order.paymentMethod,e.target.value)}>
                <option value="pending">⏳ Unpaid</option>
                <option value="paid">✅ Paid</option>
                <option value="refunded">↩️ Refunded</option>
              </select>
            </div>

            {/* Print buttons */}
            <div className="ord-print-row">
              <button className="ord-print-btn cashier" onClick={printCashier}>
                🖨️ Cashier Receipt
              </button>
              <button className="ord-print-btn kitchen" onClick={printKitchen}>
                🍳 Kitchen Ticket
              </button>
            </div>
          </div>

          {/* Right: items */}
          <div className="ord-modal-right">
            <div className="ord-section-title">🍽️ Items</div>
            <div className="ord-modal-items">
              {order.items.map((it,i)=>(
                <div key={i} className="ord-modal-item">
                  {it.productImage && <img src={it.productImage} alt="" className="ord-modal-item-img" onError={e=>e.target.style.display='none'}/>}
                  <div className="ord-modal-item-info">
                    <span className="ord-modal-item-name">{it.productName}</span>
                    <span className="ord-modal-item-price">{formatPrice(it.price, restaurant.currencyCode)} each</span>
                  </div>
                  <div className="ord-modal-item-right">
                    <span className="ord-modal-item-qty">×{it.quantity}</span>
                    <span className="ord-modal-item-sub">{formatPrice(it.price*it.quantity, restaurant.currencyCode)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="ord-modal-totals">
              {order.subtotal !== order.total && (
                <div className="ord-total-row"><span>Subtotal</span><span>{formatPrice(order.subtotal, restaurant.currencyCode)}</span></div>
              )}
              {order.serviceFee > 0 && (
                <div className="ord-total-row"><span>Service fee</span><span>{formatPrice(order.serviceFee, restaurant.currencyCode)}</span></div>
              )}
              <div className="ord-total-row grand"><span>TOTAL</span><span>{formatPrice(order.total, restaurant.currencyCode)}</span></div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ord-modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.8);backdrop-filter:blur(14px);z-index:3000;display:flex;align-items:center;justify-content:center;padding:1rem}
        .ord-modal{background:#141414;border:1px solid rgba(255,255,255,.1);border-radius:22px;width:100%;max-width:820px;max-height:90vh;overflow-y:auto;display:flex;flex-direction:column}
        .ord-modal-head{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 1.5rem;border-bottom:1px solid rgba(255,255,255,.07);gap:.75rem;flex-wrap:wrap}
        .ord-modal-num{font-family:'Playfair Display',serif;font-size:1.35rem;font-weight:900;color:#fff;margin-right:.6rem}
        .ord-modal-status{padding:.28rem .75rem;border-radius:10px;font-size:.82rem;font-weight:700}
        .ord-modal-close{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.12);color:rgba(255,255,255,.6);width:32px;height:32px;border-radius:50%;cursor:pointer;font-size:.8rem;flex-shrink:0}
        .ord-status-strip{display:flex;gap:.3rem;padding:.75rem 1.5rem;border-bottom:1px solid rgba(255,255,255,.06);flex-wrap:wrap}
        .ord-status-step{padding:.32rem .75rem;border-radius:8px;border:1px solid rgba(255,255,255,.1);background:none;color:rgba(255,255,255,.35);font-family:'DM Sans',sans-serif;font-size:.72rem;font-weight:700;cursor:pointer;transition:all .2s}
        .ord-status-step.active{font-size:.75rem}
        .ord-modal-body{display:grid;grid-template-columns:1fr 1.3fr;gap:0;flex:1}
        @media(max-width:680px){.ord-modal-body{grid-template-columns:1fr}}
        .ord-modal-left{padding:1.25rem 1.5rem;border-right:1px solid rgba(255,255,255,.06)}
        .ord-modal-right{padding:1.25rem 1.5rem}
        .ord-section-title{font-size:.7rem;font-weight:800;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.08em;margin-bottom:.6rem}
        .ord-detail-grid{display:grid;grid-template-columns:auto 1fr;gap:.3rem .75rem;align-items:start}
        .ord-dl{font-size:.72rem;color:rgba(255,255,255,.35);white-space:nowrap;padding-top:.05rem}
        .ord-dv{font-size:.8rem;color:#fff;font-weight:600;word-break:break-word}
        .ord-payment-row{display:flex;gap:.4rem;margin-bottom:.75rem;flex-wrap:wrap}
        .ord-pay-sel{flex:1;min-width:100px;padding:.4rem .6rem;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:8px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.78rem;cursor:pointer}
        .ord-print-row{display:flex;gap:.5rem;flex-wrap:wrap}
        .ord-print-btn{flex:1;padding:.6rem .8rem;border-radius:10px;border:none;font-family:'DM Sans',sans-serif;font-size:.78rem;font-weight:700;cursor:pointer;transition:opacity .2s;white-space:nowrap}
        .ord-print-btn.cashier{background:#c8a96e;color:#fff}
        .ord-print-btn.kitchen{background:#f97316;color:#fff}
        .ord-print-btn:hover{opacity:.88}
        .ord-modal-items{display:flex;flex-direction:column;gap:.4rem;margin-bottom:.75rem}
        .ord-modal-item{display:flex;align-items:center;gap:.6rem;padding:.5rem .6rem;background:rgba(255,255,255,.04);border-radius:10px}
        .ord-modal-item-img{width:36px;height:36px;border-radius:7px;object-fit:cover;flex-shrink:0}
        .ord-modal-item-info{flex:1;display:flex;flex-direction:column;gap:.08rem}
        .ord-modal-item-name{font-size:.82rem;font-weight:700;color:#fff}
        .ord-modal-item-price{font-size:.68rem;color:rgba(255,255,255,.35)}
        .ord-modal-item-right{display:flex;flex-direction:column;align-items:flex-end;gap:.08rem}
        .ord-modal-item-qty{font-size:.78rem;color:rgba(255,255,255,.5)}
        .ord-modal-item-sub{font-size:.82rem;font-weight:700;color:var(--pri)}
        .ord-modal-totals{border-top:1px solid rgba(255,255,255,.08);padding-top:.6rem;display:flex;flex-direction:column;gap:.25rem}
        .ord-total-row{display:flex;justify-content:space-between;font-size:.82rem;color:rgba(255,255,255,.6)}
        .ord-total-row.grand{font-size:1rem;font-weight:900;color:#fff;padding-top:.25rem;border-top:1px solid rgba(255,255,255,.12)}
      `}</style>
    </div>
  );
}

// SubAdminPanel — list + create + edit + delete sub-admins
// ─────────────────────────────────────────────────────────────────────────────
function SubAdminPanel({ slug, branches = [], subAdmins, currentAdminId, onCreated, onUpdated, onDeleted, notify }) {
  const { ta } = useLanguage();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', username: '', password: '', role: 'limited', branches: [] });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleBranch = (bslug) => setForm(f => ({
    ...f,
    branches: f.branches.includes(bslug) ? f.branches.filter(s => s !== bslug) : [...f.branches, bslug],
  }));

  const handleCreate = async () => {
    setError('');
    if (!form.name || !form.email || !form.password) { setError('All fields are required.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setCreating(true);
    try {
      const res = await adminApi.createSubAdmin(slug, form);
      onCreated(res.data);
      setForm({ name: '', email: '', username: '', password: '', role: 'limited', branches: [] });
      setShowCreate(false);
      notify(ta('adm_t_subadmin_created'));
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  return (
    <div>
      {/* Create button */}
      <div style={{ marginBottom: '1rem' }}>
        <button onClick={() => { setShowCreate(s => !s); setError(''); }} style={{
          padding: '0.65rem 1.25rem', background: 'var(--pri)', border: 'none', borderRadius: '10px',
          color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 700,
        }}>{showCreate ? '✕ Cancel' : '+ Add Sub-Admin'}</button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={{
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '14px', padding: '1.25rem', marginBottom: '1.5rem',
        }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', marginBottom: '1rem' }}>{ta("adm_new_subadmin")}</div>
          {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '0.6rem 0.875rem', color: '#fca5a5', fontSize: '0.82rem', marginBottom: '0.875rem' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            {[
              [ta('adm_name') || 'Name', 'name', 'text', ''],
              [ta('adm_email') || 'Email', 'email', 'email', ''],
              [ta('adm_username') || 'Username', 'username', 'text', ta('adm_username_optional') || 'optional — defaults to email'],
              [ta('adm_password') || 'Password', 'password', 'password', ''],
            ].map(([label, key, type, ph]) => (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', gridColumn: 'span 1' }}>
                <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
                <input type={type} value={form[key]} onChange={set(key)} placeholder={ph} autoComplete="off" style={{
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px', padding: '0.65rem 0.875rem', color: '#fff',
                  fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', outline: 'none',
                }} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: '0.875rem' }}>
            <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.3rem' }}>{ta("adm_access_level")}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {[
                ['limited',  '🔒 Limited',  'Orders view & status update only'],
                ['cashier',  '💳 Cashier',   'Orders + payment + cashier print'],
                ['kitchen',  '🍳 Kitchen',   'Kitchen orders & kitchen print only'],
                ['full',     '🔓 Full',      'Everything except admin management'],
              ].map(([val, label, desc]) => (
                <button key={val} onClick={() => setForm(f => ({ ...f, role: val }))} style={{
                  padding: '0.65rem', border: `1px solid ${form.role === val ? 'var(--pri)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '10px', background: form.role === val ? 'rgba(200,169,110,0.15)' : 'none',
                  color: form.role === val ? 'var(--pri)' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textAlign: 'left',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{label}</div>
                  <div style={{ fontSize: '0.72rem', opacity: 0.7, marginTop: '0.2rem' }}>{desc}</div>
                </button>
              ))}
            </div>
          </div>
          {branches.length > 0 && (
            <div style={{ marginBottom: '0.875rem' }}>
              <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.3rem' }}>📍 {ta('adm_subadmin_branches')}</label>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {branches.map(b => (
                  <button key={b.slug} type="button" onClick={() => toggleBranch(b.slug)} style={{
                    padding: '0.4rem 0.8rem', borderRadius: '9px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', fontWeight: 600,
                    border: `1px solid ${form.branches.includes(b.slug) ? 'var(--pri)' : 'rgba(255,255,255,0.12)'}`,
                    background: form.branches.includes(b.slug) ? 'rgba(200,169,110,0.15)' : 'none',
                    color: form.branches.includes(b.slug) ? 'var(--pri)' : 'rgba(255,255,255,0.55)',
                  }}>{form.branches.includes(b.slug) ? '✓ ' : ''}{b.name}</button>
                ))}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.35rem' }}>{ta('adm_subadmin_branches_hint')}</div>
            </div>
          )}
          <button onClick={handleCreate} disabled={creating} style={{
            width: '100%', padding: '0.8rem', background: 'var(--pri)', border: 'none',
            borderRadius: '10px', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
            fontSize: '0.95rem', fontWeight: 700, opacity: creating ? 0.6 : 1,
          }}>{creating ? 'Creating…' : 'Create Sub-Admin'}</button>
        </div>
      )}

      {/* List */}
      {subAdmins.length === 0 ? (
        <div className="adm-empty">{ta("adm_no_subadmins")}</div>
      ) : (
        <div className="adm-orders-list">
          {subAdmins.map(a => (
            <SubAdminRow key={a._id} admin={a} slug={slug} isSelf={a._id === currentAdminId || a.id === currentAdminId}
              onUpdated={onUpdated} onDeleted={onDeleted} notify={notify} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubAdminRow({ admin, slug, isSelf, onUpdated, onDeleted, notify }) {
  const { ta } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({ name: admin.name, role: admin.role, password: '' });
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(false);

  const isRoot = admin.role === 'root';
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, role: form.role };
      if (form.password) payload.password = form.password;
      await adminApi.updateSubAdmin(slug, admin._id, payload);
      onUpdated(admin._id, payload);
      setEditing(false);
      notify(ta('adm_t_admin_updated'));
    } catch (e) { notify(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(ta('adm_c_remove_admin'))) return;
    setDeleting(true);
    try {
      await adminApi.deleteSubAdmin(slug, admin._id);
      onDeleted(admin._id);
      notify(ta('adm_t_admin_removed'));
    } catch (e) { notify(e.message, 'error'); }
    finally { setDeleting(false); }
  };

  const ROLE_COLORS = { root: '#a5b4fc', full: '#6ee7b7', limited: '#fdba74', cashier: '#c8a96e', kitchen: '#f97316' };

  return (
    <div className="adm-order-card" style={{ marginBottom: '0.75rem' }}>
      <div className="adm-order-header">
        <span className="adm-order-num">{admin.name} {isSelf && <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400, fontSize: '0.75rem' }}>(you)</span>}</span>
        <span className="adm-order-time">{admin.email}</span>
        <span style={{ padding: '0.2rem 0.65rem', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 700, background: 'rgba(255,255,255,0.06)', color: ROLE_COLORS[admin.role] || '#fff' }}>
          {admin.role === 'root' ? '👑 Root' : admin.role === 'full' ? '🔓 Full' : '🔒 Limited'}
        </span>
        {!isRoot && !isSelf && (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button onClick={() => setEditing(e => !e)} style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px', color: '#fff', cursor: 'pointer', padding: '0.3rem 0.65rem', fontSize: '0.78rem', fontFamily: 'DM Sans,sans-serif',
            }}>{editing ? 'Cancel' : 'Edit'}</button>
            <button onClick={handleDelete} disabled={deleting} style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: '8px', color: '#f87171', cursor: 'pointer', padding: '0.3rem 0.65rem', fontSize: '0.78rem', fontFamily: 'DM Sans,sans-serif',
            }}>{deleting ? '…' : 'Remove'}</button>
          </div>
        )}
      </div>

      {editing && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{ta("adm_name")}</label>
            <input value={form.name} onChange={set('name')} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '0.6rem 0.875rem', color: '#fff', fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>New Password <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 400 }}>(leave blank to keep)</span></label>
            <input type="password" value={form.password} onChange={set('password')} placeholder="••••••••" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '0.6rem 0.875rem', color: '#fff', fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', outline: 'none' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.35rem' }}>{ta("adm_access_level")}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
              {[['limited','🔒 Limited'],['cashier','💳 Cashier'],['kitchen','🍳 Kitchen'],['full','🔓 Full']].map(([val, label]) => (
                <button key={val} onClick={() => setForm(f => ({ ...f, role: val }))} style={{
                  padding: '0.55rem', border: `1px solid ${form.role === val ? 'var(--pri)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '8px', background: form.role === val ? 'rgba(200,169,110,0.15)' : 'none',
                  color: form.role === val ? 'var(--pri)' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', fontWeight: form.role === val ? 700 : 400,
                }}>{label}</button>
              ))}
            </div>
          </div>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '0.65rem', background: 'var(--pri)', border: 'none', borderRadius: '8px',
            color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 700, opacity: saving ? 0.6 : 1,
          }}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      )}
    </div>
  );
}
